// import MUI - React
import React, {
    useCallback,
    useEffect,
    useState,
    useRef,
    useMemo,
} from "react";
import {
  Alert,
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  Dialog,
  Divider,
  FormControl,
  MenuItem,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Stack,
  TableSortLabel
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
// Import Component
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import Select from '../components/SearchableSelect';
import { PrintEventListener, SetupWSEventListener } from "../utils/setupWSEventListener";
import { useSnackbar } from "../contexts/SnackBarContext";

import SidebarLayout from "../components/sidebarLayout";
import ComandaCorrente from "../components/ComandaCorrente";
import Tastierino from "../components/Tastierino";
import FiltraProdotto from "../components/FiltraProdotto";
import { handlePrint as gestisciStampa } from "../components/handlePrint"; // rinominato per evitare conflitti
//API
import { getAllCategorie, getMyCategoryVisibility } from '../APIs/CategoriesAPI';
import { aggiornaStatoPrenotazione, getAllPrenotazioni } from '../APIs/ReservationsAPI';
import {
    aggiornaComanda,
    aggiornaRettificheComanda,
    aggiornaStatusComanda,
    creaComanda,
    eliminaComanda,
    getAllOrdini,
    getOrdineById,
    partialCheckoutComanda,
    patchMenuPrezzoFissoComanda,
    patchProdottoComanda,
    patchPacchettoComanda
} from '../APIs/OrdersAPI';
import { aggiornaTavolo, claimTableSession, getAllTavoli, releaseTableSession } from '../APIs/TablesAPI';
import { getAllProdotti } from "../APIs/ProductsAPI";
//Icons
import SearchIcon from '@mui/icons-material/Search';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ConfermaLiberaTavolo from "../components/confermaLiberaTavolo";
import { formattaFloat } from "../utils/globalUtils";

import { getTenantTranslation } from "../utils/getTenantTranslation";
import FatturazioneDialog from "../components/FatturazioneDialog";
import HeliteBuonoSignatureDialog from "../components/HeliteBuonoSignatureDialog";
import { checkoutAllaRomana, checkoutMultipleOrders, checkoutOrderReceipt } from "../APIs/ReceiptsAPI";
import {
    applyReservationEventPrepayments,
    createReservationEventPrepayment,
    getReservationEvents,
    getReservationEventPrepaymentSummary,
    patchReservationEventStatus,
    refundReservationEventPrepayment,
    reverseReservationEventPrepayment,
} from "../APIs/ReservationEventsAPI";
import {
    fiscalDailyClosure,
    fiscalReceipt as sendFiscalReceiptRequest,
    getConfiguredFiscalDevices,
} from "../APIs/PrintersAPI";
import FatturazioneRiepilogo from "../components/FatturazioneRiepilogoDialog";
import { getTenantSettings } from "../APIs/TenantSettingsAPI";
import { getHeliteCheckedInGuests, printHeliteBuono, signHeliteBuono } from "../APIs/HeliteAPI";
import { printVoucher, signVoucher } from "../APIs/VouchersAPI";
import { createInvoicePayment, printInvoiceCourtesyCopy } from "../APIs/InvoicesAPI";
import { formatBoardBasis, getGuestBoardBasis, isIncludedByBoardBasis } from "../utils/boardBasis";
import { getFiscalItemDescription } from "../utils/fiscalReceipt";
import { getCheckoutPaidTotal } from "../utils/checkoutPaidTotal";
import { applyUserCategoryVisibility } from "../utils/categoryVisibility";

const COUNTER_CHECKOUT_ID = "__COUNTER_CHECKOUT__";
const MULTIPLE_CHECKOUT_ID = "__MULTIPLE_CHECKOUT__";
const MULTIPLE_CHECKOUT_LABEL = "Checkout multiplo";
const MANUAL_PMS_BUONO_METHOD = "other";
const MANUAL_PMS_BUONO_NOTE = "Buono PMS manuale - documento fiscale non emesso";
const CARD_CIRCUIT_OPTIONS = [
    { value: "VISA", label: "Visa" },
    { value: "MASTERCARD", label: "Mastercard" },
    { value: "AMEX", label: "American Express" },
    { value: "BANCOMAT", label: "Bancomat" },
    { value: "MAESTRO", label: "Maestro" },
    { value: "NEXI_SI", label: "Nexi / SI" },
    { value: "OTHER", label: "Altro" },
];
const RECEIPT_METHOD_BY_UI_METHOD = {
    cash: "CASH",
    cards: "CARD",
    satispay: "CARD",
    helite_room_charge: "ROOM_CHARGE",
    [MANUAL_PMS_BUONO_METHOD]: "VOUCHER",
};
const ALLA_ROMANA_PAYMENT_METHOD_BY_UI_METHOD = {
    cash: "CASH",
    cards: "CARD",
    satispay: "CARD",
    other: "VOUCHER",
};
const ALLA_ROMANA_RECOVERY_ERROR_CODES = new Set([
    "FISCAL_PRINT_FAILED",
    "FISCAL_RECEIPT_VERIFICATION_REQUIRED",
]);
const NON_RESERVATION_ORDER_KEY_PREFIX = "nonReservationOrder";
const NON_RESERVATION_TABLE_KEY_PREFIX = "nonReservationTable";
const FISCAL_DEVICE_PREFERENCE_KEY_PREFIX = "preferredFiscalDevice";
const fiscalDevicePreferenceKey = (tenant) => (
    `${FISCAL_DEVICE_PREFERENCE_KEY_PREFIX}:${String(tenant || "default")}`
);
const readFiscalDevicePreference = (tenant) => {
    try {
        return localStorage.getItem(fiscalDevicePreferenceKey(tenant)) || "";
    } catch {
        return "";
    }
};
const writeFiscalDevicePreference = (tenant, deviceId) => {
    try {
        const key = fiscalDevicePreferenceKey(tenant);
        if (deviceId) localStorage.setItem(key, String(deviceId));
        else localStorage.removeItem(key);
    } catch {
        // Checkout remains usable when browser storage is unavailable.
    }
};
const FISCAL_DEPARTMENT_BY_VAT_RATE = {
    0: 4,
    4: 1,
    5: 1,
    10: 2,
    22: 3,
};
const PREPAYMENT_POS_MODES = ["DEPOSIT", "ADVANCE"];
const PREPAYMENT_POS_PAYMENT_METHODS = ["CASH", "CARD", "BANK_TRANSFER", "VOUCHER", "ROOM_CHARGE"];
const PREPAYMENT_POS_ADVANCE_DOCUMENT_TYPES = ["COMMERCIAL_DOCUMENT", "INVOICE_TD02"];
const PREPAYMENT_POS_ADVANCE_REVERSAL_TYPES = ["COMMERCIAL_CANCELLATION", "COMMERCIAL_RETURN", "CREDIT_NOTE_TD04"];
const PREPAYMENT_POS_VAT_NATURE_OPTIONS = ["N1", "N2", "N3", "N4", "N5", "N6", "N7"];
const INVOICE_SETTLEMENT_METHODS = [
    { value: "CASH", label: "Contanti" },
    { value: "CARD", label: "POS" },
    { value: "BANK_TRANSFER", label: "Bonifico" },
    { value: "CHECK", label: "Assegno" },
    { value: "SUSPENDED", label: "Sospeso" },
];

const normalizePrepaymentPosMode = (value) => {
    const normalized = String(value || "").trim().toUpperCase();
    return PREPAYMENT_POS_MODES.includes(normalized) ? normalized : null;
};

const prepaymentPosInitialForm = () => ({
    operation: "RECEIVE",
    amount: "",
    vatRate: "10",
    paymentMethod: "CASH",
    documentType: "COMMERCIAL_DOCUMENT",
    invoiceId: "",
    vatNature: "",
    documentNumber: "",
    documentDate: "",
    description: "",
    note: "",
    sourceMovementId: "",
});

const prepaymentDeliverySeverity = (delivery) => {
    if (delivery?.status === "FAILED") return "warning";
    if (delivery?.status === "SKIPPED") return "info";
    return "success";
};

const getNonReservationOrderStorageKey = (tenant) => `${NON_RESERVATION_ORDER_KEY_PREFIX}:${tenant || "default"}`;
const getNonReservationTableStorageKey = (tenant) => `${NON_RESERVATION_TABLE_KEY_PREFIX}:${tenant || "default"}`;

const getNonReservationOrderIds = (tenant) => {
    try {
        const raw = localStorage.getItem(getNonReservationOrderStorageKey(tenant));
        const ids = JSON.parse(raw || "[]");
        return new Set((Array.isArray(ids) ? ids : []).map(Number).filter((id) => Number.isInteger(id) && id > 0));
    } catch {
        return new Set();
    }
};

const getNonReservationTableMarkers = (tenant) => {
    try {
        const raw = localStorage.getItem(getNonReservationTableStorageKey(tenant));
        const markers = JSON.parse(raw || "[]");
        return new Set((Array.isArray(markers) ? markers : []).map(String));
    } catch {
        return new Set();
    }
};

const clearNonReservationMarkersForTable = (tenant, tableId, orderIds = []) => {
    const normalizedTableId = Number(tableId);
    if (!Number.isInteger(normalizedTableId) || normalizedTableId <= 0) return;

    try {
        const tableKey = getNonReservationTableStorageKey(tenant);
        const tableMarkers = JSON.parse(localStorage.getItem(tableKey) || "[]");
        const nextTableMarkers = (Array.isArray(tableMarkers) ? tableMarkers : [])
            .map(String)
            .filter((marker) => !marker.startsWith(`${normalizedTableId}:`));
        localStorage.setItem(tableKey, JSON.stringify(nextTableMarkers));

        const normalizedOrderIds = new Set(
            (Array.isArray(orderIds) ? orderIds : [])
                .map(Number)
                .filter((id) => Number.isInteger(id) && id > 0)
        );
        if (normalizedOrderIds.size > 0) {
            const orderKey = getNonReservationOrderStorageKey(tenant);
            const orderMarkers = JSON.parse(localStorage.getItem(orderKey) || "[]");
            const nextOrderMarkers = (Array.isArray(orderMarkers) ? orderMarkers : [])
                .map(Number)
                .filter((orderId) => !normalizedOrderIds.has(orderId));
            localStorage.setItem(orderKey, JSON.stringify(nextOrderMarkers));
        }
    } catch {
        // Local marker cleanup is best-effort only.
    }
};

const getOrderLinkedTableIds = (orderItem) => {
    const ids = new Set();
    const primaryTableId = Number(orderItem?.tableId ?? orderItem?.table?.id);
    if (Number.isInteger(primaryTableId) && primaryTableId > 0) ids.add(primaryTableId);

    (Array.isArray(orderItem?.orderTables) ? orderItem.orderTables : []).forEach((link) => {
        const tableId = Number(link?.tableId ?? link?.table?.id);
        if (Number.isInteger(tableId) && tableId > 0) ids.add(tableId);
    });

    return [...ids];
};

const orderBelongsToTable = (orderItem, tableId) => {
    const normalizedTableId = Number(tableId);
    if (!Number.isInteger(normalizedTableId) || normalizedTableId <= 0) return false;
    return getOrderLinkedTableIds(orderItem).includes(normalizedTableId);
};

// Checkout rows come from JSON APIs, so this remains compatible with older iPad Safari.
const cloneCheckoutRow = (value) => (
    value === undefined ? undefined : JSON.parse(JSON.stringify(value))
);

const getPaymentMethodLabel = (method, t) => {
    const labels = {
        cash: t("checkout_page.cash"),
        cards: t("checkout_page.cards"),
        satispay: t("satispay"),
        helite_room_charge: t("heliteGuestPicker.paymentRoomCharge"),
        [MANUAL_PMS_BUONO_METHOD]: "Buono PMS manuale",
        CASH: "Contanti",
        CARD: "Carta",
        VOUCHER: "Voucher",
        ROOM_CHARGE: "Addebito camera",
        INVOICE: "Fattura",
    };
    return labels[method] || t(`checkout_page.${method}`);
};

const isGenericVoucherUiMethod = (method) => method === MANUAL_PMS_BUONO_METHOD;

const isCardUiMethod = (method) => method === "cards";

const getCardCircuitLabel = (value) => (
    CARD_CIRCUIT_OPTIONS.find((option) => option.value === value)?.label || value || ""
);

const isHeliteRoomChargeUiMethod = (method) => method === "helite_room_charge";

const isFiscalPaymentRow = (row) => (
    !isGenericVoucherUiMethod(row?.method)
    && !isHeliteRoomChargeUiMethod(row?.method)
);

const getFiscalPaymentMethodLabel = (method) => {
    const map = {
        cash: "CONTANTE",
        cards: "CARTA",
        satispay: "ELETTRONICO",
        helite_room_charge: "ADDEBITO HELITE",
        [MANUAL_PMS_BUONO_METHOD]: "BUONO PMS",
        CASH: "CONTANTE",
        CARD: "CARTA",
        VOUCHER: "BUONO",
        ROOM_CHARGE: "ADDEBITO",
        INVOICE: "FATTURA",
    };
    return map[method] || "PAGAMENTO";
};

const toDisplayMessage = (value, fallback = "Errore") => {
    if (value == null) return fallback;
    if (typeof value === "string") return value;
    if (value instanceof Error) return value.message || fallback;
    if (typeof value?.message === "string") return value.message;
    if (value?.message instanceof Error) return value.message.message || fallback;
    try {
        const serialized = JSON.stringify(value);
        return serialized && serialized !== "{}" ? serialized : String(value);
    } catch {
        return String(value);
    }
};

const getCheckoutErrorMessage = (err) => {
    if (err?.code === "CHECKOUT_IN_PROGRESS") {
        const email = err?.details?.holder?.email || "Un altro operatore";
        return `${email} sta chiudendo il conto. Riprova tra poco.`;
    }
    const message = toDisplayMessage(err, "");
    if (
        message.includes("tx.checkoutLock.findUnique")
        || message.includes("P2028")
        || message.includes("25P02")
        || message.includes("current transaction is aborted")
    ) {
        return "Errore backend durante la chiusura conto. Verifica se il buono/pagamento e' gia' stato registrato e riprova dopo refresh.";
    }
    return message || "Errore chiusura conto";
};

const isReservationCompletionBlockedError = (err) => (
    /Impossibile completare la prenotazione/i.test(String(err?.message || err || ""))
    && /ordini non pagati/i.test(String(err?.message || err || ""))
);

const normalizeAmountInput = (value) => {
    const text = String(value ?? "").replace(",", ".").trim();
    const numeric = Number(text);
    return Number.isFinite(numeric) ? numeric : 0;
};

const roundMoney = (value) => Number((Number(value || 0)).toFixed(2));

const prepaymentMovementAvailableAmount = (source, movements = []) => {
    const sourceId = Number(source?.id);
    if (!Number.isInteger(sourceId) || sourceId <= 0 || source?.operation !== "RECEIPT") return 0;
    const usedAmount = (Array.isArray(movements) ? movements : [])
        .filter((movement) => Number(movement?.sourceMovementId) === sourceId)
        .filter((movement) => movement?.operation !== "RECEIPT")
        .reduce((sum, movement) => sum + Math.abs(Number(movement?.amount || 0)), 0);
    return Math.max(0, roundMoney(Number(source?.amount || 0) - usedAmount));
};

const buildPrepaymentApplications = (movements = [], payableTotal = 0) => {
    let remaining = roundMoney(payableTotal);
    const applications = [];
    const receiptMovements = (Array.isArray(movements) ? movements : [])
        .filter((movement) => movement?.operation === "RECEIPT")
        .map((movement) => ({
            movement,
            available: prepaymentMovementAvailableAmount(movement, movements),
        }))
        .filter(({ available }) => available > 0.009)
        .sort((a, b) => {
            if (a.movement?.type === b.movement?.type) return Number(a.movement?.id || 0) - Number(b.movement?.id || 0);
            return a.movement?.type === "ADVANCE" ? -1 : 1;
        });

    for (const { movement, available } of receiptMovements) {
        if (remaining <= 0.009) break;
        const amount = movement?.type === "ADVANCE"
            ? available
            : Math.min(available, remaining);
        if (amount <= 0.009 || amount - remaining > 0.009) continue;
        applications.push({ movementId: Number(movement.id), amount: roundMoney(amount) });
        remaining = roundMoney(remaining - amount);
    }

    return applications;
};

const isCashMethod = (method) => String(method || "").toLowerCase() === "cash";
const isManualPmsBuonoMethod = (method) => String(method || "") === MANUAL_PMS_BUONO_METHOD;

const formatDateDdMmYyyy = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
};

const formatDateTimeDdMmYyyy = (value = new Date()) => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${formatDateDdMmYyyy(date)}, ${hours}:${minutes}`;
};

const formatDateYyyyMmDd = (value = new Date()) => {
    const date = value instanceof Date ? value : new Date(value);
    const validDate = Number.isNaN(date.getTime()) ? new Date() : date;
    const year = validDate.getFullYear();
    const month = String(validDate.getMonth() + 1).padStart(2, "0");
    const day = String(validDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const getComparableText = (value) => String(value ?? "").toLowerCase();

const getHeliteGuestSortValue = (guest, key) => {
    if (key === "arrival" || key === "departure") {
        const date = new Date(guest?.[key]);
        return Number.isNaN(date.getTime()) ? 0 : date.getTime();
    }
    if (key === "roomnr" || key === "number") {
        const value = guest?.[key];
        const numeric = Number(String(value ?? "").replace(/[^\d.-]/g, ""));
        return Number.isFinite(numeric) ? numeric : getComparableText(value);
    }
    return getComparableText(guest?.[key]);
};

function isSameLocalDay(a, b) {
    const da = new Date(a);
    const db = new Date(b);
    return (
        da.getFullYear() === db.getFullYear() &&
        da.getMonth() === db.getMonth() &&
        da.getDate() === db.getDate()
    );
}

const isSpaCabinName = (name) => /^cab(?:in)?\s*\d+/i.test(String(name || "").trim());

const isSpaCheckoutCabin = (table) => (
    isSpaCabinName(table?.name)
    || Boolean(table?.room?.spaCabinProfile)
    || Boolean(table?.spaCabinProfile)
);

const PuntoCassa = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const tenantType = localStorage.getItem('tenantType');
    const normalizedTenantType = String(tenantType || "").trim().toUpperCase();
    const isSaleCongressiTenant = normalizedTenantType === "SALE_CONGRESSI";
    const isSpaTenant = normalizedTenantType === "SPA";
    const { showSnackbar } = useSnackbar();

    const _ = require('lodash')
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');

    const [order, setOrder] = useState(null)
    const [table, setTable] = useState(null)
    const [category, setCategory] = useState(null)
    const [products, setProducts] = useState(null)
    const [reservation, setReservation] = useState(null)
    const [reservationEvents, setReservationEvents] = useState([])

    const [selectedTable, setSelectedTable] = useState("")
    const [selectedTableReservation, setSelectedTableReservation] = useState()
    const [paidReservationLabel, setPaidReservationLabel] = useState(null)
    const [searchProduct, setSearchProduct] = useState("")
    const [selectedCategory, setSelectedCategory] = useState(null)
    const [checkboxSelectedIds, setCheckboxSelectedIds] = useState(new Set());


    // const [alert, setAlert] = useState("");
    // const [openAlert, setOpenAlert] = useState(false);
    // const [typeAlert, setTypeAlert] = useState();

    const [importoDisplay, setImportoDisplay] = useState("");
    const [editPrezzo, setEditPrezzo] = useState(false);
    const [editQty, setEditQty] = useState(false);
    const [cambiaTipoVariazione, setCambiaTipoVariazione] = useState(false); // serve a cambiare -% e +% in -€ e +€
    const [incrementoPrezzo, setIncrementoPrezzo] = useState(false);
    const [decrementoPrezzo, setDecrementoPrezzo] = useState(false);
    const priceEditInFlightRef = useRef(false);
    const [priceEditSubmitting, setPriceEditSubmitting] = useState(false);

    const [ordiniDivisi, setOrdiniDivisi] = useState(false);
    const [splitTargetRowIds, setSplitTargetRowIds] = useState(null);
    const [ordiniFiltrati, setOrdiniFiltrati] = useState([]);
    const [ordineSchermo, setOrdineSchermo] = useState([]);
    const [ordiniUniti, setOrdiniUniti] = useState([]);

    const [openCloseCheckout, setOpenCloseCheckout] = useState(false);
    const [keepTableAfterCheckoutModal, setKeepTableAfterCheckoutModal] = useState(false);
    const [openRiepilogo, setOpenRiepilogo] = useState(false);
    const [createdInvoice, setCreatedInvoice] = useState(null);
    const [openConfirmCloseCheckout, setOpenConfirmCloseCheckout] = useState(false);
    const [openPendingList, setOpenPendingList] = useState(false);
    const [openOccupiedList, setOpenOccupiedList] = useState(false);
    const [occupiedTableList, setOccupiedTableList] = useState([]);
    const [pendingTableList, setPendingTableList] = useState([]);
    const [openMultipleCheckout, setOpenMultipleCheckout] = useState(false);
    const [multipleCheckoutOrderIds, setMultipleCheckoutOrderIds] = useState(new Set());
    const [combinedCheckoutOrders, setCombinedCheckoutOrders] = useState([]);
    const [activeCounterOrderId, setActiveCounterOrderId] = useState(null);
    const activeCounterOrderIdRef = useRef(null);
    const addProductQueueRef = useRef(Promise.resolve());

    const [openTransfer, setOpenTransfer] = useState(false);

    const [openDelete, setOpenDelete] = useState(false);
    const [openLiberaTavolo, setOpenLiberaTavolo] = useState(false);
    const [freeTableSubmitting, setFreeTableSubmitting] = useState(false);
    const [openCreateInvoice, setOpenCreateInvoice] = useState(false)
    const [openInvoiceCloseConfirm, setOpenInvoiceCloseConfirm] = useState(false);
    const [invoiceCloseSubmitting, setInvoiceCloseSubmitting] = useState(false);
    const [invoiceSettlementMethod, setInvoiceSettlementMethod] = useState("");
    const [invoicePaymentRecorded, setInvoicePaymentRecorded] = useState(false);
    const [openDialogError, setOpenDialogError] = useState(false)
    const [createLoading, setCreateLoading] = useState(false);
    const [selectedClientFromReservation, setSelectedClientFromReservation] = useState(null);
    const [selectedCompanyFromReservation, setSelectedCompanyFromReservation] = useState(null);
    const table_to_free = selectedTable;
    const orderTable_da_liberare = (order || []).filter((ord) => orderBelongsToTable(ord, selectedTable?.id));

    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [paymentMethode, setPaymentMethode] = useState("");
    const [closeCheckoutPayments, setCloseCheckoutPayments] = useState([]);
    const [romanSplitParts, setRomanSplitParts] = useState(2);
    const [romanSplitOpen, setRomanSplitOpen] = useState(false);
    const [allaRomanaRecoveryPrompt, setAllaRomanaRecoveryPrompt] = useState(null);
    const [allaRomanaRecoveryDocumentNumber, setAllaRomanaRecoveryDocumentNumber] = useState("");
    const [checkoutPrepaymentSummary, setCheckoutPrepaymentSummary] = useState(null);
    const [checkoutPrepaymentsLoading, setCheckoutPrepaymentsLoading] = useState(false);
    const [lastPaymentSummary, setLastPaymentSummary] = useState(null);
    const [totalPayment, setTotalPayment] = useState(0.00);
    const [openHeliteGuestDialog, setOpenHeliteGuestDialog] = useState(false);
    const [heliteGuests, setHeliteGuests] = useState([]);
    const [heliteGuestsLoading, setHeliteGuestsLoading] = useState(false);
    const [selectedHeliteGuestId, setSelectedHeliteGuestId] = useState("");
    const [heliteGuestSearch, setHeliteGuestSearch] = useState("");
    const [heliteGuestSort, setHeliteGuestSort] = useState({ key: "roomnr", direction: "asc" });
    const [pendingHeliteBuoni, setPendingHeliteBuoni] = useState([]);
    const [heliteBuonoSignatureSaving, setHeliteBuonoSignatureSaving] = useState(false);
    const [heliteBuonoPrintPrompt, setHeliteBuonoPrintPrompt] = useState(null);
    const [heliteBuonoPrintSubmitting, setHeliteBuonoPrintSubmitting] = useState(false);
    const [pendingGenericVouchers, setPendingGenericVouchers] = useState([]);
    const [genericVoucherSignatureSaving, setGenericVoucherSignatureSaving] = useState(false);
    const [genericVoucherPrintPrompt, setGenericVoucherPrintPrompt] = useState(null);
    const [genericVoucherPrintSubmitting, setGenericVoucherPrintSubmitting] = useState(false);
    const [invoiceCourtesyPrintPrompt, setInvoiceCourtesyPrintPrompt] = useState(null);
    const [invoiceCourtesyPrintSubmitting, setInvoiceCourtesyPrintSubmitting] = useState(false);
    const [checkoutSuccessPendingBuonoAction, setCheckoutSuccessPendingBuonoAction] = useState(false);
    const [checkoutSuccessPendingVoucherAction, setCheckoutSuccessPendingVoucherAction] = useState(false);
    const [pendingHelitePaymentRows, setPendingHelitePaymentRows] = useState(null);
    const [checkoutPaymentSubmitting, setCheckoutPaymentSubmitting] = useState(false);
    const [openFiscalDailyClosureConfirm, setOpenFiscalDailyClosureConfirm] = useState(false);
    const [fiscalDailyClosureSubmitting, setFiscalDailyClosureSubmitting] = useState(false);
    const [fiscalDailyClosureError, setFiscalDailyClosureError] = useState("");
    const [fiscalDevices, setFiscalDevices] = useState([]);
    const [selectedFiscalDeviceId, setSelectedFiscalDeviceId] = useState(() => readFiscalDevicePreference(tenant));
    const [fiscalClosureTarget, setFiscalClosureTarget] = useState("default");
    const [prepaymentPosMode, setPrepaymentPosMode] = useState(null);
    const [prepaymentPosForm, setPrepaymentPosForm] = useState(prepaymentPosInitialForm);
    const [selectedPrepaymentProductId, setSelectedPrepaymentProductId] = useState("");
    const [prepaymentPosSubmitting, setPrepaymentPosSubmitting] = useState(false);
    const hasAppliedNavigationTable = useRef(false);
    const hasAppliedRestoredCheckout = useRef(false);
    const hasAppliedRoomChargeShortcut = useRef(false);
    const closeCheckoutPaymentPresetRef = useRef(null);
    const boardBasisRepriceLineKeysRef = useRef(new Set());
    const counterCheckoutTable = useMemo(() => ({
        id: COUNTER_CHECKOUT_ID,
        name: t("checkout_page.counterSale"),
        status: "COUNTER",
        isCounterSale: true,
    }), [t]);
    const isCounterCheckout = selectedTable?.id === COUNTER_CHECKOUT_ID;
    const isMultipleCheckout = selectedTable?.id === MULTIPLE_CHECKOUT_ID;

    const handleAlert = useCallback((msg, type) => {
        showSnackbar(toDisplayMessage(msg), type);
    }, [showSnackbar]);

    const isDark = theme.palette.mode === "dark";
    const COLORS = useMemo(
        () => ({
            background: isDark ? theme.palette.background.default : "#F7F7F7",
            paper: isDark ? "#1E1E1E" : "#FFFFFF",
            border: isDark ? "#3A3A3A" : "#D8D8D8",
            title: isDark ? "#F5F5F5" : "#111111",
            subtitle: isDark ? "#B7B7B7" : "#5F5F5F",
            neutralText: isDark ? "#E6E6E6" : "#333333",
            neutralMuted: isDark ? "#AFAFAF" : "#666666",
            neutralHeaderBg: isDark ? "#242424" : "#FAFAFA",
            neutralTableHeadBg: isDark ? "#2A2A2A" : "#F4F4F4",
            gold: "#A86A00",
            goldDark: "#8A5700",
            green: "#125605",
            greenBg: isDark ? "rgba(18,86,5,0.25)" : "#D8FFE5",
            red: "#A80000",
            redBg: isDark ? "rgba(168,0,0,0.20)" : "#F8E5E5",
            blue: "#0A4FAF",
            blueBg: isDark ? "rgba(10,79,175,0.22)" : "#E8F1FF",
            yellowBg: isDark ? "rgba(168,106,0,0.24)" : "#FFF3D8",
            rowBorder: isDark ? "#343434" : "#EFEFEF",
        }),
        [isDark, theme.palette.background.default]
    );
    const checkoutInfoPaperSx = {
        ...theme.checkout.closeModalCheckout_paper,
        minWidth: 0,
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
    };
    const checkoutInfoValueSx = {
        ...theme.checkout.closeModalCheckout_paperTypography2,
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        lineHeight: 1.25,
    };

    const getVariantIds = useCallback((orderProduct) => {
        if (Array.isArray(orderProduct?.variantIds)) {
            return orderProduct.variantIds
                .map((id) => Number(id))
                .filter((id) => Number.isInteger(id) && id > 0);
        }
        if (Array.isArray(orderProduct?.orderProductVariants)) {
            return orderProduct.orderProductVariants
                .map((opv) => Number(opv?.variantId ?? opv?.variant?.id))
                .filter((id) => Number.isInteger(id) && id > 0);
        }
        const single = Number(orderProduct?.variantId ?? orderProduct?.variant?.id);
        return Number.isInteger(single) && single > 0 ? [single] : [];
    }, []);

    const productHasCategory = useCallback((product, categoryId) => {
        const normalizedCategoryId = Number(categoryId);
        if (!Number.isInteger(normalizedCategoryId)) return false;

        if (Array.isArray(product?.categories) && product.categories.length > 0) {
            return product.categories.some((entry) =>
                Number(entry?.categoryId ?? entry?.category?.id) === normalizedCategoryId
            );
        }

        return Number(product?.categoryId) === normalizedCategoryId;
    }, []);

    const productSearchText = useCallback((product) => {
        const categoryLabels = Array.isArray(product?.categories)
            ? product.categories.flatMap((entry) => [
                entry?.category?.name,
                entry?.subcategory?.name,
                entry?.subcategory?.description,
            ])
            : [];
        const variants = Array.isArray(product?.variants)
            ? product.variants.map((entry) => entry?.variant?.name || entry?.name)
            : [];

        return [
            product?.name,
            product?.description,
            product?.code,
            product?.sku,
            product?.barcode,
            product?.price,
            product?.boardBasis,
            ...categoryLabels,
            ...variants,
        ]
            .filter((value) => value !== undefined && value !== null)
            .join(" ")
            .toLowerCase();
    }, []);
    const getSplitTargetKey = useCallback((row) => {
        if (!row) return "";
        const type = row?.isFixedPriceMenu ? "fixed-menu" : row?.isPackage ? "pkg" : "prd";
        return `${type}:${Number(row?.id)}`;
    }, []);

    const getRowSelectionKey = useCallback((row, splitMode = ordiniDivisi) => {
        if (!row) return "";
        if (splitMode) return String(row?.idSplit ?? "");
        return getSplitTargetKey(row);
    }, [ordiniDivisi, getSplitTargetKey]);

    const getSplitItemKey = useCallback((row) => {
        if (!row) return "";
        const isWeightProduct = !row.isPackage && !row.isFixedPriceMenu && !!row?.product?.pricePerWeight;
        const variantKey = getVariantIds(row).sort((a, b) => a - b).join("-");
        const itemKey = row.isFixedPriceMenu
            ? `fixed-menu-${row.id}`
            : row.isPackage
            ? `package-${row.packageId ?? row.product?.id}`
            : `product-${row.productId ?? row.product?.id}__v:${variantKey || "base"}`;
        const unitPrice = Number(row.unitPrice ?? row.product?.price ?? 0).toFixed(2);
        const discount = Number(row.discount ?? 0).toFixed(2);
        const note = (row.note || "").trim();
        const weightLineKey = isWeightProduct ? `__line:${Number(row?.id ?? 0)}` : "";
        return `${itemKey}__up:${unitPrice}__d:${discount}__n:${note}${weightLineKey}`;
    }, [getVariantIds]);

    const isWeightOrderRow = useCallback((row) => {
        if (!row || row.isPackage) return false;
        const typeOfWeight = row?.product?.typeOfWeight;
        return !!row?.product?.pricePerWeight || typeOfWeight === "KILOGRAM" || typeOfWeight === "HECTOGRAM";
    }, []);

    const getOrderRowsForCheckout = useCallback((ord) => {
        const sourceProducts = (ord?.orderProducts || ord?.products || [])
            .filter((op) => Number(op?.quantity || 0) > 0);
        const sourcePackages = (ord?.orderPackages || ord?.packages || [])
            .filter((op) => Number(op?.quantity || 0) > 0);
        const sourceFixedPriceMenus = (ord?.orderFixedPriceMenus || [])
            .filter((menu) => Number(menu?.quantity || 0) > 0);

        const productRows = sourceProducts.map((op) => ({
            ...op,
            isPackage: false,
            orderId: ord.id,
            orderSubTotal: ord.subTotal,
            orderTotalDiscount: ord.totalDiscount,
            orderDiscountAmount: ord.discountAmount,
            orderSurchargeAmount: ord.surchargeAmount,
        }));

        const packageRows = sourcePackages.map((op) => ({
            ...op,
            isPackage: true,
            orderId: ord.id,
            orderSubTotal: ord.subTotal,
            orderTotalDiscount: ord.totalDiscount,
            orderDiscountAmount: ord.discountAmount,
            orderSurchargeAmount: ord.surchargeAmount,
            product: {
                id: op.packageId,
                name: op.package?.name || `Package #${op.packageId}`,
                price: Number(op.package?.price ?? op.unitPrice ?? 0),
            },
            productId: null,
        }));

        const fixedPriceMenuRows = sourceFixedPriceMenus.map((menu) => {
            const originalPrice = Number(menu.basePriceSnapshot || 0) + Number(menu.supplementsTotal || 0);
            const finalPrice = Number(menu.finalPrice || 0);
            return {
                ...menu,
                isPackage: false,
                isFixedPriceMenu: true,
                orderId: ord.id,
                orderSubTotal: ord.subTotal,
                orderTotalDiscount: ord.totalDiscount,
                orderDiscountAmount: ord.discountAmount,
                orderSurchargeAmount: ord.surchargeAmount,
                unitPrice: finalPrice,
                discount: formattaFloat(finalPrice - originalPrice),
                productId: null,
                product: {
                    id: `fixed-menu-${menu.id}`,
                    name: menu.menuNameSnapshot || t("fixedPriceMenu.menuFallback"),
                    price: originalPrice,
                },
            };
        });

        return [...productRows, ...packageRows, ...fixedPriceMenuRows];
    }, [t]);

    const checkoutTableIdsWithOrders = useMemo(() => {
        const ids = new Set();
        (order || []).forEach((ord) => {
            if (["PAID", "CANCELLED"].includes(ord?.status)) return;
            if (getOrderRowsForCheckout(ord).length === 0) return;
            getOrderLinkedTableIds(ord).forEach((tableId) => ids.add(tableId));
        });
        return ids;
    }, [order, getOrderRowsForCheckout]);

    const nonReservationOrderIds = useMemo(() => getNonReservationOrderIds(tenant), [tenant, order]);
    const nonReservationTableMarkers = useMemo(() => getNonReservationTableMarkers(tenant), [tenant, order, selectedTable?.id]);

    const congressCheckedInTableLabels = useMemo(() => {
        const labelsByTableId = new Map();
        if (!isSaleCongressiTenant) return labelsByTableId;

        (Array.isArray(reservationEvents) ? reservationEvents : [])
            .filter((event) => ["CHECKED_IN", "NO_SHOW"].includes(event?.status))
            .forEach((event) => {
                const clientName = [event?.client?.name, event?.client?.surname].filter(Boolean).join(" ").trim();
                const recipientName = clientName
                    || event?.company?.companyName
                    || event?.genericRecipientName
                    || event?.eventName
                    || "Cliente non indicato";
                (event?.days || []).forEach((day) => {
                    (day?.tableLinks || []).forEach((link) => {
                        const tableId = Number(link?.tableId ?? link?.table?.id);
                        if (!Number.isInteger(tableId) || tableId <= 0) return;
                        const names = labelsByTableId.get(tableId) || new Set();
                        names.add(recipientName);
                        labelsByTableId.set(tableId, names);
                    });
                });
            });

        return new Map([...labelsByTableId.entries()].map(([tableId, names]) => [
            tableId,
            [...names].join(", "),
        ]));
    }, [isSaleCongressiTenant, reservationEvents]);

    const checkoutTableLabel = useCallback((tab) => {
        const baseName = tab?.name || "";
        if (!isSaleCongressiTenant) return baseName;
        const recipient = congressCheckedInTableLabels.get(Number(tab?.id));
        return recipient ? `${baseName} - ${recipient}` : baseName;
    }, [congressCheckedInTableLabels, isSaleCongressiTenant]);

    const checkoutSelectableTables = useMemo(() => {
        if (!Array.isArray(table)) return [];
        const seen = new Set();
        return table.filter((tab) => {
            const tableId = Number(tab?.id);
            if (!Number.isInteger(tableId) || tableId <= 0 || seen.has(tableId)) return false;
            const hasCheckoutOrder = checkoutTableIdsWithOrders.has(tableId);
            const canCheckout = isSaleCongressiTenant
                ? congressCheckedInTableLabels.has(tableId) && hasCheckoutOrder
                : isSpaTenant
                    ? isSpaCheckoutCabin(tab)
                : tab?.status === "IN_ATTESA" || hasCheckoutOrder;
            if (!canCheckout) return false;
            seen.add(tableId);
            return true;
        });
    }, [table, checkoutTableIdsWithOrders, congressCheckedInTableLabels, isSaleCongressiTenant, isSpaTenant]);

    const checkoutSourceRows = useMemo(
        () => (ordiniDivisi ? (ordineSchermo || []) : (ordiniUniti || [])),
        [ordiniDivisi, ordineSchermo, ordiniUniti]
    );

    const checkoutPayableRows = useMemo(() => {
        if (checkboxSelectedIds.size < 1) return ordiniFiltrati || [];
        return checkoutSourceRows.filter((row) => checkboxSelectedIds.has(getRowSelectionKey(row)));
    }, [
        checkoutSourceRows,
        ordiniFiltrati,
        checkboxSelectedIds,
        getRowSelectionKey,
    ]);

    const getRowsSnapshotTotal = useCallback((rows = []) => Number((rows || []).reduce((total, elem) => {
        const unitPrice = Number(elem.unitPrice ?? elem.product?.price ?? 0);
        const quantity = Number(elem.quantity ?? 0);
        return total + (unitPrice * quantity);
    }, 0).toFixed(2)), []);

    const getOrderNetTotal = useCallback((subTotal, totalDiscount = 0, discountAmount = 0, surchargeAmount = 0) => {
        const gross = Number(subTotal || 0);
        const percent = Number(totalDiscount || 0);
        const amount = Math.max(0, Number(discountAmount || 0));
        const surcharge = Math.max(0, Number(surchargeAmount || 0));
        return Number(Math.max(0, gross * (1 + percent / 100) + surcharge - amount).toFixed(2));
    }, []);

    const getBackendOrdersTotal = useCallback((rows = []) => {
        const totalsByOrder = new Map();
        const fallbackRows = [];
        const sourceRows = (rows || []).flatMap((row) => (
            Array.isArray(row?.originalOrders) && row.originalOrders.length > 0
                ? row.originalOrders
                : [row]
        ));

        sourceRows.forEach((row) => {
            const orderId = Number(row?.orderId);
            const hasOrderSubTotal = row?.orderSubTotal !== undefined && row?.orderSubTotal !== null && row?.orderSubTotal !== "";
            const orderSubTotal = Number(row?.orderSubTotal);
            if (Number.isInteger(orderId) && hasOrderSubTotal && Number.isFinite(orderSubTotal)) {
                totalsByOrder.set(orderId, getOrderNetTotal(
                    orderSubTotal,
                    row?.orderTotalDiscount,
                    row?.orderDiscountAmount,
                    row?.orderSurchargeAmount
                ));
            } else {
                fallbackRows.push(row);
            }
        });

        const backendTotal = [...totalsByOrder.values()].reduce((sum, value) => sum + value, 0);
        return Number((backendTotal + getRowsSnapshotTotal(fallbackRows)).toFixed(2));
    }, [getOrderNetTotal, getRowsSnapshotTotal]);

    const getBackendOrdersGrossTotal = useCallback((rows = []) => {
        const subtotalsByOrder = new Map();
        const fallbackRows = [];
        const sourceRows = (rows || []).flatMap((row) => (
            Array.isArray(row?.originalOrders) && row.originalOrders.length > 0
                ? row.originalOrders
                : [row]
        ));

        sourceRows.forEach((row) => {
            const orderId = Number(row?.orderId);
            const hasOrderSubTotal = row?.orderSubTotal !== undefined && row?.orderSubTotal !== null && row?.orderSubTotal !== "";
            const orderSubTotal = Number(row?.orderSubTotal);
            if (Number.isInteger(orderId) && hasOrderSubTotal && Number.isFinite(orderSubTotal)) {
                subtotalsByOrder.set(orderId, orderSubTotal);
            } else {
                fallbackRows.push(row);
            }
        });

        return roundMoney(
            [...subtotalsByOrder.values()].reduce((sum, value) => sum + value, 0)
            + getRowsSnapshotTotal(fallbackRows)
        );
    }, [getRowsSnapshotTotal]);

    const isPartialCheckoutSelection = useMemo(() => (
        !isMultipleCheckout
        && checkboxSelectedIds.size > 0
        && checkoutSourceRows.some((row) => !checkboxSelectedIds.has(getRowSelectionKey(row)))
    ), [
        isMultipleCheckout,
        checkboxSelectedIds,
        checkoutSourceRows,
        getRowSelectionKey,
    ]);

    const closeCheckoutActionLabel = useMemo(
        () => isPartialCheckoutSelection
            ? t("checkout_page.partialPayment")
            : t("checkout_page.totalPayment"),
        [isPartialCheckoutSelection, t]
    );

    const checkoutTotalValue = useMemo(() => {
        if (checkboxSelectedIds.size < 1 || !isPartialCheckoutSelection) return getBackendOrdersTotal(checkoutPayableRows);
        return getRowsSnapshotTotal(checkoutPayableRows);
    }, [checkboxSelectedIds.size, checkoutPayableRows, getBackendOrdersTotal, getRowsSnapshotTotal, isPartialCheckoutSelection]);
    const checkoutGrossTotalValue = useMemo(() => (
        isPartialCheckoutSelection
            ? getRowsSnapshotTotal(checkoutPayableRows)
            : getBackendOrdersGrossTotal(checkoutPayableRows)
    ), [checkoutPayableRows, getBackendOrdersGrossTotal, getRowsSnapshotTotal, isPartialCheckoutSelection]);
    const checkoutOrderAdjustmentTotals = useMemo(() => {
        if (isPartialCheckoutSelection) return { surcharge: 0, discount: 0 };

        const byOrder = new Map();
        const sourceRows = (checkoutPayableRows || []).flatMap((row) => (
            Array.isArray(row?.originalOrders) && row.originalOrders.length > 0
                ? row.originalOrders
                : [row]
        ));
        sourceRows.forEach((row) => {
            const orderId = Number(row?.orderId);
            if (!Number.isInteger(orderId) || byOrder.has(orderId)) return;
            const subtotal = Number(row?.orderSubTotal || 0);
            const percentAdjustment = subtotal * Number(row?.orderTotalDiscount || 0) / 100;
            byOrder.set(orderId, {
                surcharge: Math.max(0, Number(row?.orderSurchargeAmount || 0)) + Math.max(0, percentAdjustment),
                discount: Math.max(0, Number(row?.orderDiscountAmount || 0)),
            });
        });

        return [...byOrder.values()].reduce((totals, item) => ({
            surcharge: roundMoney(totals.surcharge + item.surcharge),
            discount: roundMoney(totals.discount + item.discount),
        }), { surcharge: 0, discount: 0 });
    }, [checkoutPayableRows, isPartialCheckoutSelection]);
    const checkoutOrderSurchargeItems = useMemo(() => {
        if (checkoutOrderAdjustmentTotals.surcharge <= 0.009) return [];
        return [{ orderId: "consolidated-extra", amount: checkoutOrderAdjustmentTotals.surcharge, label: "Extra banco", sign: 1 }];
    }, [checkoutOrderAdjustmentTotals.surcharge]);
    const checkoutOrderDiscountItems = useMemo(() => {
        if (checkoutOrderAdjustmentTotals.discount <= 0.009) return [];
        return [{ orderId: "consolidated-discount", amount: checkoutOrderAdjustmentTotals.discount, label: "Sconto ordine", sign: -1 }];
    }, [checkoutOrderAdjustmentTotals.discount]);
    const checkoutOrderIdsForPayment = useMemo(() => [...new Set(checkoutPayableRows
        .map((row) => Number(row?.orderId))
        .filter((orderId) => Number.isInteger(orderId) && orderId > 0))], [checkoutPayableRows]);
    const checkoutOrdersForPayment = useMemo(() => (order || []).filter((item) =>
        checkoutOrderIdsForPayment.includes(Number(item?.id))
    ), [order, checkoutOrderIdsForPayment]);
    const checkoutReservationEventIds = useMemo(() => [...new Set(checkoutOrdersForPayment
        .map((item) => Number(item?.reservationEventId))
        .filter((eventId) => Number.isInteger(eventId) && eventId > 0))], [checkoutOrdersForPayment]);
    const routeCheckoutReservationEventId = useMemo(() => {
        const params = new URLSearchParams(location.search || "");
        const routeTableId = Number(location.state?.checkoutTableId) || Number(params.get("tableId"));
        const selectedTableId = Number(selectedTable?.id);
        if (routeTableId && selectedTableId && routeTableId !== selectedTableId) return null;

        const stateId = Number(
            location.state?.reservationEventId
            ?? location.state?.checkoutReservationEventId
        );
        if (Number.isInteger(stateId) && stateId > 0) return stateId;

        const queryId = Number(params.get("reservationEventId") || params.get("eventId"));
        return Number.isInteger(queryId) && queryId > 0 ? queryId : null;
    }, [location.search, location.state, selectedTable?.id]);
    const preserveReservationEventStatus = useMemo(() => {
        const params = new URLSearchParams(location.search || "");
        const status = String(
            location.state?.preserveReservationEventStatus
            || params.get("preserveReservationEventStatus")
            || ""
        ).toUpperCase();
        return status === "NO_SHOW" ? status : null;
    }, [location.search, location.state]);
    const routeRestoredReservationId = useMemo(() => {
        const id = Number(location.state?.restoredReservationId);
        return Number.isInteger(id) && id > 0 ? id : null;
    }, [location.state]);
    const checkoutReservationEventOrderLink = useMemo(() => (
        isSaleCongressiTenant
        && Number.isInteger(Number(routeCheckoutReservationEventId))
        && Number(routeCheckoutReservationEventId) > 0
            ? { reservationEventId: Number(routeCheckoutReservationEventId) }
            : {}
    ), [isSaleCongressiTenant, routeCheckoutReservationEventId]);
    const checkoutReservationEventIdFromEvents = useMemo(() => {
        if (!isSaleCongressiTenant || isCounterCheckout || isMultipleCheckout || !selectedTable?.id) return null;
        const tableId = Number(selectedTable.id);
        const now = new Date();
        const candidates = (Array.isArray(reservationEvents) ? reservationEvents : [])
            .filter((event) => event?.status === "CHECKED_IN")
            .filter((event) => (Array.isArray(event?.days) ? event.days : []).some((day) => {
                const dayDate = new Date(day?.startDate || day?.dayDate);
                if (Number.isNaN(dayDate.getTime()) || !isSameLocalDay(dayDate, now)) return false;
                return (Array.isArray(day?.tableLinks) ? day.tableLinks : []).some((link) =>
                    Number(link?.tableId ?? link?.table?.id) === tableId
                );
            }));

        const clientMatched = candidates.filter((event) => checkoutOrdersForPayment.some((ord) => (
            (event?.clientId != null && Number(ord?.clientId) === Number(event.clientId)) ||
            (event?.client?.id != null && Number(ord?.clientId) === Number(event.client.id)) ||
            (event?.companyId != null && Number(ord?.companyId) === Number(event.companyId)) ||
            (event?.company?.id != null && Number(ord?.companyId) === Number(event.company.id))
        )));
        const source = clientMatched.length === 1
            ? clientMatched
            : candidates.length === 1
                ? candidates
                : [];
        const eventId = Number(source[0]?.id);
        return Number.isInteger(eventId) && eventId > 0 ? eventId : null;
    }, [checkoutOrdersForPayment, isCounterCheckout, isMultipleCheckout, isSaleCongressiTenant, reservationEvents, selectedTable?.id]);
    const checkoutReservationEventIdFromReservation = useMemo(() => {
        if (isCounterCheckout || isMultipleCheckout || !selectedTable?.id || !Array.isArray(reservation)) return null;
        const tableId = Number(selectedTable.id);
        const now = new Date();
        const eventIds = [...new Set(reservation
            .filter((res) => {
                if (!["CONFIRMED", "CHECKED_IN"].includes(res?.status)) return false;
                if (nonReservationTableMarkers.has(`${tableId}:${Number(res?.id)}`)) return false;
                const belongsToTable = Array.isArray(res?.tableReservations)
                    && res.tableReservations.some((tr) => Number(tr?.tableId ?? tr?.table?.id) === tableId);
                if (!belongsToTable) return false;
                const start = new Date(res.startDate);
                if (Number.isNaN(start.getTime()) || !isSameLocalDay(start, now)) return false;
                return true;
            })
            .map((res) => Number(
                res?.eventDay?.eventId
                ?? res?.eventDay?.event?.id
                ?? res?.reservationEventId
                ?? res?.eventId
            ))
            .filter((eventId) => Number.isInteger(eventId) && eventId > 0))];
        return eventIds.length === 1 ? eventIds[0] : null;
    }, [isCounterCheckout, isMultipleCheckout, nonReservationTableMarkers, reservation, selectedTable?.id]);
    const checkoutReservationEventId = useMemo(() => (
        isSaleCongressiTenant
        && !isMultipleCheckout
        && !isPartialCheckoutSelection
        && (routeCheckoutReservationEventId || checkoutReservationEventIds.length === 1 || checkoutReservationEventIdFromEvents || checkoutReservationEventIdFromReservation)
            ? routeCheckoutReservationEventId || checkoutReservationEventIds[0] || checkoutReservationEventIdFromEvents || checkoutReservationEventIdFromReservation
            : null
    ), [
        checkoutReservationEventIdFromEvents,
        checkoutReservationEventIdFromReservation,
        checkoutReservationEventIds,
        isMultipleCheckout,
        isPartialCheckoutSelection,
        isSaleCongressiTenant,
        routeCheckoutReservationEventId,
    ]);
    const routePrepaymentPosMode = useMemo(() => {
        const params = new URLSearchParams(location.search || "");
        return normalizePrepaymentPosMode(location.state?.prepaymentMode || params.get("prepaymentMode"));
    }, [location.search, location.state]);
    const routeRoomChargeShortcut = useMemo(() => {
        const params = new URLSearchParams(location.search || "");
        return Boolean(location.state?.roomChargeShortcut || params.get("payment") === "room_charge");
    }, [location.search, location.state]);
    const isPrepaymentPosActive = Boolean(isSaleCongressiTenant && checkoutReservationEventId && prepaymentPosMode);
    const checkoutPrepaymentApplications = useMemo(() => (
        buildPrepaymentApplications(checkoutPrepaymentSummary?.movements, checkoutTotalValue)
    ), [checkoutPrepaymentSummary?.movements, checkoutTotalValue]);
    const checkoutAppliedPrepaymentTotal = useMemo(() => roundMoney(
        checkoutPrepaymentApplications.reduce((sum, item) => sum + Number(item.amount || 0), 0)
    ), [checkoutPrepaymentApplications]);
    const checkoutAmountDue = useMemo(
        () => Math.max(0, roundMoney(checkoutTotalValue - checkoutAppliedPrepaymentTotal)),
        [checkoutTotalValue, checkoutAppliedPrepaymentTotal]
    );
    const closeCheckoutPaymentsTotal = useMemo(
        () => closeCheckoutPayments.reduce((sum, row) => sum + normalizeAmountInput(row.amount), 0),
        [closeCheckoutPayments]
    );
    const closeCheckoutRemaining = useMemo(
        () => Math.max(0, roundMoney(checkoutAmountDue - closeCheckoutPaymentsTotal)),
        [checkoutAmountDue, closeCheckoutPaymentsTotal]
    );
    const isCloseCheckoutPaymentBalanced = useMemo(
        () => checkoutAmountDue <= 0.009
            ? closeCheckoutPaymentsTotal <= 0.009
            : Math.abs(closeCheckoutPaymentsTotal - checkoutAmountDue) < 0.01,
        [closeCheckoutPaymentsTotal, checkoutAmountDue]
    );
    const isCashGivenValid = useMemo(() => closeCheckoutPayments.every((row) => {
        if (!isCashMethod(row.method)) return true;
        return normalizeAmountInput(row.given) >= normalizeAmountInput(row.amount);
    }), [closeCheckoutPayments]);
    const areCardCircuitsValid = useMemo(() => closeCheckoutPayments.every((row) => (
        !isCardUiMethod(row.method) || Boolean(row.cardCircuit)
    )), [closeCheckoutPayments]);
    const totalCashChange = useMemo(
        () => closeCheckoutPayments.reduce((sum, row) => {
            if (!isCashMethod(row.method)) return sum;
            const rowChange = normalizeAmountInput(row.given) - normalizeAmountInput(row.amount);
            return sum + (rowChange > 0 ? rowChange : 0);
        }, 0),
        [closeCheckoutPayments]
    );

    const initializeCloseCheckoutPayments = useCallback(() => {
        const amount = Number(checkoutAmountDue || 0).toFixed(2);
        if (checkoutAmountDue <= 0.009) {
            setCloseCheckoutPayments([]);
            return;
        }
        const presetMethod = closeCheckoutPaymentPresetRef.current || "cash";
        closeCheckoutPaymentPresetRef.current = null;
        setCloseCheckoutPayments([{
            id: Date.now(),
            method: presetMethod,
            cardCircuit: "",
            amount,
            given: isCashMethod(presetMethod) ? amount : "",
        }]);
    }, [checkoutAmountDue]);

    useEffect(() => {
        if (openConfirmCloseCheckout) {
            setRomanSplitOpen(false);
            initializeCloseCheckoutPayments();
        }
    }, [openConfirmCloseCheckout, initializeCloseCheckoutPayments]);

    useEffect(() => {
        let active = true;
        getConfiguredFiscalDevices(token, tenant)
            .then((devices) => {
                if (!active) return;
                const normalized = Array.isArray(devices) ? devices : [];
                setFiscalDevices(normalized);
                const defaultDevice = normalized.find((device) => device.isDefault) || normalized[0];
                const preferredDeviceId = readFiscalDevicePreference(tenant);
                const hasPreferredDevice = normalized.some(
                    (device) => String(device.id) === preferredDeviceId
                );
                if (preferredDeviceId && !hasPreferredDevice) {
                    writeFiscalDevicePreference(tenant, "");
                }
                setSelectedFiscalDeviceId(
                    hasPreferredDevice
                        ? preferredDeviceId
                        : defaultDevice ? String(defaultDevice.id) : ""
                );
                setFiscalClosureTarget((current) => (
                    current === "all" || normalized.some((device) => String(device.id) === String(current))
                        ? current
                        : defaultDevice ? String(defaultDevice.id) : "default"
                ));
            })
            .catch((error) => {
                if (active) console.warn("Impossibile caricare le casse fiscali:", error?.message || error);
            });
        return () => { active = false; };
    }, [tenant, token]);

    useEffect(() => {
        let active = true;

        const loadCheckoutPrepayments = async () => {
            if (!checkoutReservationEventId) {
                setCheckoutPrepaymentSummary(null);
                setCheckoutPrepaymentsLoading(false);
                return;
            }

            setCheckoutPrepaymentsLoading(true);
            try {
                const summary = await getReservationEventPrepaymentSummary(checkoutReservationEventId, token, tenant);
                if (active) setCheckoutPrepaymentSummary(summary);
            } catch (err) {
                if (active) {
                    setCheckoutPrepaymentSummary(null);
                    handleAlert(err?.message || t("checkout_page.prepaymentsLoadError"), "warning");
                }
            } finally {
                if (active) setCheckoutPrepaymentsLoading(false);
            }
        };

        loadCheckoutPrepayments();
        return () => {
            active = false;
        };
    }, [checkoutReservationEventId, handleAlert, tenant, token, t]);

    useEffect(() => {
        if (routePrepaymentPosMode && isSaleCongressiTenant) {
            setPrepaymentPosMode(routePrepaymentPosMode);
            setPrepaymentPosForm((prev) => ({
                ...prepaymentPosInitialForm(),
                ...prev,
                operation: "RECEIVE",
                documentType: routePrepaymentPosMode === "DEPOSIT" ? "PLAIN_RECEIPT" : "COMMERCIAL_DOCUMENT",
            }));
            return;
        }
        if (!checkoutReservationEventId) {
            setPrepaymentPosMode(null);
        }
    }, [checkoutReservationEventId, isSaleCongressiTenant, routePrepaymentPosMode]);

    const updateClosePaymentRow = useCallback((id, updates) => {
        setCloseCheckoutPayments((prev) => prev.map((row) => {
            if (row.id !== id) return row;
            const cashGivenWasAutomatic = isCashMethod(row.method)
                && normalizeAmountInput(row.given) === normalizeAmountInput(row.amount);

            return {
                    ...row,
                    ...updates,
                    ...(updates.amount !== undefined && cashGivenWasAutomatic ? { given: updates.amount } : {}),
                    ...(updates.method && !isCashMethod(updates.method) ? { given: "" } : {}),
                    ...(updates.method && isCashMethod(updates.method) && !row.given ? { given: row.amount } : {}),
                    ...(updates.method && !isCardUiMethod(updates.method) ? { cardCircuit: "" } : {}),
                };
        }));
    }, []);

    const addClosePaymentRow = useCallback(() => {
        setCloseCheckoutPayments((prev) => [
            ...prev,
            {
                id: Date.now() + prev.length,
                method: "cards",
                cardCircuit: "",
                amount: closeCheckoutRemaining.toFixed(2),
                given: "",
            }
        ]);
    }, [closeCheckoutRemaining]);

    const removeClosePaymentRow = useCallback((id) => {
        setCloseCheckoutPayments((prev) => prev.filter((row) => row.id !== id));
    }, []);

    const completeAllaRomanaCheckout = useCallback((result, paymentUiMethod, fallbackParts) => {
        const total = Number(result?.total ?? checkoutAmountDue);
        const documentsCount = Array.isArray(result?.documents) ? result.documents.length : fallbackParts;

        setLastPaymentSummary({
            total,
            methodLabel: `${t("checkout_page.romanSplit")} (${documentsCount})`,
            cashGiven: 0,
            cashChange: 0,
            hasCash: false,
        });
        setPaymentMethode(paymentUiMethod);
        setTotalPayment(total);
        setRomanSplitOpen(false);
        setAllaRomanaRecoveryPrompt(null);
        setAllaRomanaRecoveryDocumentNumber("");
        setOpenConfirmCloseCheckout(false);
        window.dispatchEvent(new CustomEvent('comanda-creata'));
        window.dispatchEvent(new CustomEvent('map-data-changed'));
        setRefreshTrigger((prev) => prev + 1);
        handleAlert(`${documentsCount} scontrini emessi con divisione in parti uguali`, "success");
        setOpenCloseCheckout(true);
    }, [checkoutAmountDue, handleAlert, t]);

    const handleAllaRomanaCheckout = useCallback(async () => {
        if (checkoutPaymentSubmitting) return;
        const parts = Math.max(2, Math.trunc(Number(romanSplitParts) || 2));
        const orderIds = checkoutOrderIdsForPayment.map(Number);
        const orderId = orderIds[0];
        const romanSplitPaymentUiMethod = closeCheckoutPayments[0]?.method || "cash";
        const paymentMethod = ALLA_ROMANA_PAYMENT_METHOD_BY_UI_METHOD[romanSplitPaymentUiMethod];

        if (orderIds.length === 0 || orderIds.some((id) => !Number.isInteger(id) || id <= 0)) {
            handleAlert("Nessun ordine valido da dividere in parti uguali", "warning");
            return;
        }
        if (isPartialCheckoutSelection) {
            handleAlert("La divisione in parti uguali non è disponibile per un pagamento parziale", "warning");
            return;
        }
        if (!paymentMethod) {
            handleAlert("Metodo di pagamento non supportato per la divisione in parti uguali", "warning");
            return;
        }
        if (romanSplitPaymentUiMethod === "cards" && !closeCheckoutPayments[0]?.cardCircuit) {
            handleAlert("Seleziona il tipo di carta", "warning");
            return;
        }
        if (checkoutAmountDue <= 0.009) {
            handleAlert(t("checkout_page.invalidCheckoutTotal"), "warning");
            return;
        }
        if (checkoutAppliedPrepaymentTotal > 0.009 || checkoutPrepaymentApplications.length > 0) {
            handleAlert("La divisione in parti uguali non è disponibile con acconti o anticipi applicati", "warning");
            return;
        }

        setCheckoutPaymentSubmitting(true);
        const payload = {
            orderId: Number(orderId),
            ...(orderIds.length > 1 ? { orderIds } : {}),
            parts,
            paymentMethod,
            ...(selectedFiscalDeviceId ? { fiscalDeviceId: Number(selectedFiscalDeviceId) } : {}),
            ...(paymentMethod === "CARD" && closeCheckoutPayments[0]?.cardCircuit
                ? { cardCircuit: closeCheckoutPayments[0].cardCircuit }
                : {}),
            idempotencyKey: [
                "alla-romana",
                orderIds.join("-"),
                parts,
                Math.round(Number(checkoutAmountDue || 0) * 100),
            ].join(":"),
        };
        try {
            const result = await checkoutAllaRomana(payload, token, tenant);
            completeAllaRomanaCheckout(result, romanSplitPaymentUiMethod, parts);
        } catch (err) {
            if (ALLA_ROMANA_RECOVERY_ERROR_CODES.has(err?.code) && err?.details?.receiptId) {
                setAllaRomanaRecoveryPrompt({
                    payload,
                    paymentUiMethod: romanSplitPaymentUiMethod,
                    details: err.details,
                    message: getCheckoutErrorMessage(err),
                });
                setAllaRomanaRecoveryDocumentNumber("");
                handleAlert("Verifica la cassa fiscale prima di continuare la divisione in parti uguali", "warning");
                return;
            }
            handleAlert(getCheckoutErrorMessage(err), err?.code === "CHECKOUT_IN_PROGRESS" ? "warning" : "error");
        } finally {
            setCheckoutPaymentSubmitting(false);
        }
    }, [
        checkoutAmountDue,
        checkoutAppliedPrepaymentTotal,
        closeCheckoutPayments,
        checkoutOrderIdsForPayment,
        checkoutPaymentSubmitting,
        checkoutPrepaymentApplications.length,
        completeAllaRomanaCheckout,
        handleAlert,
        isPartialCheckoutSelection,
        romanSplitParts,
        selectedFiscalDeviceId,
        t,
        tenant,
        token,
    ]);

    const handleAllaRomanaRecovery = useCallback(async (outcome) => {
        if (checkoutPaymentSubmitting || !allaRomanaRecoveryPrompt) return;

        const details = allaRomanaRecoveryPrompt.details || {};
        const documentNumber = allaRomanaRecoveryDocumentNumber.trim();
        if (outcome === "PRINTED" && !documentNumber) {
            handleAlert("Inserisci il numero documento stampato dalla cassa", "warning");
            return;
        }

        const recovery = {
            receiptId: Number(details.receiptId),
            outcome,
            verified: true,
            ...(outcome === "PRINTED" ? { documentNumber } : {}),
        };

        setCheckoutPaymentSubmitting(true);
        try {
            const result = await checkoutAllaRomana({
                ...allaRomanaRecoveryPrompt.payload,
                recovery,
            }, token, tenant);
            completeAllaRomanaCheckout(
                result,
                allaRomanaRecoveryPrompt.paymentUiMethod,
                allaRomanaRecoveryPrompt.payload.parts
            );
        } catch (err) {
            if (ALLA_ROMANA_RECOVERY_ERROR_CODES.has(err?.code) && err?.details?.receiptId) {
                setAllaRomanaRecoveryPrompt((current) => ({
                    ...(current || allaRomanaRecoveryPrompt),
                    details: err.details,
                    message: getCheckoutErrorMessage(err),
                }));
                setAllaRomanaRecoveryDocumentNumber("");
                handleAlert("Verifica di nuovo la cassa fiscale prima di continuare", "warning");
                return;
            }
            handleAlert(getCheckoutErrorMessage(err), err?.code === "CHECKOUT_IN_PROGRESS" ? "warning" : "error");
        } finally {
            setCheckoutPaymentSubmitting(false);
        }
    }, [
        allaRomanaRecoveryDocumentNumber,
        allaRomanaRecoveryPrompt,
        checkoutPaymentSubmitting,
        completeAllaRomanaCheckout,
        handleAlert,
        tenant,
        token,
    ]);

    const prodottiFiltratiPerCategoria = selectedCategory ? (products?.filter(p => productHasCategory(p, selectedCategory))) : (products)
    const normalizedProductSearch = searchProduct.trim().toLowerCase();
    const prodottiFiltratiPerNome = normalizedProductSearch
        ? products?.filter(p => productSearchText(p).includes(normalizedProductSearch))
        : prodottiFiltratiPerCategoria
    const prepaymentPosProducts = useMemo(() => {
        if (!prepaymentPosMode) return [];
        const modeNeedles = prepaymentPosMode === "DEPOSIT"
            ? ["deposit", "deposito", "depositi", "versamento deposito", "reso deposito"]
            : ["advance", "anticipo", "anticipi", "acconto"];
        return (Array.isArray(products) ? products : [])
            .filter((product) => modeNeedles.some((needle) => productSearchText(product).includes(needle)))
            .filter((product) => (
                searchProduct.trim().length === 0
                || productSearchText(product).includes(searchProduct.trim().toLowerCase())
            ));
    }, [prepaymentPosMode, productSearchText, products, searchProduct]);

    const getReservationRecipientLabel = useCallback((reservationItem) => {
        if (!reservationItem) return null;
        const clientName = [reservationItem.client?.name, reservationItem.client?.surname]
            .filter(Boolean)
            .join(" ")
            .trim();
        return clientName
            || reservationItem.company?.companyName
            || reservationItem.genericRecipientName
            || reservationItem.eventName
            || null;
    }, []);

    const activeCheckoutReservation = useMemo(() => {
        if (isCounterCheckout || isMultipleCheckout || !selectedTable?.id || !Array.isArray(reservation)) return null;
        if (routeRestoredReservationId) {
            const restoredReservation = reservation.find((item) => Number(item?.id) === routeRestoredReservationId);
            if (restoredReservation) return restoredReservation;
        }
        const now = new Date();
        const tableId = Number(selectedTable.id);
        const candidates = reservation
            .filter((res) => {
                if (!["CONFIRMED", "CHECKED_IN"].includes(res?.status)) return false;
                if (nonReservationTableMarkers.has(`${tableId}:${Number(res?.id)}`)) return false;
                const belongsToTable = Array.isArray(res?.tableReservations)
                    && res.tableReservations.some((tr) => Number(tr?.tableId ?? tr?.table?.id) === tableId);
                if (!belongsToTable) return false;
                const start = new Date(res.startDate);
                if (Number.isNaN(start.getTime())) return false;
                return isSameLocalDay(start, now);
            })
            .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

        const tableOrders = (order || []).filter((ord) =>
            orderBelongsToTable(ord, tableId) &&
            !["PAID", "CANCELLED"].includes(ord?.status) &&
            getOrderRowsForCheckout(ord).length > 0 &&
            !nonReservationOrderIds.has(Number(ord?.id))
        );
        const matchedByOrderRecipient = candidates.find((res) =>
            tableOrders.some((ord) => (
                (res?.clientId != null && Number(ord?.clientId) === Number(res.clientId)) ||
                (res?.client?.id != null && Number(ord?.clientId) === Number(res.client.id)) ||
                (res?.companyId != null && Number(ord?.companyId) === Number(res.companyId)) ||
                (res?.company?.id != null && Number(ord?.companyId) === Number(res.company.id))
            ))
        );
        if (matchedByOrderRecipient) return matchedByOrderRecipient;

        if (candidates.length === 1 && tableOrders.length > 0) {
            return candidates[0];
        }

        return null;
    }, [order, reservation, selectedTable?.id, isCounterCheckout, isMultipleCheckout, getOrderRowsForCheckout, nonReservationOrderIds, nonReservationTableMarkers, routeRestoredReservationId]);

    const activeCheckoutReservationEvent = useMemo(() => {
        if (!routeCheckoutReservationEventId || !Array.isArray(reservationEvents)) return null;
        return reservationEvents.find((event) => Number(event?.id) === Number(routeCheckoutReservationEventId)) || null;
    }, [reservationEvents, routeCheckoutReservationEventId]);

    const selectedTableTodayReservation = useMemo(() => {
        if (isCounterCheckout || isMultipleCheckout || !selectedTable?.id || !Array.isArray(reservation)) return null;
        const now = new Date();
        const tableId = Number(selectedTable.id);
        return reservation
            .filter((res) => {
                if (!["CONFIRMED", "CHECKED_IN"].includes(res?.status)) return false;
                const belongsToTable = Array.isArray(res?.tableReservations)
                    && res.tableReservations.some((tr) => Number(tr?.tableId ?? tr?.table?.id) === tableId);
                if (!belongsToTable) return false;
                const start = new Date(res.startDate);
                if (Number.isNaN(start.getTime())) return false;
                return isSameLocalDay(start, now);
            })
            .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))[0] || null;
    }, [reservation, selectedTable?.id, isCounterCheckout, isMultipleCheckout]);

    const checkoutClientLabel = useMemo(() => {
        if (isCounterCheckout) return t("checkout_page.counterSale");
        if (paidReservationLabel || selectedTableReservation) return paidReservationLabel || selectedTableReservation;
        if (selectedTableTodayReservation && ordiniFiltrati?.length > 0) {
            return t("checkout_page.notBookedClient");
        }
        return t("checkout_page.noBookedClient");
    }, [isCounterCheckout, paidReservationLabel, selectedTableReservation, selectedTableTodayReservation, ordiniFiltrati?.length, t]);

    const formatHeliteGuestLabel = useCallback((guest) => {
        if (!guest) return "";
        const parts = [
            guest.roomnr ? t("heliteGuestPicker.roomWithNumber", { room: guest.roomnr }) : null,
            guest.holderName || guest.name || null,
            guest.number ? t("heliteGuestPicker.bookingShort", { number: guest.number }) : null,
            getGuestBoardBasis(guest) ? `${t("heliteGuestPicker.board")} ${getGuestBoardBasis(guest)}` : null,
        ].filter(Boolean);
        return parts.join(" - ");
    }, [t]);

    const getHeliteBillId = useCallback((guest) => (
        String(guest?.id || guest?.heliteBillId || guest?.billId || "").trim()
    ), []);

    const getHeliteRoomNumber = useCallback((guest) => (
        String(guest?.roomnr || guest?.heliteRoomNumber || guest?.roomNumber || "").trim()
    ), []);

    const getHeliteGuestFromReservation = useCallback((reservationItem) => {
        if (!reservationItem) return null;

        const client = reservationItem.client || {};
        const roomnr = reservationItem.heliteRoomNumber || reservationItem.roomnr || client.roomNumber || "";
        const holderName = reservationItem.heliteHolderName
            || reservationItem.holderName
            || [client.name, client.surname].filter(Boolean).join(" ").trim();

        if (!roomnr && !holderName) return null;

        return {
            id: reservationItem.heliteBillId || reservationItem.heliteId || "",
            number: reservationItem.heliteBillNumber || reservationItem.number || "",
            idrispren: reservationItem.heliteIdrispren || reservationItem.idrispren || "",
            roomnr,
            holderName,
            holderEmail: reservationItem.heliteHolderEmail || client.email || "",
            boardBasis: getGuestBoardBasis(reservationItem),
            arrival: reservationItem.arrival || reservationItem.startDate || "",
            departure: reservationItem.departure || reservationItem.endDate || "",
        };
    }, []);

    const getHeliteGuestFromOrder = useCallback((orderItem) => {
        const helite = orderItem?.helite;
        if (!helite) return null;

        const billId = helite.heliteBillId || helite.billId || "";
        const roomnr = helite.heliteRoomNumber || helite.roomNumber || "";
        const holderName = helite.heliteHolderName || helite.holderName || "";
        if (!billId && !roomnr && !holderName) return null;

        return {
            id: billId,
            billId,
            heliteBillId: billId,
            roomnr,
            roomNumber: roomnr,
            heliteRoomNumber: roomnr,
            holderName,
            heliteHolderName: holderName,
            boardBasis: helite.heliteBoardBasis || helite.boardBasis || "",
        };
    }, []);

    const checkoutOrderHeliteGuest = useMemo(() => {
        const guests = (checkoutOrdersForPayment || [])
            .map(getHeliteGuestFromOrder)
            .filter((guest) => guest && getHeliteBillId(guest));
        if (guests.length < 1) return null;

        const billIds = [...new Set(guests.map((guest) => getHeliteBillId(guest)).filter(Boolean))];
        return billIds.length === 1 ? guests[0] : null;
    }, [checkoutOrdersForPayment, getHeliteBillId, getHeliteGuestFromOrder]);

    const currentReservationHeliteGuest = useMemo(
        () => checkoutOrderHeliteGuest || getHeliteGuestFromReservation(activeCheckoutReservation || selectedTableTodayReservation),
        [activeCheckoutReservation, checkoutOrderHeliteGuest, selectedTableTodayReservation, getHeliteGuestFromReservation]
    );

    const activeBoardBasis = useMemo(
        () => getGuestBoardBasis(currentReservationHeliteGuest),
        [currentReservationHeliteGuest]
    );

    const selectedHeliteGuest = useMemo(() => (
        (heliteGuests || []).find((guest) => String(guest.id) === String(selectedHeliteGuestId)) || null
    ), [heliteGuests, selectedHeliteGuestId]);

    const buildHeliteChargePayload = useCallback(({
        orderId,
        amount,
        guest,
        sourceType = "ORDER",
        suffix = "ROOM_CHARGE",
    }) => {
        const billId = getHeliteBillId(guest);
        const normalizedAmount = Number(normalizeAmountInput(amount).toFixed(2));

        if (!billId) {
            throw new Error(t("heliteGuestPicker.missingBillId"));
        }
        if (!Number.isFinite(normalizedAmount) || normalizedAmount <= 0) {
            throw new Error(t("heliteGuestPicker.invalidAmount"));
        }

        const room = guest?.roomnr ? t("heliteGuestPicker.roomWithNumber", { room: guest.roomnr }) : "HElite";
        const heliteRoomNumber = getHeliteRoomNumber(guest);
        const tableName = selectedTable?.name ? ` - ${selectedTable.name}` : "";
        const sourceId = Number(orderId);
        const rifPv = `${sourceType}-${sourceId}${suffix ? `-${suffix}` : ""}`;

        return {
            billId,
            heliteBillId: billId,
            ...(heliteRoomNumber ? { heliteRoomNumber, roomNumber: heliteRoomNumber } : {}),
            ...(guest?.holderName ? { heliteHolderName: guest.holderName, holderName: guest.holderName } : {}),
            amount: normalizedAmount,
            pax: Number(selectedTable?.takenSeats || guest?.guests || 1),
            desc: t("heliteGuestPicker.restaurantChargeDescription", { room, table: tableName }),
            rifPv,
            date: formatDateYyyyMmDd(),
            idempotencyKey: `${sourceType}:${sourceId}:${billId}:${normalizedAmount.toFixed(2)}:${suffix}`,
        };
    }, [getHeliteBillId, getHeliteRoomNumber, selectedTable?.name, selectedTable?.takenSeats, t]);

    const enqueueHeliteBuoniFromResult = useCallback((result) => {
        const buoni = [
            ...(Array.isArray(result?.heliteBuoni) ? result.heliteBuoni : []),
            ...(Array.isArray(result?.orders)
                ? result.orders.flatMap((item) => Array.isArray(item?.heliteBuoni) ? item.heliteBuoni : [])
                : []),
        ].filter((buono) => buono?.id && !buono.isSigned);

        if (buoni.length === 0) return 0;

        setPendingHeliteBuoni((prev) => {
            const byId = new Map((prev || []).map((item) => [Number(item.id), item]));
            buoni.forEach((buono) => byId.set(Number(buono.id), buono));
            return [...byId.values()];
        });
        return buoni.length;
    }, []);

    const activeHeliteBuono = pendingHeliteBuoni[0] || null;

    const closeActiveHeliteBuono = useCallback(() => {
        const shouldOpenCheckoutSuccess = pendingHeliteBuoni.length <= 1 && checkoutSuccessPendingBuonoAction;
        setPendingHeliteBuoni((prev) => prev.slice(1));
        if (shouldOpenCheckoutSuccess) {
            setCheckoutSuccessPendingBuonoAction(false);
            setOpenCloseCheckout(true);
            setOpenConfirmCloseCheckout(false);
        }
    }, [checkoutSuccessPendingBuonoAction, pendingHeliteBuoni.length]);

    const handleSaveHeliteBuonoSignature = useCallback(async (signature) => {
        if (!activeHeliteBuono?.id) return;
        setHeliteBuonoSignatureSaving(true);
        try {
            const signedBuono = await signHeliteBuono(activeHeliteBuono.id, signature, token, tenant);
            handleAlert("Firma buono salvata", "success");
            const shouldOpenCheckoutSuccess = pendingHeliteBuoni.length <= 1 && checkoutSuccessPendingBuonoAction;
            setPendingHeliteBuoni((prev) => prev.slice(1));
            if (shouldOpenCheckoutSuccess) {
                setCheckoutSuccessPendingBuonoAction(false);
            }
            setHeliteBuonoPrintPrompt({
                buono: signedBuono || activeHeliteBuono,
                shouldOpenCheckoutSuccess,
            });
        } catch (err) {
            handleAlert(err?.message || "Errore salvataggio firma buono", "error");
        } finally {
            setHeliteBuonoSignatureSaving(false);
        }
    }, [activeHeliteBuono, checkoutSuccessPendingBuonoAction, handleAlert, pendingHeliteBuoni.length, tenant, token]);

    const closeHeliteBuonoPrintPrompt = useCallback(() => {
        const shouldOpenCheckoutSuccess = Boolean(heliteBuonoPrintPrompt?.shouldOpenCheckoutSuccess);
        setHeliteBuonoPrintPrompt(null);
        if (shouldOpenCheckoutSuccess) {
            setOpenCloseCheckout(true);
            setOpenConfirmCloseCheckout(false);
        }
    }, [heliteBuonoPrintPrompt?.shouldOpenCheckoutSuccess]);

    const handleConfirmHeliteBuonoPrint = useCallback(async () => {
        const buonoId = heliteBuonoPrintPrompt?.buono?.id;
        if (!buonoId) {
            closeHeliteBuonoPrintPrompt();
            return;
        }
        setHeliteBuonoPrintSubmitting(true);
        try {
            await printHeliteBuono(buonoId, token, tenant);
            handleAlert("Stampa buono inviata", "success");
            closeHeliteBuonoPrintPrompt();
        } catch (err) {
            handleAlert(err?.message || "Errore stampa buono", "error");
        } finally {
            setHeliteBuonoPrintSubmitting(false);
        }
    }, [closeHeliteBuonoPrintPrompt, handleAlert, heliteBuonoPrintPrompt?.buono?.id, tenant, token]);

    const enqueueGenericVouchersFromResult = useCallback((result) => {
        const vouchers = [
            ...(Array.isArray(result?.vouchers) ? result.vouchers : []),
            ...(Array.isArray(result?.orders)
                ? result.orders.flatMap((item) => Array.isArray(item?.vouchers) ? item.vouchers : [])
                : []),
        ].filter((voucher) => voucher?.id && !voucher.isSigned);

        if (vouchers.length === 0) return 0;

        setPendingGenericVouchers((prev) => {
            const byId = new Map((prev || []).map((item) => [Number(item.id), item]));
            vouchers.forEach((voucher) => byId.set(Number(voucher.id), voucher));
            return [...byId.values()];
        });
        return vouchers.length;
    }, []);

    const activeGenericVoucher = pendingGenericVouchers[0] || null;

    const closeActiveGenericVoucher = useCallback(() => {
        const shouldOpenCheckoutSuccess = pendingGenericVouchers.length <= 1 && checkoutSuccessPendingVoucherAction;
        setPendingGenericVouchers((prev) => prev.slice(1));
        if (shouldOpenCheckoutSuccess) {
            setCheckoutSuccessPendingVoucherAction(false);
            setOpenCloseCheckout(true);
            setOpenConfirmCloseCheckout(false);
        }
    }, [checkoutSuccessPendingVoucherAction, pendingGenericVouchers.length]);

    const handleSaveGenericVoucherSignature = useCallback(async (signature) => {
        if (!activeGenericVoucher?.id) return;
        setGenericVoucherSignatureSaving(true);
        try {
            const signedVoucher = await signVoucher(activeGenericVoucher.id, signature, token, tenant);
            handleAlert("Firma buono salvata", "success");
            const shouldOpenCheckoutSuccess = pendingGenericVouchers.length <= 1 && checkoutSuccessPendingVoucherAction;
            setPendingGenericVouchers((prev) => prev.slice(1));
            if (shouldOpenCheckoutSuccess) {
                setCheckoutSuccessPendingVoucherAction(false);
            }
            setGenericVoucherPrintPrompt({
                voucher: signedVoucher || activeGenericVoucher,
                shouldOpenCheckoutSuccess,
            });
        } catch (err) {
            handleAlert(err?.message || "Errore salvataggio firma buono", "error");
        } finally {
            setGenericVoucherSignatureSaving(false);
        }
    }, [activeGenericVoucher, checkoutSuccessPendingVoucherAction, handleAlert, pendingGenericVouchers.length, tenant, token]);

    const closeGenericVoucherPrintPrompt = useCallback(() => {
        const shouldOpenCheckoutSuccess = Boolean(genericVoucherPrintPrompt?.shouldOpenCheckoutSuccess);
        setGenericVoucherPrintPrompt(null);
        if (shouldOpenCheckoutSuccess) {
            setOpenCloseCheckout(true);
            setOpenConfirmCloseCheckout(false);
        }
    }, [genericVoucherPrintPrompt?.shouldOpenCheckoutSuccess]);

    const handleConfirmGenericVoucherPrint = useCallback(async () => {
        const voucherId = genericVoucherPrintPrompt?.voucher?.id;
        if (!voucherId) {
            closeGenericVoucherPrintPrompt();
            return;
        }
        setGenericVoucherPrintSubmitting(true);
        try {
            await printVoucher(voucherId, token, tenant);
            handleAlert("Stampa buono inviata", "success");
            closeGenericVoucherPrintPrompt();
        } catch (err) {
            handleAlert(err?.message || "Errore stampa buono", "error");
        } finally {
            setGenericVoucherPrintSubmitting(false);
        }
    }, [closeGenericVoucherPrintPrompt, genericVoucherPrintPrompt?.voucher?.id, handleAlert, tenant, token]);

    const filteredHeliteGuests = useMemo(() => {
        const query = heliteGuestSearch.trim().toLowerCase();
        const guests = !query
            ? heliteGuests || []
            : (heliteGuests || []).filter((guest) => (
                [
                    guest.roomnr,
                    guest.holderName,
                    guest.boardBasis,
                    guest.number,
                    guest.idrispren,
                    formatDateDdMmYyyy(guest.arrival),
                    formatDateDdMmYyyy(guest.departure),
                ].filter(Boolean).join(" ").toLowerCase().includes(query)
            ));

        return [...guests].sort((a, b) => {
            const aValue = getHeliteGuestSortValue(a, heliteGuestSort.key);
            const bValue = getHeliteGuestSortValue(b, heliteGuestSort.key);
            const result = typeof aValue === "number" && typeof bValue === "number"
                ? aValue - bValue
                : String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: "base" });
            return heliteGuestSort.direction === "asc" ? result : -result;
        });
    }, [heliteGuests, heliteGuestSearch, heliteGuestSort]);

    const handleHeliteGuestSort = useCallback((key) => {
        setHeliteGuestSort((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    }, []);

    const fetchAllData = useCallback(async () => {
        const results = await Promise.allSettled([
            getAllCategorie(token, tenant),
            getAllPrenotazioni(token, tenant),
            getAllOrdini(token, tenant, { noCache: true }),
            getAllTavoli(token, tenant, { noCache: true }),
            getAllProdotti(token, tenant),
            isSaleCongressiTenant ? getReservationEvents(token, tenant) : Promise.resolve([]),
            normalizedTenantType === "RISTORANTE"
                ? getMyCategoryVisibility(token, tenant)
                : Promise.resolve({ configured: false, visibleCategoryIds: [] }),
        ]);

        const firstError = results.find((r) => r.status === "rejected");
        const categoriesResult = results[0];
        const reservationsResult = results[1];
        const ordersResult = results[2];
        const tablesResult = results[3];
        const productsResult = results[4];
        const reservationEventsResult = results[5];
        const categoryVisibilityResult = results[6];

        const visibleCatalog = applyUserCategoryVisibility(
            categoriesResult.status === "fulfilled" ? categoriesResult.value : [],
            productsResult.status === "fulfilled" ? productsResult.value : [],
            categoryVisibilityResult.status === "fulfilled"
                ? categoryVisibilityResult.value
                : { configured: false, visibleCategoryIds: [] },
        );

        setCategory(visibleCatalog.categories);
        setReservation(reservationsResult.status === "fulfilled" ? reservationsResult.value : []);
        setOrder(ordersResult.status === "fulfilled" ? ordersResult.value : []);
        setTable(
            tablesResult.status === "fulfilled"
                ? _.sortBy(tablesResult.value, ["roomId", "id"], ["asc", "asc"])
                : []
        );
        setProducts(
            _.sortBy(visibleCatalog.products, ["categoryId", "subcategoryId"], ["asc", "asc"])
        );
        setReservationEvents(reservationEventsResult.status === "fulfilled" ? reservationEventsResult.value : []);

        if (firstError && !String(firstError.reason?.message || "").includes("403")) {
            showSnackbar(firstError.reason?.message || "Errore caricamento dati", "error");
        }
    }, [token, tenant, isSaleCongressiTenant, normalizedTenantType, _, showSnackbar]);

    useEffect(() => {
        fetchAllData();

        // Aggiorna i dati in risposta a eventi WS
        const cleanup = SetupWSEventListener(fetchAllData);
        return cleanup;
    }, [fetchAllData]);

    useEffect(() => {
        if (refreshTrigger > 0) {
            fetchAllData();
        }
    }, [refreshTrigger, fetchAllData]);

    useEffect(() => {
        if (isCounterCheckout || isMultipleCheckout || !activeBoardBasis || !selectedTable?.id || !Array.isArray(order)) return;

        const rowsToPatch = order.flatMap((ord) => {
            if (
                !orderBelongsToTable(ord, selectedTable.id)
                || ["PAID", "CANCELLED"].includes(ord?.status)
                || !Array.isArray(ord?.orderProducts)
            ) {
                return [];
            }

            return ord.orderProducts
                .filter((row) => isIncludedByBoardBasis(row?.product, activeBoardBasis))
                .filter((row) => Number(row?.unitPrice ?? row?.product?.price ?? 0) !== 0)
                .map((row) => ({ orderId: Number(ord.id), row }));
        });

        const pendingRows = rowsToPatch.filter(({ orderId, row }) => {
            const lineKey = `${orderId}:${Number(row?.id)}`;
            return !boardBasisRepriceLineKeysRef.current.has(lineKey);
        });
        if (pendingRows.length === 0) return;

        let cancelled = false;
        const repriceRows = async () => {
            try {
                for (const { orderId, row } of pendingRows) {
                    const lineKey = `${orderId}:${Number(row?.id)}`;
                    await patchProdottoComanda(
                        orderId,
                        {
                            orderProductId: Number(row.id),
                            quantity: Number(row.quantity || 0),
                            unitPrice: 0,
                            discount: Number(row.discount || 0),
                            note: row.note ?? null,
                        },
                        token,
                        tenant
                    );
                    boardBasisRepriceLineKeysRef.current.add(lineKey);
                }

                if (!cancelled) await fetchAllData();
            } catch (err) {
                console.error("Errore aggiornamento prezzi board basis:", err);
                if (!cancelled) handleAlert("Errore aggiornamento prezzi inclusi", "error");
            }
        };

        repriceRows();

        return () => {
            cancelled = true;
        };
    }, [activeBoardBasis, fetchAllData, handleAlert, isCounterCheckout, isMultipleCheckout, order, selectedTable?.id, tenant, token]);

    useEffect(() => {
        setOrdiniFiltrati(() => {
            if (isMultipleCheckout) {
                const combinedOrderIds = new Set((combinedCheckoutOrders || []).map((ord) => Number(ord.id)));
                const latestOrders = (order || []).filter((ord) => combinedOrderIds.has(Number(ord.id)) && !["PAID", "CANCELLED"].includes(ord.status));
                const sourceOrders = latestOrders.length > 0 ? latestOrders : combinedCheckoutOrders;
                return sourceOrders.flatMap((ord) => getOrderRowsForCheckout(ord));
            }

            const tableOrders = (order || []).filter(
                (ordini) => (
                    isCounterCheckout
                        ? ordini.tableId == null && Number(ordini.id) === Number(activeCounterOrderId)
                        : orderBelongsToTable(ordini, selectedTable?.id)
                ) && !["PAID", "CANCELLED"].includes(ordini.status)
            );

            if (tableOrders.length === 0) return [];

            // Use only the latest active session day for the selected table,
            // avoiding accumulation from old unpaid orders.
            const latestCreatedAt = tableOrders
                .map((o) => new Date(o.createdAt))
                .sort((a, b) => b.getTime() - a.getTime())[0];

            return tableOrders
                .filter((ord) => isSameLocalDay(ord.createdAt, latestCreatedAt))
                .flatMap((ord) => getOrderRowsForCheckout(ord));
        })
    }, [selectedTable, order, fetchAllData, isCounterCheckout, isMultipleCheckout, combinedCheckoutOrders, getOrderRowsForCheckout, activeCounterOrderId]);

    useEffect(() => {
        setOrdiniUniti(() => {
            const grouped = (ordiniFiltrati || []).reduce((acc, ord) => {
                const key = getSplitItemKey(ord);
                if (!acc[key]) {
                    acc[key] = {
                        ...ord,
                        quantity: ord.quantity,
                        originalOrders: [{ ...ord }]
                    };
                } else {
                    acc[key].quantity += ord.quantity;
                    acc[key].originalOrders.push({ ...ord });
                }
                return acc;
            }, {});
            return Object.values(grouped).filter(Boolean);
        })
    }, [ordiniFiltrati, getSplitItemKey]);

    useEffect(() => {
        setOrdineSchermo(() => {
            if (!ordiniFiltrati || ordiniFiltrati.length < 1) return [];

            if (ordiniDivisi && splitTargetRowIds) {
                const targetRows = ordiniFiltrati
                    .filter((prod) => splitTargetRowIds.has(getSplitTargetKey(prod)))
                    .flatMap((prod) => {
                        const rowType = prod.isFixedPriceMenu ? "fixed-menu" : prod.isPackage ? "pkg" : "prd";
                        const rowId = prod.id ?? (prod.isPackage ? prod.packageId : prod.productId);
                        const baseId = `${rowType}-${rowId}`;
                        const arrayTemp = [];

                        for (let i = 0; i < prod.quantity; i++) {
                            const temp = cloneCheckoutRow(prod);
                            temp.idSplit = `${baseId}_${i}`;
                            temp.quantity = 1;
                            arrayTemp.push(temp);
                        }

                        return arrayTemp;
                    });

                const untouchedRows = (ordiniUniti || [])
                    .map((row) => {
                        const originals = Array.isArray(row.originalOrders) && row.originalOrders.length > 0
                            ? row.originalOrders
                            : [row];
                        const untouchedOriginals = originals.filter((orig) => !splitTargetRowIds.has(getSplitTargetKey(orig)));
                        if (untouchedOriginals.length < 1) return null;

                        const first = untouchedOriginals[0];
                        const quantity = untouchedOriginals.reduce((sum, orig) => sum + (Number(orig.quantity) || 0), 0);
                        const rowType = first.isFixedPriceMenu ? "fixed-menu" : first.isPackage ? "pkg" : "prd";
                        const rowId = first.id ?? (first.isPackage ? first.packageId : first.productId);

                        return {
                            ...cloneCheckoutRow(first),
                            quantity,
                            originalOrders: untouchedOriginals.map((orig) => ({ ...orig })),
                            idSplit: `${rowType}-${rowId}_bulk`,
                        };
                    })
                    .filter(Boolean);

                return [...untouchedRows, ...targetRows];
            }

            return ordiniFiltrati
                .map((prod) => {
                    const shouldSplit = !splitTargetRowIds || splitTargetRowIds.has(getSplitTargetKey(prod));
                    const rowType = prod.isFixedPriceMenu ? "fixed-menu" : prod.isPackage ? "pkg" : "prd";
                    const rowId = prod.id ?? (prod.isPackage ? prod.packageId : prod.productId);
                    const baseId = `${rowType}-${rowId}`;

                    if (!ordiniDivisi || !shouldSplit) {
                        return [{ ...cloneCheckoutRow(prod), idSplit: `${baseId}_bulk`, quantity: prod.quantity }];
                    }

                    const arrayTemp = [];
                    for (let i = 0; i < prod.quantity; i++) {
                        const temp = cloneCheckoutRow(prod);
                        temp.idSplit = `${baseId}_${i}`;
                        temp.quantity = 1;
                        arrayTemp.push(temp);
                    }
                    return arrayTemp;
                })
                .flatMap((ord) => ord);
        });
    }, [ordiniDivisi, ordiniFiltrati, ordiniUniti, splitTargetRowIds, getSplitTargetKey]);

    useEffect(() => {
        const checkoutRecipient = activeCheckoutReservation || activeCheckoutReservationEvent;
        setSelectedTableReservation(getReservationRecipientLabel(checkoutRecipient));
        // Estrai clientId e companyId dalla prenotazione
        if (checkoutRecipient?.clientId) {
            setSelectedClientFromReservation(checkoutRecipient.clientId);
            // Se il cliente ha una company collegata, usa quella per la fatturazione
            if (checkoutRecipient.client?.companyId) {
                setSelectedCompanyFromReservation(checkoutRecipient.client.companyId);
            } else {
                setSelectedCompanyFromReservation(checkoutRecipient.companyId || null);
            }
        } else {
            setSelectedClientFromReservation(null);
            setSelectedCompanyFromReservation(checkoutRecipient?.companyId || null);
        }
    }, [activeCheckoutReservation, activeCheckoutReservationEvent, getReservationRecipientLabel]);

    useEffect(() => {
        setOrdiniDivisi(false);
        setSplitTargetRowIds(null);
        setCheckboxSelectedIds(new Set());
        setPaidReservationLabel(null);
        if (selectedTable?.id !== MULTIPLE_CHECKOUT_ID) {
            setCombinedCheckoutOrders([]);
        }
    }, [selectedTable?.id]);

    const startCounterSale = useCallback(() => {
        activeCounterOrderIdRef.current = null;
        setActiveCounterOrderId(null);
        setSelectedTable(counterCheckoutTable);
        setOpenPendingList(false);
        setOpenOccupiedList(false);
        setOpenLiberaTavolo(false);
        setOrdiniDivisi(false);
        setSplitTargetRowIds(null);
        setCheckboxSelectedIds(new Set());
        navigate("/punto_cassa", { replace: true, state: {} });
    }, [counterCheckoutTable, navigate]);

    useEffect(() => {
        const queryTableId = Number(new URLSearchParams(location.search).get("tableId"));
        const incomingTableId = Number(location.state?.checkoutTableId) || queryTableId;
        if (!incomingTableId || !Array.isArray(table) || hasAppliedNavigationTable.current) return;

        const sourceTables = isSaleCongressiTenant ? checkoutSelectableTables : table;
        const targetTable = sourceTables.find((tab) => Number(tab.id) === incomingTableId);
        if (!targetTable) return;

        setSelectedTable(targetTable);
        hasAppliedNavigationTable.current = true;
    }, [checkoutSelectableTables, isSaleCongressiTenant, location.state, location.search, table]);

    useEffect(() => {
        const restoredIds = (Array.isArray(location.state?.restoredOrderIds) ? location.state.restoredOrderIds : [])
            .map(Number)
            .filter((id) => Number.isInteger(id) && id > 0);
        if (hasAppliedRestoredCheckout.current || restoredIds.length === 0 || !Array.isArray(order)) return;
        const restoredOrders = restoredIds.map((id) => order.find((item) => Number(item.id) === id)).filter(Boolean);
        if (restoredOrders.length !== restoredIds.length) return;

        const primaryTableIds = [...new Set(restoredOrders.map((item) => Number(item.tableId)).filter((id) => id > 0))];
        if (primaryTableIds.length === 1 && !Array.isArray(table)) return;
        if (primaryTableIds.length > 1) {
            setCombinedCheckoutOrders(restoredOrders);
            setSelectedTable({
                id: MULTIPLE_CHECKOUT_ID,
                name: MULTIPLE_CHECKOUT_LABEL,
                status: "IN_ATTESA",
                isMultipleCheckout: true,
            });
            setPaidReservationLabel(t("checkout_page.multipleCheckoutOrderCount", { count: restoredOrders.length }));
        } else if (primaryTableIds.length === 0) {
            activeCounterOrderIdRef.current = Number(restoredOrders[0].id);
            setActiveCounterOrderId(restoredOrders[0].id);
            setSelectedTable(counterCheckoutTable);
        } else if (primaryTableIds.length === 1 && Array.isArray(table)) {
            const targetTable = table.find((tab) => Number(tab.id) === Number(primaryTableIds[0]));
            if (targetTable) setSelectedTable(targetTable);
        }
        hasAppliedRestoredCheckout.current = true;
    }, [counterCheckoutTable, location.state, order, table, t]);

    useEffect(() => {
        if (!routeRoomChargeShortcut) {
            hasAppliedRoomChargeShortcut.current = false;
            return;
        }
        if (
            hasAppliedRoomChargeShortcut.current ||
            !selectedTable?.id ||
            isCounterCheckout ||
            isMultipleCheckout ||
            checkoutPrepaymentsLoading ||
            checkoutAmountDue <= 0.009 ||
            checkoutPayableRows.length < 1
        ) {
            return;
        }

        closeCheckoutPaymentPresetRef.current = "helite_room_charge";
        setPaymentMethode("helite_room_charge");
        setOpenConfirmCloseCheckout(true);
        hasAppliedRoomChargeShortcut.current = true;
    }, [
        checkoutAmountDue,
        checkoutPayableRows.length,
        checkoutPrepaymentsLoading,
        isCounterCheckout,
        isMultipleCheckout,
        routeRoomChargeShortcut,
        selectedTable?.id,
    ]);

    // Listener per evento print_failed
    useEffect(() => {
        const handlePrintError = (message) => {
            handleAlert(('Errore nella stampa della comanda:' + message), "error")
        };
        const cleanup = PrintEventListener(handlePrintError);
        return cleanup;
    }, [handleAlert]);

    useEffect(() => {
        const sourceTables = Array.isArray(table) ? table : [];
        if (isSpaTenant) {
            const cabinTables = sourceTables.filter(isSpaCheckoutCabin);
            setOccupiedTableList(cabinTables.filter((el) => checkoutTableIdsWithOrders.has(Number(el.id))));
            setPendingTableList(cabinTables.filter((el) =>
                el.status === "IN_ATTESA" || checkoutTableIdsWithOrders.has(Number(el.id))
            ));
            return;
        }
        setOccupiedTableList(sourceTables.filter((el) =>
            el.status === "OCCUPATO" || (el.status === "RISERVATO" && checkoutTableIdsWithOrders.has(Number(el.id)))
        ))
        setPendingTableList(sourceTables.filter((el) => el.status === "IN_ATTESA"))
    }, [table, checkoutTableIdsWithOrders, isSpaTenant]);

    const removeTableFromCheckoutLists = useCallback((tableId) => {
        const normalizedTableId = Number(tableId);
        if (!Number.isInteger(normalizedTableId) || normalizedTableId <= 0) return;

        setOccupiedTableList((prev) => (prev || []).filter((tab) => Number(tab?.id) !== normalizedTableId));
        setPendingTableList((prev) => (prev || []).filter((tab) => Number(tab?.id) !== normalizedTableId));
    }, []);


    const handleCheck = useCallback((elem, row = null) => {
        if (elem !== "all") {
            if (!ordiniDivisi && row && Number(row?.quantity || 0) > 1 && !isWeightOrderRow(row)) {
                const sourceRows = Array.isArray(row?.originalOrders) && row.originalOrders.length > 0
                    ? row.originalOrders
                    : [row];
                const lineIds = new Set(sourceRows.map((sourceRow) => getSplitTargetKey(sourceRow)));
                const firstRow = sourceRows[0];
                const rowType = firstRow?.isFixedPriceMenu ? "fixed-menu" : firstRow?.isPackage ? "pkg" : "prd";
                const rowId = firstRow?.id ?? (firstRow?.isPackage ? firstRow?.packageId : firstRow?.productId);

                setSplitTargetRowIds(lineIds);
                setOrdiniDivisi(true);
                setCheckboxSelectedIds(new Set([`${rowType}-${rowId}_0`]));
                return;
            }
            setCheckboxSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(elem)) next.delete(elem);
                else next.add(elem);
                return next;
            });
        } else {
            const allIds = new Set((ordiniDivisi ? ordineSchermo : ordiniUniti)
                .map((o) => getRowSelectionKey(o)).sort());

            const allChecked = Array.from(allIds).every(id => checkboxSelectedIds.has(id));
            setCheckboxSelectedIds(allChecked ? new Set() : allIds);
        }
    }, [ordiniDivisi, ordineSchermo, ordiniUniti, checkboxSelectedIds, getRowSelectionKey, getSplitTargetKey, isWeightOrderRow]);

    const handleSearch = (e) => {
        setSearchProduct(e.target.value);
    }

    async function addProductToCheckout(prodotto) {
        try {
            if (isMultipleCheckout) {
                handleAlert(t("checkout_page.multipleCheckoutActiveWarning"), "warning");
                return;
            }

            const tableId = isCounterCheckout ? null : selectedTable?.id;
            const activeReservation = activeCheckoutReservation;

            if (!isCounterCheckout && !tableId) {
                handleAlert(getTenantTranslation(t, tenantType, "No_table_selected"), "warning")
                return;
            }

            const quantity = importoDisplay.length > 0 ? Number(importoDisplay) : 1;
            if (!Number.isFinite(quantity) || quantity <= 0) {
                handleAlert(t("checkout_page.editQty_note_1"), "warning");
                return;
            }

            const isBoardBasisIncludedProduct = Boolean(
                !isCounterCheckout && activeBoardBasis && isIncludedByBoardBasis(prodotto, activeBoardBasis)
            );

            const currentCounterOrderId = Number(activeCounterOrderIdRef.current || activeCounterOrderId);
            let latestCounterOrder = null;
            if (isCounterCheckout && currentCounterOrderId) {
                latestCounterOrder = await getOrdineById(currentCounterOrderId, token, tenant);
                if (latestCounterOrder && ["PAID", "CANCELLED"].includes(latestCounterOrder.status)) {
                    latestCounterOrder = null;
                }
            }

            if (latestCounterOrder?.id) {
                const existingProducts = Array.isArray(latestCounterOrder.orderProducts)
                    ? latestCounterOrder.orderProducts
                    : [];
                const existingPackages = Array.isArray(latestCounterOrder.orderPackages)
                    ? latestCounterOrder.orderPackages
                    : [];

                const productsPayload = existingProducts.map((row) => ({
                    id: row.id,
                    productId: Number(row.productId ?? row.product?.id),
                    variantIds: getVariantIds(row),
                    quantity: Number(row.quantity || 0),
                    discount: row.discount ?? null,
                    note: row.note ?? null,
                })).filter((row) => Number.isInteger(row.productId) && row.productId > 0 && row.quantity > 0);

                const nextProductId = Number(prodotto.id);
                const targetRow = productsPayload.find((row) =>
                    row.productId === nextProductId && getVariantIds(row).length === 0
                );

                if (targetRow) {
                    targetRow.quantity = Number(targetRow.quantity || 0) + quantity;
                } else {
                    productsPayload.push({
                        productId: nextProductId,
                        variantIds: [],
                        quantity,
                    });
                }

                const packagesPayload = existingPackages.map((row) => ({
                    id: row.id,
                    packageId: Number(row.packageId ?? row.package?.id),
                    quantity: Number(row.quantity || 0),
                    discount: row.discount ?? null,
                    note: row.note ?? null,
                })).filter((row) => Number.isInteger(row.packageId) && row.packageId > 0 && row.quantity > 0);

                await aggiornaComanda(latestCounterOrder.id, {
                    tableId: null,
                    clientId: null,
                    totalDiscount: Number(latestCounterOrder.totalDiscount || 0),
                    discountAmount: Math.max(0, Number(latestCounterOrder.discountAmount || 0)),
                    surchargeAmount: Math.max(0, Number(latestCounterOrder.surchargeAmount || 0)),
                    status: latestCounterOrder.status || "CLOSED",
                    products: productsPayload,
                    packages: packagesPayload,
                }, token, tenant);

                await fetchAllData();
                setImportoDisplay("");
                setOrdiniDivisi(false);
                setSplitTargetRowIds(null);
                setCheckboxSelectedIds(new Set());
                setSelectedTable(counterCheckoutTable);
                setSearchProduct("");
                return;
            }

            const ordineCart = {
                tableId,
                ...checkoutReservationEventOrderLink,
                clientId: isCounterCheckout ? null : (activeReservation?.clientId ?? activeReservation?.client?.id ?? null),
                totalDiscount: 0,
                status: "CLOSED",
                products: [
                    {
                        productId: prodotto.id,
                        quantity,
                        ...(isBoardBasisIncludedProduct ? { unitPrice: 0 } : {}),
                    }
                ],
            }

            if (!isCounterCheckout && tableId) {
                await claimTableSession(tableId, token, tenant);
            }

            if (!isCounterCheckout && selectedTable.status === "LIBERO") {
                await aggiornaTavolo(tableId, { status: "IN_ATTESA" }, token, tenant)
            }

            const createdOrder = await creaComanda(ordineCart, token, tenant);
            if (isCounterCheckout && createdOrder?.id) {
                activeCounterOrderIdRef.current = Number(createdOrder.id);
                setActiveCounterOrderId(Number(createdOrder.id));
            }
            await fetchAllData();
            setImportoDisplay("")
            setOrdiniDivisi(false)
            setSplitTargetRowIds(null)
            setCheckboxSelectedIds(new Set())
            setSelectedTable(isCounterCheckout ? counterCheckoutTable : selectedTable)
            setSearchProduct("")
        } catch (err) {
            handleAlert(err?.message || String(err), err?.status === 409 ? "warning" : "error");
        }

    }

    function handleAddProduct(prodotto) {
        const queuedOperation = addProductQueueRef.current.then(() => addProductToCheckout(prodotto));
        // Keep the queue alive after a failed request so later taps are still processed.
        addProductQueueRef.current = queuedOperation.catch(() => undefined);
        return queuedOperation;
    }

    function handleDisplay(val) {
        const insertKeys = ["00", "0", ".", "1", "2", "3", "4", "5", "6", "7", "8", "9"]

        if (val === "C") {
            ResetImportDisplay()
        }

        if (val === "-") {
            if (editPrezzo) {
                setIncrementoPrezzo(false)
                setDecrementoPrezzo(true)
                setEditQty(false)
                handleEditPrice(-1)
                return;
            }
            if (decrementoPrezzo === false) {
                setIncrementoPrezzo(false)
                setDecrementoPrezzo(true)
                setEditQty(false)
            } else {
                handleEditPrice()
                setDecrementoPrezzo(false)
            }
        }
        if (val === "+") {
            if (editPrezzo) {
                setDecrementoPrezzo(false)
                setIncrementoPrezzo(true)
                setEditQty(false)
                handleEditPrice(1)
                return;
            }
            if (incrementoPrezzo === false) {
                setDecrementoPrezzo(false)
                setIncrementoPrezzo(true)
                setEditQty(false)
            } else {
                handleEditPrice()
                setIncrementoPrezzo(false)
            }
        }
        if (val === "X") {
            setDecrementoPrezzo(false)
            setIncrementoPrezzo(false)
            setEditQty(false)
            setCambiaTipoVariazione(!cambiaTipoVariazione)
        }

        if (!((val === "0" || val === "00") && importoDisplay.length < 1)) {
            let calcolo = importoDisplay

            if (insertKeys.includes(val)) {
                if (calcolo.includes(".") && val === ".")
                    return null;
                calcolo = calcolo + val

                if (calcolo.includes(".")) {
                    const num = calcolo.split(".")
                    let mantissa = num[1]
                    if (mantissa.length > 2) {
                        while (mantissa.length > 2) {
                            mantissa = mantissa.substring(0, mantissa.length - 1)
                        }
                        calcolo = num[0] + "." + mantissa
                    }
                }

                if (calcolo.charAt(0) === ".") {
                    setImportoDisplay("0" + calcolo)
                } else {
                    setImportoDisplay(calcolo)
                }
            }

        }
    }

    function ResetImportDisplay() {
        setImportoDisplay("")
        setIncrementoPrezzo(false)
        setDecrementoPrezzo(false)
        setEditQty(false)
    }

    function mostraDisplay() {
        let display = "0.00";
        if (importoDisplay) {
            display = importoDisplay;

            if ((incrementoPrezzo || decrementoPrezzo)) {
                let operazione = " (";

                if (incrementoPrezzo) operazione += "+";
                if (decrementoPrezzo) operazione += "-";
                operazione += (cambiaTipoVariazione ? "€" : "%") + ")";
                display += operazione;
            }
        }
        return display;
    }

    async function handleDelete() {
        if (checkboxSelectedIds.size < 1) {
            handleAlert(t("checkout_page.delete_note_1"), "error");
            return;
        }

        try {
            const sourceRows = ordiniDivisi ? (ordineSchermo || []) : (ordiniUniti || []);
            const selectedRows = sourceRows.filter((row) =>
                checkboxSelectedIds.has(getRowSelectionKey(row))
            );

            if (selectedRows.length < 1) {
                handleAlert(t("checkout_page.delete_note_1"), "error");
                return;
            }
            const removalsByOrder = new Map();
            const deletedOrderIds = new Set();
            const selectedTableIdForDelete = Number(selectedTable?.id);

            const addRemoval = (row, qtyToRemove) => {
                if (!row?.orderId || !row?.id || !qtyToRemove) return;

                const type = row.isFixedPriceMenu ? "fixed-menu" : row.isPackage ? "package" : "product";
                const lineId = row.id;
                const orderId = Number(row.orderId);
                const lineKey = `${type}-${lineId}`;

                if (!Number.isInteger(orderId) || orderId <= 0) return;

                if (!removalsByOrder.has(orderId)) {
                    removalsByOrder.set(orderId, new Map());
                }

                const orderRemovals = removalsByOrder.get(orderId);
                const existing = orderRemovals.get(lineKey) || {
                    type,
                    lineId,
                    qty: 0,
                };

                existing.qty += qtyToRemove;
                orderRemovals.set(lineKey, existing);
            };

            selectedRows.forEach((row) => {
                if (ordiniDivisi) {
                    addRemoval(row, Number(row.quantity) || 1);
                    return;
                }

                const originals =
                    Array.isArray(row.originalOrders) && row.originalOrders.length > 0
                        ? row.originalOrders
                        : [row];

                originals.forEach((orig) => {
                    addRemoval(orig, Number(orig.quantity) || 0);
                });
            });

            const hasRemainingRowsOnSelectedTable = () => {
                if (!Number.isInteger(selectedTableIdForDelete) || selectedTableIdForDelete <= 0) return true;

                return (order || []).some((ord) => {
                    if (!orderBelongsToTable(ord, selectedTableIdForDelete)) return false;
                    if (["PAID", "CANCELLED"].includes(ord?.status)) return false;

                    const removals = removalsByOrder.get(ord.id) || new Map();
                    const remainingProducts = (Array.isArray(ord.orderProducts) ? ord.orderProducts : []).reduce((sum, row) => {
                        const removal = removals.get(`product-${row.id}`);
                        return sum + Math.max(0, (Number(row.quantity) || 0) - (Number(removal?.qty) || 0));
                    }, 0);
                    const remainingPackages = (Array.isArray(ord.orderPackages) ? ord.orderPackages : []).reduce((sum, row) => {
                        const removal = removals.get(`package-${row.id}`);
                        return sum + Math.max(0, (Number(row.quantity) || 0) - (Number(removal?.qty) || 0));
                    }, 0);
                    const fixedMenus = (Array.isArray(ord.orderFixedPriceMenus) ? ord.orderFixedPriceMenus : []).reduce(
                        (sum, row) => {
                            const removal = removals.get(`fixed-menu-${row.id}`);
                            return sum + Math.max(0, (Number(row.quantity) || 0) - (Number(removal?.qty) || 0));
                        },
                        0
                    );

                    return remainingProducts + remainingPackages + fixedMenus > 0;
                });
            };

            const shouldOfferClearTable = !hasRemainingRowsOnSelectedTable();

            for (const [orderId, lineMap] of removalsByOrder.entries()) {
                const selectedOrder = [
                    ...(Array.isArray(order) ? order : []),
                    ...(Array.isArray(combinedCheckoutOrders) ? combinedCheckoutOrders : []),
                ].find((o) => Number(o?.id) === Number(orderId));
                if (!selectedOrder) continue;

                const orderProducts = Array.isArray(selectedOrder.orderProducts)
                    ? selectedOrder.orderProducts
                    : [];

                const orderPackages = Array.isArray(selectedOrder.orderPackages)
                    ? selectedOrder.orderPackages
                    : [];

                const orderFixedPriceMenus = Array.isArray(selectedOrder.orderFixedPriceMenus)
                    ? selectedOrder.orderFixedPriceMenus
                    : [];

                let remainingTotal = 0;

                for (const p of orderProducts) {
                    const removal = lineMap.get(`product-${p.id}`);
                    const nextQty = Math.max(0, (Number(p.quantity) || 0) - (removal?.qty || 0));
                    remainingTotal += nextQty;
                }

                for (const p of orderPackages) {
                    const removal = lineMap.get(`package-${p.id}`);
                    const nextQty = Math.max(0, (Number(p.quantity) || 0) - (removal?.qty || 0));
                    remainingTotal += nextQty;
                }

                for (const menu of orderFixedPriceMenus) {
                    const removal = lineMap.get(`fixed-menu-${menu.id}`);
                    const nextQty = Math.max(0, (Number(menu.quantity) || 0) - (removal?.qty || 0));
                    remainingTotal += nextQty;
                }

                if (remainingTotal === 0) {
                    await eliminaComanda(orderId, token, tenant);
                    deletedOrderIds.add(Number(orderId));
                    continue;
                }

                for (const removal of lineMap.values()) {
                    if (removal.type === "product") {
                        const targetProduct = orderProducts.find((p) => p.id === removal.lineId);
                        if (!targetProduct) continue;

                        const nextQty = Math.max(
                            0,
                            (Number(targetProduct.quantity) || 0) - removal.qty
                        );

                        const payload = {
                            orderProductId: Number(targetProduct.id),
                            quantity: Number(nextQty),
                        };

                        if (targetProduct.unitPrice !== undefined && targetProduct.unitPrice !== null) {
                            payload.unitPrice = Number(targetProduct.unitPrice);
                        }

                        if (targetProduct.discount !== undefined && targetProduct.discount !== null) {
                            payload.discount = Number(targetProduct.discount);
                        }

                        if (typeof targetProduct.note === "string") {
                            payload.note = targetProduct.note;
                        }

                        if (!Number.isInteger(payload.orderProductId) || payload.orderProductId <= 0) {
                            console.warn("PATCH prodotto skipped: invalid orderProductId", payload, targetProduct);
                            continue;
                        }

                        await patchProdottoComanda(orderId, payload, token, tenant);
                    } else if (removal.type === "package") {
                        const targetPackage = orderPackages.find((p) => p.id === removal.lineId);
                        if (!targetPackage) continue;

                        const nextQty = Math.max(
                            0,
                            (Number(targetPackage.quantity) || 0) - removal.qty
                        );

                        const payload = {
                            orderPackageId: Number(targetPackage.id),
                            packageId: Number(targetPackage.packageId ?? targetPackage.package?.id),
                            quantity: Number(nextQty),
                        };

                        if (targetPackage.unitPrice !== undefined && targetPackage.unitPrice !== null) {
                            payload.unitPrice = Number(targetPackage.unitPrice);
                        }

                        if (targetPackage.discount !== undefined && targetPackage.discount !== null) {
                            payload.discount = Number(targetPackage.discount);
                        }

                        if (typeof targetPackage.note === "string") {
                            payload.note = targetPackage.note;
                        }

                        if (!Number.isInteger(payload.packageId) || payload.packageId <= 0) {
                            console.warn("PATCH pacchetto skipped: invalid packageId", payload, targetPackage);
                            continue;
                        }

                        await patchPacchettoComanda(orderId, payload, token, tenant);
                    } else {
                        const targetMenu = orderFixedPriceMenus.find((menu) => menu.id === removal.lineId);
                        if (!targetMenu) continue;

                        const nextQty = Math.max(
                            0,
                            (Number(targetMenu.quantity) || 0) - removal.qty
                        );

                        await patchMenuPrezzoFissoComanda(
                            orderId,
                            {
                                orderFixedPriceMenuId: Number(targetMenu.id),
                                quantity: Number(nextQty),
                            },
                            token,
                            tenant
                        );
                    }
                }
            }

            if (isMultipleCheckout) {
                const retainedOrderIds = [...new Set((combinedCheckoutOrders || [])
                    .map((item) => Number(item?.id))
                    .filter((id) => Number.isInteger(id) && id > 0 && !deletedOrderIds.has(id)))];
                const refreshedOrders = (await Promise.all(retainedOrderIds.map((orderId) => (
                    getOrdineById(orderId, token, tenant).catch(() => null)
                )))).filter((item) => (
                    item
                    && !["PAID", "CANCELLED"].includes(item.status)
                    && getOrderRowsForCheckout(item).length > 0
                ));

                setCombinedCheckoutOrders(refreshedOrders);
                if (refreshedOrders.length === 0) {
                    setSelectedTable("");
                    setSelectedTableReservation(null);
                    setPaidReservationLabel(null);
                } else {
                    const countLabel = t("checkout_page.multipleCheckoutOrderCount", { count: refreshedOrders.length });
                    setPaidReservationLabel(countLabel);
                    setSelectedTableReservation(countLabel);
                }
            }

            await fetchAllData();
            window.dispatchEvent(new CustomEvent('comanda-creata'));
            window.dispatchEvent(new CustomEvent('map-data-changed'));
            setCheckboxSelectedIds(new Set());
            setOpenDelete(false);
            setRefreshTrigger((prev) => prev + 1);
            if (shouldOfferClearTable) {
                setOpenLiberaTavolo(true);
            }
        } catch (err) {
            handleAlert(err?.message || String(err), "error");
        }
    }

    const resolveSelectedRows = useCallback(() => {
        const sourceRows = ordiniDivisi ? (ordineSchermo || []) : (ordiniUniti || []);
        if (sourceRows.length < 1) return [];

        if (checkboxSelectedIds.size < 1) {
            if (ordiniDivisi) {
                const seen = new Set();
                return sourceRows.filter((row) => {
                    const rowKey = getRowSelectionKey(row);
                    if (seen.has(rowKey)) return false;
                    seen.add(rowKey);
                    return true;
                });
            }
            return sourceRows;
        }

        return sourceRows.filter((row) =>
            checkboxSelectedIds.has(getRowSelectionKey(row))
        );
    }, [ordiniDivisi, ordineSchermo, ordiniUniti, checkboxSelectedIds, getRowSelectionKey]);

    const buildPatchRows = useCallback((row) => {
        if (!row) return [];
        if (ordiniDivisi) return [row];
        if (Array.isArray(row.originalOrders) && row.originalOrders.length > 0) return row.originalOrders;
        return [row];
    }, [ordiniDivisi]);

    const registerSplitCreatedOrder = useCallback((sourceOrderId, createdOrder, removeSourceOrder = false) => {
        if (!isMultipleCheckout || !createdOrder?.id) return;

        setCombinedCheckoutOrders((prev) => {
            const next = (prev || []).filter((ord) => {
                if (Number(ord?.id) === Number(createdOrder.id)) return false;
                if (removeSourceOrder && Number(ord?.id) === Number(sourceOrderId)) return false;
                return true;
            });
            return [...next, createdOrder];
        });
    }, [isMultipleCheckout]);

    const applySplitAwareLinePatch = useCallback(async (patchRow, payload) => {
        if (!ordiniDivisi) return false;

        const splitId = String(patchRow?.idSplit || "");
        if (!/_\d+$/.test(splitId)) return false;

        const selectedOrder =
            await getOrdineById(patchRow.orderId, token, tenant)
                .catch(() => (order || []).find((o) => Number(o?.id) === Number(patchRow?.orderId)));
        const currentProducts = Array.isArray(selectedOrder?.orderProducts) ? selectedOrder.orderProducts : [];
        const currentPackages = Array.isArray(selectedOrder?.orderPackages) ? selectedOrder.orderPackages : [];
        if (!selectedOrder) return false;
        const selectedOrderTableId = selectedOrder?.tableId ?? selectedOrder?.table?.id;
        const resolvedTableId = selectedOrderTableId == null
            ? null
            : Number(selectedOrderTableId ?? selectedTable?.id);
        if (resolvedTableId !== null && (!Number.isInteger(resolvedTableId) || resolvedTableId <= 0)) {
            throw new Error("Impossibile determinare tableId durante aggiornamento ordine.");
        }

        if (patchRow?.isPackage) {
            const targetPackage =
                currentPackages.find((p) => Number(p?.id) === Number(patchRow?.id)) ||
                currentPackages.find((p) =>
                    Number(p?.packageId ?? p?.package?.id) === Number(patchRow?.packageId ?? patchRow?.product?.id)
                );
            const currentQty = Number(targetPackage?.quantity || 0);
            if (!targetPackage || currentQty <= 1) return false;

            await patchPacchettoComanda(
                selectedOrder.id,
                {
                    orderPackageId: Number(targetPackage.id),
                    packageId: Number(targetPackage?.packageId ?? targetPackage?.package?.id),
                    quantity: currentQty - 1,
                    unitPrice: Number(targetPackage?.unitPrice ?? targetPackage?.package?.price ?? 0),
                    discount: Number(targetPackage?.discount ?? 0),
                    note: targetPackage?.note ?? null,
                },
                token,
                tenant
            );

            const nextPackageId = Number(targetPackage?.packageId ?? targetPackage?.package?.id);
            const nextPackageQuantity = Number(payload?.quantity ?? 1);
            const nextPackageUnitPrice = Number(payload?.unitPrice ?? targetPackage?.unitPrice ?? targetPackage?.package?.price ?? 0);
            const nextPackageDiscount = Number(payload?.discount ?? targetPackage?.discount ?? 0);
            const nextPackageNote = targetPackage?.note ?? null;

            const createdOrder = await creaComanda(
                {
                    tableId: resolvedTableId,
                    ...checkoutReservationEventOrderLink,
                    clientId: selectedOrder.clientId ?? null,
                    totalDiscount: 0,
                    status: selectedOrder.status ?? "CLOSED",
                    packages: [{
                        packageId: nextPackageId,
                        quantity: nextPackageQuantity,
                        unitPrice: nextPackageUnitPrice,
                        note: nextPackageNote,
                        discount: nextPackageDiscount,
                    }],
                },
                token,
                tenant
            );

            const createdPackage = Array.isArray(createdOrder?.orderPackages)
                ? createdOrder.orderPackages.find((p) => Number(p?.packageId ?? p?.package?.id) === nextPackageId)
                : null;
            if (createdPackage?.id && Number(createdPackage.unitPrice) !== nextPackageUnitPrice) {
                await patchPacchettoComanda(
                    createdOrder.id,
                    {
                        orderPackageId: Number(createdPackage.id),
                        packageId: nextPackageId,
                        quantity: nextPackageQuantity,
                        unitPrice: nextPackageUnitPrice,
                        discount: nextPackageDiscount,
                        note: nextPackageNote,
                    },
                    token,
                    tenant
                );
            }
            registerSplitCreatedOrder(selectedOrder.id, createdOrder);
            return true;
        }

        const targetProduct =
            currentProducts.find((p) => Number(p?.id) === Number(patchRow?.id)) ||
            currentProducts.find((p) =>
                Number(p?.productId ?? p?.product?.id) === Number(patchRow?.productId ?? patchRow?.product?.id) &&
                getVariantIds(p).sort((a, b) => a - b).join("-") === getVariantIds(patchRow).sort((a, b) => a - b).join("-")
            );
        const currentQty = Number(targetProduct?.quantity || 0);
        if (!targetProduct) return false;

        const nextProductId = Number(targetProduct?.productId ?? targetProduct?.product?.id);
        const nextProductVariantIds = getVariantIds(targetProduct);
        const nextProductQuantity = Number(payload?.quantity ?? 1);
        const nextProductUnitPrice = Number(payload?.unitPrice ?? targetProduct?.unitPrice ?? targetProduct?.product?.price ?? 0);
        const nextProductDiscount = Number(payload?.discount ?? targetProduct?.discount ?? 0);
        const nextProductNote = targetProduct?.note ?? null;

        if (currentQty <= 1) {
            const orderLineQty = [
                ...currentProducts.map((p) => Number(p?.quantity || 0)),
                ...currentPackages.map((p) => Number(p?.quantity || 0)),
            ].reduce((sum, qty) => sum + qty, 0);

            const removeSourceOrder = orderLineQty <= currentQty;
            if (removeSourceOrder) {
                await eliminaComanda(selectedOrder.id, token, tenant);
            } else {
                await patchProdottoComanda(
                    selectedOrder.id,
                    {
                        orderProductId: Number(targetProduct.id),
                        quantity: 0,
                        unitPrice: Number(targetProduct?.unitPrice ?? targetProduct?.product?.price ?? 0),
                        discount: Number(targetProduct?.discount ?? 0),
                        note: targetProduct?.note ?? null,
                    },
                    token,
                    tenant
                );
            }

            const createdOrder = await creaComanda(
                {
                    tableId: resolvedTableId,
                    ...checkoutReservationEventOrderLink,
                    clientId: selectedOrder.clientId ?? null,
                    totalDiscount: 0,
                    status: selectedOrder.status ?? "CLOSED",
                    products: [{
                        productId: nextProductId,
                        variantIds: nextProductVariantIds,
                        quantity: nextProductQuantity,
                        unitPrice: nextProductUnitPrice,
                        note: nextProductNote,
                        discount: nextProductDiscount,
                    }],
                },
                token,
                tenant
            );

            const createdProduct = Array.isArray(createdOrder?.orderProducts)
                ? createdOrder.orderProducts.find((p) => Number(p?.productId ?? p?.product?.id) === nextProductId)
                : null;
            if (createdProduct?.id && Number(createdProduct.unitPrice) !== nextProductUnitPrice) {
                await patchProdottoComanda(
                    createdOrder.id,
                    {
                        orderProductId: Number(createdProduct.id),
                        quantity: nextProductQuantity,
                        unitPrice: nextProductUnitPrice,
                        discount: nextProductDiscount,
                        note: nextProductNote,
                    },
                    token,
                    tenant
                );
            }
            registerSplitCreatedOrder(selectedOrder.id, createdOrder, removeSourceOrder);

            const removedKey = getSplitTargetKey(targetProduct);
            return {
                remainingSplitTargetKeys: Array.from(splitTargetRowIds || []).filter((key) => key !== removedKey),
            };
        }

        await patchProdottoComanda(
            selectedOrder.id,
            {
                orderProductId: Number(targetProduct.id),
                quantity: currentQty - 1,
                unitPrice: Number(targetProduct?.unitPrice ?? targetProduct?.product?.price ?? 0),
                discount: Number(targetProduct?.discount ?? 0),
                note: targetProduct?.note ?? null,
            },
            token,
            tenant
        );

        const createdOrder = await creaComanda(
            {
                tableId: resolvedTableId,
                ...checkoutReservationEventOrderLink,
                clientId: selectedOrder.clientId ?? null,
                totalDiscount: 0,
                status: selectedOrder.status ?? "CLOSED",
                products: [{
                    productId: nextProductId,
                    variantIds: nextProductVariantIds,
                    quantity: nextProductQuantity,
                    unitPrice: nextProductUnitPrice,
                    note: nextProductNote,
                    discount: nextProductDiscount,
                }],
            },
            token,
            tenant
        );

        const createdProduct = Array.isArray(createdOrder?.orderProducts)
            ? createdOrder.orderProducts.find((p) => Number(p?.productId ?? p?.product?.id) === nextProductId)
            : null;
        if (createdProduct?.id && Number(createdProduct.unitPrice) !== nextProductUnitPrice) {
            await patchProdottoComanda(
                createdOrder.id,
                {
                    orderProductId: Number(createdProduct.id),
                    quantity: nextProductQuantity,
                    unitPrice: nextProductUnitPrice,
                    discount: nextProductDiscount,
                    note: nextProductNote,
                },
                token,
                tenant
            );
        }
        registerSplitCreatedOrder(selectedOrder.id, createdOrder);

        return {
            remainingSplitTargetKeys: [getSplitTargetKey(targetProduct)],
        };
    }, [ordiniDivisi, order, getVariantIds, getSplitTargetKey, splitTargetRowIds, selectedTable?.id, checkoutReservationEventOrderLink, token, tenant, registerSplitCreatedOrder]);

    const patchLineQuantity = useCallback(async (patchRow, quantity) => {
        const nextQty = Number(quantity);
        if (!patchRow || !Number.isFinite(nextQty) || nextQty < 0) return false;

        if (patchRow.isPackage) {
            const handledBySplit = await applySplitAwareLinePatch(patchRow, {
                quantity: nextQty,
                unitPrice: Number(patchRow.unitPrice ?? patchRow.product?.price ?? 0),
                discount: Number(patchRow.discount ?? 0),
            });
            if (handledBySplit) return handledBySplit;

            await patchPacchettoComanda(
                patchRow.orderId,
                {
                    packageId: Number(patchRow.packageId ?? patchRow.product?.id),
                    orderPackageId: Number(patchRow.id),
                    quantity: nextQty,
                    unitPrice: Number(patchRow.unitPrice ?? patchRow.product?.price ?? 0),
                    discount: Number(patchRow.discount ?? 0),
                },
                token,
                tenant
            );
            return true;
        }

        const handledBySplit = await applySplitAwareLinePatch(patchRow, {
            quantity: nextQty,
            unitPrice: Number(patchRow.unitPrice ?? patchRow.product?.price ?? 0),
            discount: Number(patchRow.discount ?? 0),
        });
        if (handledBySplit) return handledBySplit;

        await patchProdottoComanda(
            patchRow.orderId,
            {
                orderProductId: Number(patchRow.id),
                quantity: nextQty,
                unitPrice: Number(patchRow.unitPrice ?? patchRow.product?.price ?? 0),
                discount: Number(patchRow.discount ?? 0),
            },
            token,
            tenant
        );
        return true;
    }, [applySplitAwareLinePatch, token, tenant]);

    const applyOrderLevelAdjustment = useCallback(async (patchRows, amount, sign) => {
        const rowsByOrder = new Map();
        (patchRows || []).forEach((row) => {
            const orderId = Number(row?.orderId);
            if (!Number.isInteger(orderId) || orderId <= 0) return;
            if (!rowsByOrder.has(orderId)) rowsByOrder.set(orderId, []);
            rowsByOrder.get(orderId).push(row);
        });

        const entries = Array.from(rowsByOrder.entries());
        if (entries.length < 1) {
            throw new Error(t("checkout_page.editPrice_note_2"));
        }

        const orderWeights = entries.map(([, rows]) => Math.max(0, Math.round(getRowsSnapshotTotal(rows) * 100)));
        const totalWeight = orderWeights.reduce((sum, value) => sum + value, 0);
        const requestedAmountCents = Math.max(0, Math.round(Number(amount || 0) * 100));
        if (requestedAmountCents < 1) throw new Error(t("checkout_page.editPrice_note_2"));
        let assignedAmountCents = 0;

        for (let index = 0; index < entries.length; index++) {
            const [orderId] = entries[index];
            const ratio = totalWeight > 0 ? orderWeights[index] / totalWeight : 1 / entries.length;
            const orderAmountCents = index < entries.length - 1
                ? Math.round(requestedAmountCents * ratio)
                : requestedAmountCents - assignedAmountCents;
            assignedAmountCents += orderAmountCents;
            if (orderAmountCents <= 0) continue;

            const latestOrder = await getOrdineById(orderId, token, tenant)
                .catch(() => (order || []).find((item) => Number(item?.id) === orderId));
            if (!latestOrder) throw new Error(`Ordine ${orderId} non trovato`);

            const orderSubTotal = Number(latestOrder?.subTotal || 0);
            const orderPercentDiscount = Number(latestOrder?.totalDiscount || 0);
            const currentDiscountAmount = Math.max(0, Number(latestOrder?.discountAmount || 0));
            const currentSurchargeAmount = Math.max(0, Number(latestOrder?.surchargeAmount || 0));
            const nextSurchargeAmount = sign > 0
                ? formattaFloat(currentSurchargeAmount + (orderAmountCents / 100))
                : currentSurchargeAmount;
            const maxDiscountAmount = Math.max(
                0,
                orderSubTotal * (1 + orderPercentDiscount / 100) + nextSurchargeAmount
            );
            const nextDiscountAmount = sign < 0
                ? Math.min(maxDiscountAmount, formattaFloat(currentDiscountAmount + (orderAmountCents / 100)))
                : currentDiscountAmount;

            await aggiornaRettificheComanda(
                orderId,
                {
                    discountAmount: nextDiscountAmount,
                    surchargeAmount: nextSurchargeAmount,
                },
                token,
                tenant
            );
        }
    }, [getRowsSnapshotTotal, order, t, token, tenant]);

    function startPriceEditRequest() {
        if (priceEditInFlightRef.current) return false;
        priceEditInFlightRef.current = true;
        setPriceEditSubmitting(true);
        return true;
    }

    function finishPriceEditRequest() {
        priceEditInFlightRef.current = false;
        setPriceEditSubmitting(false);
    }

    async function handleEditPrice(forcedSign = null) {
        if (!importoDisplay || Number(importoDisplay) <= 0) {
            handleAlert(t("checkout_page.editQty_note_1"), "warning")
            setEditPrezzo(false)
            return;
        }

        if (forcedSign === null && !incrementoPrezzo && !decrementoPrezzo) {
            handleAlert(t("checkout_page.editPrice_note_1"), "warning")
            setEditPrezzo(false)
            return;
        }

        if (!startPriceEditRequest()) return;

        try {
            const selectedRows = resolveSelectedRows();
            if (selectedRows.length < 1) {
                handleAlert(t("checkout_page.editPrice_note_1"), "warning")
                setEditPrezzo(false)
                return;
            }
            const variationValue = Number(importoDisplay);
            const sign = forcedSign !== null ? forcedSign : (incrementoPrezzo ? 1 : -1);
            const selectedPatchRows = selectedRows.flatMap((row) => buildPatchRows(row));
            if (selectedPatchRows.length < 1) {
                handleAlert(t("checkout_page.editPrice_note_1"), "warning")
                setEditPrezzo(false)
                return;
            }

            const selectedAmount = getRowsSnapshotTotal(selectedPatchRows);
            const adjustmentAmount = cambiaTipoVariazione
                ? variationValue
                : formattaFloat(selectedAmount * variationValue / 100);
            await applyOrderLevelAdjustment(selectedPatchRows, adjustmentAmount, sign);

            setRefreshTrigger(prev => prev + 1)
            setImportoDisplay("")
            setCheckboxSelectedIds(new Set())
            setEditPrezzo(false)
            setIncrementoPrezzo(false)
            setDecrementoPrezzo(false)
        } catch (err) {
            handleAlert(err?.message || t("checkout_page.editPrice_note_2"), "error")
            setEditPrezzo(false)
            return;
        } finally {
            finishPriceEditRequest();
        }
    }

    async function handleApplyDirectPrice() {
        const nextUnitPrice = Number(importoDisplay);
        if (!importoDisplay || Number.isNaN(nextUnitPrice) || nextUnitPrice <= 0) {
            handleAlert(t("checkout_page.editQty_note_1"), "warning");
            return;
        }

        if (!startPriceEditRequest()) return;

        try {
            const selectedRows = resolveSelectedRows();
            if (selectedRows.length < 1) {
                handleAlert(t("checkout_page.editPrice_note_1"), "warning");
                return;
            }
            const patchRows = selectedRows.flatMap((row) => buildPatchRows(row));
            if (patchRows.length < 1) {
                handleAlert(t("checkout_page.editPrice_note_1"), "warning");
                return;
            }

            // 1 row -> il prezzo inserito diventa il nuovo prezzo unitario della riga, con eventuale calcolo dello sconto se differisce dal prezzo base del prodotto/pacchetto
            if (patchRows.length === 1) {
                const patchRow = patchRows[0];
                const basePrice = Number(patchRow.product?.price ?? nextUnitPrice);
                const payload = {
                    quantity: Number(patchRow.quantity) || 1,
                    unitPrice: formattaFloat(nextUnitPrice),
                    discount: formattaFloat(nextUnitPrice - basePrice),
                };

                if (patchRow.isFixedPriceMenu) {
                    await patchMenuPrezzoFissoComanda(
                        patchRow.orderId,
                        {
                            orderFixedPriceMenuId: Number(patchRow.id),
                            finalPrice: payload.unitPrice,
                        },
                        token,
                        tenant
                    );
                } else if (patchRow.isPackage) {
                    const handledBySplit = await applySplitAwareLinePatch(patchRow, payload);
                    if (handledBySplit) {
                        setRefreshTrigger(prev => prev + 1);
                        setCheckboxSelectedIds(new Set());
                        setImportoDisplay("");
                        setEditPrezzo(false);
                        setIncrementoPrezzo(false);
                        setDecrementoPrezzo(false);
                        return;
                    }

                    await patchPacchettoComanda(
                        patchRow.orderId,
                        {
                            ...payload,
                            packageId: Number(patchRow.packageId ?? patchRow.product?.id),
                            orderPackageId: Number(patchRow.id),
                        },
                        token,
                        tenant
                    );
                } else {
                    const handledBySplit = await applySplitAwareLinePatch(patchRow, payload);
                    if (handledBySplit) {
                        setRefreshTrigger(prev => prev + 1);
                        setCheckboxSelectedIds(new Set());
                        setImportoDisplay("");
                        setEditPrezzo(false);
                        setIncrementoPrezzo(false);
                        setDecrementoPrezzo(false);
                        return;
                    }

                    await patchProdottoComanda(
                        patchRow.orderId,
                        { ...payload, orderProductId: Number(patchRow.id) },
                        token,
                        tenant
                    );
                }
            } else {
                // more rows -> il prezzo inserito viene distribuito proporzionalmente tra le righe selezionate in base al prezzo unitario corrente, mantenendo invariato lo sconto unitario attuale di ogni riga
                const currentTotal = patchRows.reduce((acc, row) => {
                    const qty = Number(row.quantity) || 1;
                    const unit = Number(row.unitPrice ?? row.product?.price ?? 0);
                    return acc + (qty * unit);
                }, 0);

                if (currentTotal <= 0) {
                    handleAlert(t("checkout_page.editPrice_note_2"), "warning");
                    return;
                }

                const ratio = nextUnitPrice / currentTotal;
                let assignedSubtotal = 0;

                for (let i = 0; i < patchRows.length; i++) {
                    const patchRow = patchRows[i];
                    const qty = Number(patchRow.quantity) || 1;
                    const currentUnit = Number(patchRow.unitPrice ?? patchRow.product?.price ?? 0);

                    let nextUnit;
                    if (i < patchRows.length - 1) {
                        nextUnit = formattaFloat(currentUnit * ratio);
                        assignedSubtotal += (qty * nextUnit);
                    } else {
                        const remaining = formattaFloat(nextUnitPrice - assignedSubtotal);
                        nextUnit = formattaFloat(remaining / qty);
                    }

                    const basePrice = Number(patchRow.product?.price ?? nextUnit);
                    const payload = {
                        quantity: qty,
                        unitPrice: nextUnit,
                        discount: formattaFloat(nextUnit - basePrice),
                    };

                    if (patchRow.isFixedPriceMenu) {
                        await patchMenuPrezzoFissoComanda(
                            patchRow.orderId,
                            {
                                orderFixedPriceMenuId: Number(patchRow.id),
                                finalPrice: payload.unitPrice,
                            },
                            token,
                            tenant
                        );
                    } else if (patchRow.isPackage) {
                        const handledBySplit = await applySplitAwareLinePatch(patchRow, payload);
                        if (handledBySplit) continue;

                        await patchPacchettoComanda(
                            patchRow.orderId,
                            {
                                ...payload,
                                packageId: Number(patchRow.packageId ?? patchRow.product?.id),
                                orderPackageId: Number(patchRow.id),
                            },
                            token,
                            tenant
                        );
                    } else {
                        const handledBySplit = await applySplitAwareLinePatch(patchRow, payload);
                        if (handledBySplit) continue;

                        await patchProdottoComanda(
                            patchRow.orderId,
                            { ...payload, orderProductId: Number(patchRow.id) },
                            token,
                            tenant
                        );
                    }
                }
            }

            setRefreshTrigger(prev => prev + 1);
            setCheckboxSelectedIds(new Set());
            setImportoDisplay("");
            setEditPrezzo(false);
            setIncrementoPrezzo(false);
            setDecrementoPrezzo(false);
        } catch (err) {
            handleAlert(err?.message || String(err), "error");
        } finally {
            finishPriceEditRequest();
        }
    }

    async function handleClearDiscount() {
        try {
            const selectedRows = resolveSelectedRows();
            if (selectedRows.length < 1) {
                handleAlert(t("checkout_page.editPrice_note_1"), "warning")
                return;
            }
            const selectedPatchRows = selectedRows.flatMap((row) => buildPatchRows(row));
            const orderIdsToClear = [...new Set(selectedPatchRows
                .map((row) => Number(row?.orderId))
                .filter((orderId) => Number.isInteger(orderId) && orderId > 0)
            )];

            for (const orderId of orderIdsToClear) {
                const latestOrder = await getOrdineById(orderId, token, tenant)
                    .catch(() => (order || []).find((item) => Number(item?.id) === orderId));
                const currentDiscountAmount = Number(latestOrder?.discountAmount || 0);
                const currentSurchargeAmount = Number(latestOrder?.surchargeAmount || 0);
                if (!latestOrder || (currentDiscountAmount <= 0 && currentSurchargeAmount <= 0)) continue;

                await aggiornaRettificheComanda(
                    orderId,
                    { discountAmount: 0, surchargeAmount: 0 },
                    token,
                    tenant
                );
            }
            for (const row of selectedRows) {
                const patchRows = buildPatchRows(row);
                for (const patchRow of patchRows) {
                    const currentUnitPrice = Number(patchRow.unitPrice ?? 0);
                    const currentDiscount = Number(patchRow.discount ?? 0);
                    // "Rimuovi sconto" deve ripristinare il prezzo reale della riga
                    // senza dipendere dal listino prodotto/pacchetto.
                    const baseUnitPrice = formattaFloat(currentUnitPrice - currentDiscount);

                    const payload = {
                        quantity: Number(patchRow.quantity) || 1,
                        unitPrice: baseUnitPrice,
                        discount: 0,
                    };

                    if (patchRow.isFixedPriceMenu) {
                        await patchMenuPrezzoFissoComanda(
                            patchRow.orderId,
                            {
                                orderFixedPriceMenuId: Number(patchRow.id),
                                finalPrice: payload.unitPrice,
                            },
                            token,
                            tenant
                        );
                    } else if (patchRow.isPackage) {
                        const handledBySplit = await applySplitAwareLinePatch(patchRow, payload);
                        if (handledBySplit) continue;

                        await patchPacchettoComanda(
                            patchRow.orderId,
                            {
                                ...payload,
                                packageId: Number(patchRow.packageId ?? patchRow.product?.id),
                                orderPackageId: Number(patchRow.id),
                            },
                            token,
                            tenant
                        );
                    } else {
                        const handledBySplit = await applySplitAwareLinePatch(patchRow, payload);
                        if (handledBySplit) continue;

                        await patchProdottoComanda(
                            patchRow.orderId,
                            { ...payload, orderProductId: Number(patchRow.id) },
                            token,
                            tenant
                        );
                    }
                }
            }

            setRefreshTrigger(prev => prev + 1);
            setCheckboxSelectedIds(new Set());
            ResetImportDisplay();
            setEditPrezzo(false);
        } catch (err) {
            handleAlert(err?.message || String(err), "error")
        }
    }

    const handleSplitToggle = useCallback(() => {
        if (ordiniDivisi) {
            setOrdiniDivisi(false);
            setSplitTargetRowIds(null);
            setCheckboxSelectedIds(new Set());
            return;
        }

        const sourceRows = ordiniUniti || [];
        if (sourceRows.length < 1) return;
        if (checkboxSelectedIds.size < 1) {
            handleAlert(t("checkout_page.editPrice_note_1"), "warning");
            return;
        }

        const selectedRows = sourceRows.filter((row) => checkboxSelectedIds.has(getRowSelectionKey(row, false)));

        if (selectedRows.length < 1) {
            handleAlert(t("checkout_page.editPrice_note_1"), "warning");
            return;
        }
        if (selectedRows.some((row) => row?.isFixedPriceMenu)) {
            handleAlert(t("fixedPriceMenu.checkoutSplitBlocked"), "warning");
            return;
        }

        const hasWeightProduct = selectedRows.some((row) => {
            const rows = Array.isArray(row.originalOrders) ? row.originalOrders : [row];
            return rows.some(isWeightOrderRow);
        });
        if (hasWeightProduct) {
            handleAlert(t("checkout_page.splitWeightProductWarning"), "warning");
            return;
        }

        const lineIds = new Set(
            selectedRows
                .flatMap((row) => (Array.isArray(row.originalOrders) ? row.originalOrders : [row]))
                .map((row) => getSplitTargetKey(row))
        );
        setSplitTargetRowIds(lineIds);

        setOrdiniDivisi(true);
        setCheckboxSelectedIds(new Set());
    }, [ordiniDivisi, ordiniUniti, checkboxSelectedIds, t, handleAlert, getRowSelectionKey, getSplitTargetKey, isWeightOrderRow]);

    async function handleEditQty() {
        if (checkboxSelectedIds.size < 1) {
            handleAlert(t("checkout_page.editQty_note_1"), "warning")
            setEditQty(false)
            return;
        } if (checkboxSelectedIds.size > 1) {
            handleAlert(t("checkout_page.editQty_note_2"), "warning")
            setEditQty(false)
            return;
        } else {
            const selectedRows = resolveSelectedRows();
            const ordine_da_modificare = selectedRows?.[0];

            if (!ordine_da_modificare?.id || !ordine_da_modificare?.orderId) {
                handleAlert(t("checkout_page.editQty_note_1"), "warning")
                setEditQty(false)
                return;
            }
            if (ordine_da_modificare?.isFixedPriceMenu) {
                handleAlert(t("fixedPriceMenu.checkoutQuantitySnapshot"), "warning");
                setEditQty(false);
                return;
            }

            const nextQty = parseInt(importoDisplay, 10);
            if (!nextQty || Number.isNaN(nextQty) || nextQty <= 0) {
                handleAlert(t("checkout_page.editQty_note_1"), "warning")
                setEditQty(false)
                return;
            }
            const keepSplitAfterEdit =
                ordiniDivisi &&
                /_\d+$/.test(String(ordine_da_modificare?.idSplit || ""));
            const splitTargetAfterEdit = keepSplitAfterEdit
                ? new Set([getSplitTargetKey(ordine_da_modificare)])
                : null;

            setEditQty(false)
            try {
                let splitPatchResult = null;
                const patchRows = buildPatchRows(ordine_da_modificare);

                if (!ordiniDivisi && patchRows.length > 1) {
                    const currentTotalQty = patchRows.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);
                    let remainingQty = nextQty;

                    for (let i = 0; i < patchRows.length; i++) {
                        const patchRow = patchRows[i];
                        const currentRowQty = Number(patchRow.quantity) || 0;
                        let rowNextQty;

                        if (nextQty >= currentTotalQty) {
                            rowNextQty = i === 0
                                ? currentRowQty + (nextQty - currentTotalQty)
                                : currentRowQty;
                        } else if (i < patchRows.length - 1) {
                            rowNextQty = Math.min(currentRowQty, Math.max(remainingQty, 0));
                            remainingQty -= rowNextQty;
                        } else {
                            rowNextQty = Math.max(remainingQty, 0);
                        }

                        await patchLineQuantity(patchRow, rowNextQty);
                    }
                } else {
                    splitPatchResult = await patchLineQuantity(patchRows[0], nextQty);
                }

                setRefreshTrigger(prev => prev + 1)
                setImportoDisplay("")
                setCheckboxSelectedIds(new Set())
                setOrdiniDivisi(keepSplitAfterEdit)
                setSplitTargetRowIds(
                    keepSplitAfterEdit && Array.isArray(splitPatchResult?.remainingSplitTargetKeys)
                        ? new Set(splitPatchResult.remainingSplitTargetKeys)
                        : splitTargetAfterEdit
                )
            } catch (err) {
                handleAlert(err?.message || String(err), "error");
            }
        }
    }

    function dataAdesso() {
        return formatDateTimeDdMmYyyy(new Date());
    }

    const calcolaTotale = (array) => {
        const rows = Array.isArray(array) ? array : [];
        const total = rows === ordiniFiltrati
            ? getBackendOrdersTotal(rows)
            : getRowsSnapshotTotal(rows);
        return total.toFixed(2);
    }

    const calcolaTotaleOrdine = useCallback((ordine) => {
        const hasBackendSubTotal = ordine?.subTotal !== undefined && ordine?.subTotal !== null && ordine?.subTotal !== "";
        const backendSubTotal = Number(ordine?.subTotal);
        if (hasBackendSubTotal && Number.isFinite(backendSubTotal)) {
            return getOrderNetTotal(
                backendSubTotal,
                ordine?.totalDiscount,
                ordine?.discountAmount,
                ordine?.surchargeAmount
            );
        }

        const productsTotal = (ordine?.orderProducts || ordine?.products || []).reduce((sum, row) => {
            const unitPrice = Number(row?.unitPrice ?? row?.product?.price ?? 0);
            const quantity = Number(row?.quantity ?? 0);
            return sum + (unitPrice * quantity);
        }, 0);

        const packagesTotal = (ordine?.orderPackages || ordine?.packages || []).reduce((sum, row) => {
            const unitPrice = Number(row?.unitPrice ?? row?.package?.price ?? 0);
            const quantity = Number(row?.quantity ?? 0);
            return sum + (unitPrice * quantity);
        }, 0);

        const fixedPriceMenusTotal = (ordine?.orderFixedPriceMenus || []).reduce((sum, row) => {
            return sum + (Number(row?.finalPrice || 0) * Number(row?.quantity || 0));
        }, 0);

        return getOrderNetTotal(
            productsTotal + packagesTotal + fixedPriceMenusTotal,
            ordine?.totalDiscount,
            ordine?.discountAmount,
            ordine?.surchargeAmount
        );
    }, [getOrderNetTotal]);

    const normalizeVatRate = useCallback((value) => {
        if (value === null || value === undefined || value === "") return undefined;
        const parsed = Number(String(value).replace(",", "."));
        return Number.isFinite(parsed) ? parsed : undefined;
    }, []);

    const getProductVatRate = useCallback((product, fallbackVatRate) => {
        const rates = Array.isArray(product?.categories)
            ? product.categories
                .map((entry) => normalizeVatRate(entry?.category?.vatRate))
                .filter((rate) => rate !== undefined)
            : [];

        if (rates.length > 0) return Math.max(...rates);

        const categoryId = Number(product?.categoryId);
        if (Number.isInteger(categoryId) && Array.isArray(category)) {
            const matchedCategory = category.find((item) => Number(item?.id) === categoryId);
            const categoryVatRate = normalizeVatRate(matchedCategory?.vatRate);
            if (categoryVatRate !== undefined) return categoryVatRate;
        }

        return fallbackVatRate;
    }, [category, normalizeVatRate]);

    const refreshCheckoutPrepaymentSummary = useCallback(async () => {
        if (!checkoutReservationEventId) return null;
        const summary = await getReservationEventPrepaymentSummary(checkoutReservationEventId, token, tenant);
        setCheckoutPrepaymentSummary(summary);
        return summary;
    }, [checkoutReservationEventId, tenant, token]);

    const selectedPrepaymentProduct = useMemo(() => (
        prepaymentPosProducts.find((product) => Number(product?.id) === Number(selectedPrepaymentProductId)) || null
    ), [prepaymentPosProducts, selectedPrepaymentProductId]);

    const prepaymentPosMovements = useMemo(() => (
        Array.isArray(checkoutPrepaymentSummary?.movements) ? checkoutPrepaymentSummary.movements : []
    ), [checkoutPrepaymentSummary?.movements]);

    const prepaymentPosRefundableSources = useMemo(() => (
        prepaymentPosMovements.filter((movement) => (
            movement?.operation === "RECEIPT"
            && movement?.type === prepaymentPosMode
            && prepaymentMovementAvailableAmount(movement, prepaymentPosMovements) > 0.009
        ))
    ), [prepaymentPosMode, prepaymentPosMovements]);

    const prepaymentPosTotals = checkoutPrepaymentSummary?.totals || {};

    const prepaymentPosDocumentTypes = prepaymentPosForm.operation === "REVERSE"
        ? PREPAYMENT_POS_ADVANCE_REVERSAL_TYPES
        : PREPAYMENT_POS_ADVANCE_DOCUMENT_TYPES;

    const prepaymentDocumentLabel = useCallback((value) => {
        const key = `checkout_page.prepaymentPos.documentTypes.${value}`;
        const label = t(key);
        return label === key ? String(value || "").replace(/_/g, " ") : label;
    }, [t]);

    const updatePrepaymentPosForm = useCallback((field, value) => {
        setPrepaymentPosForm((prev) => ({ ...prev, [field]: value }));
    }, []);

    const setPrepaymentPosOperation = useCallback((operation) => {
        setPrepaymentPosForm((prev) => ({
            ...prev,
            operation,
            sourceMovementId: operation === "RECEIVE" ? "" : prev.sourceMovementId,
            documentType: prepaymentPosMode === "DEPOSIT"
                ? "PLAIN_RECEIPT"
                : operation === "REVERSE"
                    ? "COMMERCIAL_CANCELLATION"
                    : "COMMERCIAL_DOCUMENT",
        }));
    }, [prepaymentPosMode]);

    const openPrepaymentPosMode = useCallback((mode) => {
        const normalizedMode = normalizePrepaymentPosMode(mode);
        if (!normalizedMode) return;
        if (!checkoutReservationEventId) {
            handleAlert(t("checkout_page.prepaymentPos.noEvent"), "warning");
            return;
        }
        setPrepaymentPosMode(normalizedMode);
        setSelectedPrepaymentProductId("");
        setPrepaymentPosForm({
            ...prepaymentPosInitialForm(),
            documentType: normalizedMode === "DEPOSIT" ? "PLAIN_RECEIPT" : "COMMERCIAL_DOCUMENT",
        });
    }, [checkoutReservationEventId, handleAlert, t]);

    const closePrepaymentPosMode = useCallback(() => {
        setPrepaymentPosMode(null);
        setSelectedPrepaymentProductId("");
        setPrepaymentPosForm(prepaymentPosInitialForm());
    }, []);

    const selectPrepaymentProduct = useCallback((product) => {
        const nextAmount = normalizeAmountInput(importoDisplay) > 0
            ? normalizeAmountInput(importoDisplay).toFixed(2)
            : Number(product?.price || 0) > 0
                ? Number(product.price).toFixed(2)
                : prepaymentPosForm.amount;
        const text = productSearchText(product);
        const nextOperation = prepaymentPosMode === "DEPOSIT" && /(reso|refund|rimborso)/i.test(text)
            ? "REFUND"
            : "RECEIVE";

        setSelectedPrepaymentProductId(product?.id || "");
        setPrepaymentPosForm((prev) => ({
            ...prev,
            operation: nextOperation,
            amount: nextAmount,
            vatRate: prepaymentPosMode === "ADVANCE"
                ? String((getProductVatRate(product, Number(prev.vatRate || 10)) ?? prev.vatRate) || "10")
                : "",
            documentType: prepaymentPosMode === "DEPOSIT" ? "PLAIN_RECEIPT" : prev.documentType || "COMMERCIAL_DOCUMENT",
            description: product?.name || prev.description,
        }));
    }, [getProductVatRate, importoDisplay, prepaymentPosForm.amount, prepaymentPosMode, productSearchText]);

    const prepaymentPosErrorMessage = useCallback((err) => {
        if (err?.code === "PREPAYMENT_EXCEEDS_PRACTICE_TOTAL") return t("checkout_page.prepaymentPos.errors.exceedsTotal");
        if (err?.code === "REFUND_EXCEEDS_AVAILABLE_AMOUNT") return t("checkout_page.prepaymentPos.errors.refundExceeds");
        if (err?.code === "ADVANCE_ALREADY_APPLIED") return t("checkout_page.prepaymentAdvanceAlreadyApplied");
        if (err?.code === "ADVANCE_ALREADY_PROCESSED") return t("checkout_page.prepaymentPos.errors.advanceProcessed");
        if (err?.code === "SOURCE_MOVEMENT_NOT_FOUND") return t("checkout_page.prepaymentSourceMissing");
        if (err?.code === "TD02_NOT_SENT") return t("checkout_page.prepaymentPos.errors.td02NotSent");
        return err?.message || t("checkout_page.prepaymentPos.errors.generic");
    }, [t]);

    const submitPrepaymentPos = useCallback(async () => {
        if (!isPrepaymentPosActive || prepaymentPosSubmitting) return;
        const amount = normalizeAmountInput(prepaymentPosForm.amount);
        if (prepaymentPosForm.operation !== "REVERSE" && amount <= 0) {
            handleAlert(t("checkout_page.prepaymentPos.errors.amountRequired"), "warning");
            return;
        }

        const nowKey = Date.now();
        const common = {
            paymentMethod: prepaymentPosForm.paymentMethod,
            note: prepaymentPosForm.note.trim() || undefined,
            idempotencyKey: [
                "pos",
                checkoutReservationEventId,
                prepaymentPosMode,
                prepaymentPosForm.operation,
                nowKey,
            ].join(":").slice(0, 100),
        };

        setPrepaymentPosSubmitting(true);
        try {
            let result;
            if (prepaymentPosForm.operation === "REFUND") {
                const sourceMovementId = Number(prepaymentPosForm.sourceMovementId);
                if (!sourceMovementId) throw new Error(t("checkout_page.prepaymentPos.errors.sourceRequired"));
                result = await refundReservationEventPrepayment(checkoutReservationEventId, sourceMovementId, {
                    ...common,
                    amount,
                    documentType: "PLAIN_RECEIPT",
                }, token, tenant);
            } else if (prepaymentPosForm.operation === "REVERSE") {
                const sourceMovementId = Number(prepaymentPosForm.sourceMovementId);
                if (!sourceMovementId) throw new Error(t("checkout_page.prepaymentPos.errors.sourceRequired"));
                const payload = {
                    ...common,
                    documentType: prepaymentPosForm.documentType,
                };
                if (prepaymentPosForm.documentType !== "CREDIT_NOTE_TD04") {
                    if (!prepaymentPosForm.documentNumber.trim() || !prepaymentPosForm.documentDate) {
                        throw new Error(t("checkout_page.prepaymentPos.errors.documentRequired"));
                    }
                    payload.documentNumber = prepaymentPosForm.documentNumber.trim();
                    payload.documentDate = new Date(`${prepaymentPosForm.documentDate}T12:00:00`).toISOString();
                }
                result = await reverseReservationEventPrepayment(checkoutReservationEventId, sourceMovementId, payload, token, tenant);
            } else {
                const payload = {
                    ...common,
                    type: prepaymentPosMode,
                    amount,
                    documentType: prepaymentPosMode === "DEPOSIT" ? "PLAIN_RECEIPT" : prepaymentPosForm.documentType,
                    description: prepaymentPosForm.description.trim() || selectedPrepaymentProduct?.name || undefined,
                };

                if (prepaymentPosMode === "ADVANCE") {
                    const vatRate = normalizeAmountInput(prepaymentPosForm.vatRate);
                    if (!vatRate && vatRate !== 0) throw new Error(t("checkout_page.prepaymentPos.errors.vatRequired"));
                    if (!payload.description) throw new Error(t("checkout_page.prepaymentPos.errors.descriptionRequired"));
                    payload.vatRate = vatRate;
                    if (prepaymentPosForm.documentType === "INVOICE_TD02") {
                        if (vatRate === 0) {
                            if (!prepaymentPosForm.vatNature) throw new Error(t("checkout_page.prepaymentPos.errors.vatNatureRequired"));
                            payload.vatNature = prepaymentPosForm.vatNature;
                        }
                    } else {
                        if (!prepaymentPosForm.documentNumber.trim() || !prepaymentPosForm.documentDate) {
                            throw new Error(t("checkout_page.prepaymentPos.errors.documentRequired"));
                        }
                        payload.documentNumber = prepaymentPosForm.documentNumber.trim();
                        payload.documentDate = new Date(`${prepaymentPosForm.documentDate}T12:00:00`).toISOString();
                    }
                }

                result = await createReservationEventPrepayment(checkoutReservationEventId, payload, token, tenant);
            }

            await refreshCheckoutPrepaymentSummary();
            const invoiceDelivery = result?.invoiceDelivery;
            if (invoiceDelivery?.status) {
                handleAlert(
                    t(`checkout_page.prepaymentPos.invoiceDelivery.${invoiceDelivery.status}`, {
                        invoiceId: invoiceDelivery.invoiceId || "",
                        message: invoiceDelivery.message || "",
                    }),
                    prepaymentDeliverySeverity(invoiceDelivery),
                );
            } else {
                handleAlert(t("checkout_page.prepaymentPos.saved"), "success");
            }
            setPrepaymentPosForm((prev) => ({
                ...prepaymentPosInitialForm(),
                paymentMethod: prev.paymentMethod,
                documentType: prepaymentPosMode === "DEPOSIT" ? "PLAIN_RECEIPT" : "COMMERCIAL_DOCUMENT",
            }));
            setSelectedPrepaymentProductId("");
            setImportoDisplay("");
        } catch (err) {
            handleAlert(prepaymentPosErrorMessage(err), err?.status === 409 ? "warning" : "error");
        } finally {
            setPrepaymentPosSubmitting(false);
        }
    }, [
        checkoutReservationEventId,
        handleAlert,
        isPrepaymentPosActive,
        prepaymentPosErrorMessage,
        prepaymentPosForm,
        prepaymentPosMode,
        prepaymentPosSubmitting,
        refreshCheckoutPrepaymentSummary,
        selectedPrepaymentProduct,
        tenant,
        token,
        t,
    ]);

    const getFiscalDepartmentByVatRate = useCallback((vatRate) => {
        const roundedVatRate = Math.round(Number(vatRate || 0) * 100) / 100;
        const normalizedKey = Number.isInteger(roundedVatRate)
            ? String(roundedVatRate)
            : String(roundedVatRate);
        const department = FISCAL_DEPARTMENT_BY_VAT_RATE[normalizedKey];

        if (!department) {
            throw new Error(`Aliquota IVA ${roundedVatRate}% non associata a un reparto fiscale Epson.`);
        }

        return department;
    }, []);

    const buildFiscalRowsForCheckout = useCallback((rows, fallbackVatRate) => {
        const defaultVatRate = normalizeVatRate(fallbackVatRate) ?? 10;
        const fiscalRows = [];

        rows.forEach((row) => {
            const quantity = Number(row?.quantity || 0);
            if (quantity <= 0) return;

            const rowUnitPrice = Number(row?.unitPrice ?? row?.product?.price ?? row?.package?.price ?? 0);

            if (row?.isPackage) {
                const packageName = row?.package?.name || row?.product?.name || row?.name || "Pacchetto";
                const packageItems = Array.isArray(row?.package?.items) && row.package.items.length > 0
                    ? row.package.items
                    : [];

                if (packageItems.length > 0) {
                    const itemBases = packageItems.map((item) => {
                        const itemQuantity = Number(item?.quantity || 1);
                        const itemUnitPrice = Number(item?.product?.price ?? item?.unitPrice ?? 0);
                        const vatRate = getProductVatRate(item?.product, defaultVatRate);
                        return {
                            item,
                            itemQuantity,
                            itemUnitPrice,
                            vatRate,
                            baseTotal: itemQuantity * itemUnitPrice,
                        };
                    });

                    const distinctVatRates = [...new Set(itemBases.map((item) => String(item.vatRate)))];
                    const baseTotal = itemBases.reduce((sum, item) => sum + item.baseTotal, 0);

                    if (distinctVatRates.length === 1) {
                        const vatRate = Number(distinctVatRates[0]);
                        fiscalRows.push({
                            description: packageName,
                            quantity,
                            unitPrice: rowUnitPrice,
                            vatRate,
                            department: getFiscalDepartmentByVatRate(vatRate),
                        });
                    } else {
                        let assignedPackageUnitTotal = 0;
                        itemBases.forEach(({ item, itemQuantity, vatRate, baseTotal: itemBaseTotal }, index) => {
                            const ratio = baseTotal > 0 ? itemBaseTotal / baseTotal : 1 / itemBases.length;
                            const allocatedPackageUnitTotal = index === itemBases.length - 1
                                ? Number((rowUnitPrice - assignedPackageUnitTotal).toFixed(2))
                                : Number((rowUnitPrice * ratio).toFixed(2));
                            assignedPackageUnitTotal += allocatedPackageUnitTotal;
                            const allocatedUnitPrice = itemQuantity > 0
                                ? Number((allocatedPackageUnitTotal / itemQuantity).toFixed(4))
                                : allocatedPackageUnitTotal;
                            if (allocatedUnitPrice <= 0) return;

                            fiscalRows.push({
                                description: `${packageName} - ${item?.product?.name || item?.productName || "Articolo"}`,
                                quantity: quantity * itemQuantity,
                                unitPrice: allocatedUnitPrice / itemQuantity,
                                vatRate,
                                department: getFiscalDepartmentByVatRate(vatRate),
                            });
                        });
                    }
                    return;
                }
            }

            const product = row?.product || {};
            const description = getFiscalItemDescription(row);
            const vatRate = getProductVatRate(product, defaultVatRate);

            fiscalRows.push({
                description,
                quantity,
                unitPrice: rowUnitPrice,
                vatRate,
                department: getFiscalDepartmentByVatRate(vatRate),
            });
        });

        return fiscalRows.filter((row) => row.quantity > 0 && row.unitPrice >= 0);
    }, [getFiscalDepartmentByVatRate, getProductVatRate, normalizeVatRate]);

    const getOrderItemsSummary = useCallback((ord) => {
        const rows = [];

        (Array.isArray(ord?.orderProducts) ? ord.orderProducts : []).forEach((row) => {
            const variants = Array.isArray(row?.orderProductVariants)
                ? row.orderProductVariants.map((entry) => entry?.variant?.name).filter(Boolean)
                : [];
            const variantSuffix = variants.length ? ` + ${variants.join(" + ")}` : "";
            const quantity = Number(row?.quantity || 0);
            rows.push(`${row?.product?.name || row?.productNameSnapshot || t("newproduct")}${variantSuffix} x${quantity || 1}`);
        });

        (Array.isArray(ord?.orderPackages) ? ord.orderPackages : []).forEach((row) => {
            const quantity = Number(row?.quantity || 0);
            rows.push(`${row?.package?.name || t("orderPackages.label")} x${quantity || 1}`);
        });

        (Array.isArray(ord?.orderFixedPriceMenus) ? ord.orderFixedPriceMenus : []).forEach((row) => {
            const quantity = Number(row?.quantity || 0);
            rows.push(`${row?.menuNameSnapshot || t("fixedPriceMenu.menuFallback")} x${quantity || 1}`);
        });

        if (rows.length === 0) return `#${ord?.id || "-"}`;
        const visible = rows.slice(0, 3).join(", ");
        return rows.length > 3 ? `${visible} +${rows.length - 3}` : visible;
    }, [t]);

    const multipleCheckoutOrders = useMemo(() => {
        const tableById = new Map((table || []).map((tab) => [Number(tab.id), tab]));

        return (order || [])
            .filter((ord) => !["PAID", "CANCELLED"].includes(ord?.status))
            .map((ord) => {
                const linkedTable = ord?.table || tableById.get(Number(ord?.tableId));
                const total = calcolaTotaleOrdine(ord);
                return {
                    ...ord,
                    checkoutTotal: total,
                    checkoutTableName: linkedTable?.name || (ord?.tableId ? `Tavolo ${ord.tableId}` : t("checkout_page.counterSale")),
                    checkoutRoomLabel: linkedTable?.roomId ? `Sala ${linkedTable.roomId}` : "-",
                    checkoutItemsSummary: getOrderItemsSummary(ord),
                };
            })
            .filter((ord) => ord.checkoutTotal > 0)
            .sort((a, b) => {
                const roomDiff = Number(a?.table?.roomId ?? a?.roomId ?? 0) - Number(b?.table?.roomId ?? b?.roomId ?? 0);
                if (roomDiff !== 0) return roomDiff;
                const tableDiff = String(a.checkoutTableName).localeCompare(String(b.checkoutTableName));
                if (tableDiff !== 0) return tableDiff;
                return Number(a.id) - Number(b.id);
            });
    }, [order, table, calcolaTotaleOrdine, getOrderItemsSummary, t]);

    const selectedMultipleCheckoutOrders = useMemo(() => (
        multipleCheckoutOrders.filter((ord) => multipleCheckoutOrderIds.has(Number(ord.id)))
    ), [multipleCheckoutOrders, multipleCheckoutOrderIds]);

    const multipleCheckoutTotal = useMemo(() => (
        Number(selectedMultipleCheckoutOrders.reduce((sum, ord) => sum + Number(ord.checkoutTotal || 0), 0).toFixed(2))
    ), [selectedMultipleCheckoutOrders]);

    const calcolaTotaleSconto = (array) => {
        let totale = 0.00
        array?.forEach((elem) => {
            const discount = Number(elem.discount ?? 0);
            const quantity = Number(elem.quantity ?? 0);
            totale += (discount * quantity);
        });
        return totale.toFixed(2);
    }

    const liberaTavolo = async (tavolo, statoTavolo) => {
        try {
            if (!["LIBERO", "IN_ATTESA", "OCCUPATO", "RISERVATO", "NON_DISPONIBILE"].includes(statoTavolo))
                throw new Error("Stato Tavolo invalido. StatoTavolo= ");
            await aggiornaTavolo(tavolo.id, { status: statoTavolo, takenSeats: 0 }, token, tenant);
            if (statoTavolo === "LIBERO") {
                await releaseTableSession(tavolo.id, token, tenant).catch(() => {});
            }
        } catch (err) {
            handleAlert((err + statoTavolo), "error")
            throw err;
        }
    }

    const pagaOrdini = async (ordini, paymentRows = [], heliteGuest = null) => {
        try {
            const orderIdsToPay = [...new Set((ordini || [])
                .map((ordine) => Number(ordine.orderId))
                .filter((orderId) => Number.isInteger(orderId) && orderId > 0))];
            const orderTotals = orderIdsToPay.map((orderId) => {
                const rows = (ordini || []).filter((ordine) => Number(ordine.orderId) === orderId);
                return {
                    orderId,
                    total: normalizeAmountInput(calcolaTotale(rows)),
                };
            }).filter((item) => item.total > 0);

            const payableOrderIds = orderTotals
                .map((item) => item.orderId)
                .filter((orderId) => {
                    const currentOrder = (order || []).find((ord) => Number(ord.id) === orderId);
                    return currentOrder && !["PAID", "CANCELLED"].includes(currentOrder.status);
                });

            if (payableOrderIds.length === 0) return;

            const receiptPayments = (paymentRows || [])
                .map((row) => ({
                    source: row,
                    method: RECEIPT_METHOD_BY_UI_METHOD[row.method] || "CARD",
                    ...(isCardUiMethod(row.method) && row.cardCircuit ? { cardCircuit: row.cardCircuit } : {}),
                    amount: Number(normalizeAmountInput(row.amount).toFixed(2)),
                    isFiscal: isFiscalPaymentRow(row),
                    note: isManualPmsBuonoMethod(row.method) ? MANUAL_PMS_BUONO_NOTE : "",
                }))
                .filter((payment) => payment.amount > 0);

            const canCloseWithoutNewPayment = isSaleCongressiTenant
                && !isMultipleCheckout
                && !isPartialCheckoutSelection
                && checkoutAmountDue <= 0.009
                && checkoutAppliedPrepaymentTotal > 0.009;

            if (receiptPayments.length === 0 && !canCloseWithoutNewPayment) {
                throw new Error("Nessun pagamento valido da registrare.");
            }

            const heliteCheckoutData = heliteGuest ? {
                billId: getHeliteBillId(heliteGuest),
                heliteBillId: getHeliteBillId(heliteGuest),
                ...(getHeliteRoomNumber(heliteGuest) ? {
                    heliteRoomNumber: getHeliteRoomNumber(heliteGuest),
                    roomNumber: getHeliteRoomNumber(heliteGuest),
                } : {}),
                ...(heliteGuest?.holderName ? { heliteHolderName: heliteGuest.holderName, holderName: heliteGuest.holderName } : {}),
                pax: Number(selectedTable?.takenSeats || heliteGuest?.guests || 1),
                desc: t("heliteGuestPicker.restaurantChargeDescription", {
                    room: formatHeliteGuestLabel(heliteGuest),
                    table: "",
                }),
                date: formatDateYyyyMmDd(),
                ...(getGuestBoardBasis(heliteGuest) ? { boardBasis: getGuestBoardBasis(heliteGuest) } : {}),
            } : {};

            if (payableOrderIds.length > 1) {
                if (receiptPayments.length === 0) {
                    throw new Error("Nessun pagamento valido da registrare.");
                }
                const result = await checkoutMultipleOrders({
                    orderIds: payableOrderIds,
                    payments: receiptPayments.map((payment) => ({
                        method: payment.method,
                        ...(payment.cardCircuit ? { cardCircuit: payment.cardCircuit } : {}),
                        amount: payment.amount,
                        isFiscal: payment.isFiscal,
                        note: payment.note || (heliteGuest ? formatHeliteGuestLabel(heliteGuest) : ""),
                    })),
                    ...heliteCheckoutData,
                }, token, tenant);
                return {
                    heliteBuoniQueued: enqueueHeliteBuoniFromResult(result) > 0,
                    genericVouchersQueued: enqueueGenericVouchersFromResult(result) > 0,
                    documents: result?.documents || [],
                    paidTotal: getCheckoutPaidTotal(result, checkoutAmountDue),
                };
            }

            const orderId = payableOrderIds[0];
            const currentOrder = (order || []).find((ord) => Number(ord.id) === orderId);
            const result = await checkoutOrderReceipt({
                orderId,
                discount: Number(currentOrder?.totalDiscount || 0),
                discountAmount: Math.max(0, Number(currentOrder?.discountAmount || 0)),
                surchargeAmount: Math.max(0, Number(currentOrder?.surchargeAmount || 0)),
                payments: receiptPayments.map((payment, index) => {
                    const base = {
                        method: payment.method,
                        ...(payment.cardCircuit ? { cardCircuit: payment.cardCircuit } : {}),
                        amount: payment.amount,
                        isFiscal: payment.isFiscal,
                        note: payment.note || (heliteGuest ? formatHeliteGuestLabel(heliteGuest) : ""),
                    };

                    if (payment.source?.method === "helite_room_charge" && heliteGuest) {
                        return {
                            ...base,
                            ...buildHeliteChargePayload({
                                orderId,
                                amount: payment.amount,
                                guest: heliteGuest,
                                suffix: `ROOM_CHARGE-P${index + 1}`,
                            }),
                        };
                    }

                    return base;
                }),
                ...heliteCheckoutData,
            }, token, tenant);
            return {
                heliteBuoniQueued: enqueueHeliteBuoniFromResult(result) > 0,
                genericVouchersQueued: enqueueGenericVouchersFromResult(result) > 0,
                documents: result?.documents || [],
                paidTotal: getCheckoutPaidTotal(result, checkoutAmountDue),
            };
        } catch (err) {
            throw err;
        }
    }

    const pagaRigheSelezionate = async (paymentRows = [], heliteGuest = null) => {
        if (!isPartialCheckoutSelection) {
            const result = await pagaOrdini(ordiniFiltrati, paymentRows, heliteGuest);
            return {
                partial: false,
                heliteBuoniQueued: Boolean(result?.heliteBuoniQueued),
                genericVouchersQueued: Boolean(result?.genericVouchersQueued),
                documents: result?.documents || [],
                paidTotal: result?.paidTotal,
            };
        }

        const allocationsByOrder = new Map();
        const addAllocation = (row, quantity) => {
            const orderId = Number(row?.orderId);
            const lineId = Number(row?.id);
            const qty = Number(quantity || 0);
            if (!Number.isInteger(orderId) || orderId <= 0 || !Number.isInteger(lineId) || lineId <= 0 || qty <= 0) {
                return;
            }

            const type = row?.isFixedPriceMenu ? "fixed-menu" : row?.isPackage ? "package" : "product";
            if (!allocationsByOrder.has(orderId)) allocationsByOrder.set(orderId, new Map());
            const orderAllocations = allocationsByOrder.get(orderId);
            const key = `${type}:${lineId}`;
            const current = orderAllocations.get(key) || { type, lineId, quantity: 0 };
            current.quantity += qty;
            orderAllocations.set(key, current);
        };

        checkoutPayableRows.forEach((row) => {
            const sourceRows = ordiniDivisi
                ? [row]
                : (Array.isArray(row?.originalOrders) && row.originalOrders.length > 0
                    ? row.originalOrders
                    : [row]);
            sourceRows.forEach((sourceRow) => addAllocation(sourceRow, sourceRow?.quantity));
        });

        if (allocationsByOrder.size === 0) {
            throw new Error("Nessun articolo valido selezionato per il pagamento");
        }

        const buildPartialCheckoutPayments = (plan) => {
            const planTotal = Number(plan?.selectedTotal || 0);
            const partialSuffix = [
                ...((plan?.selectedProducts || []).map(({ row, quantity }) => `P${row.id}x${quantity}`)),
                ...((plan?.selectedPackages || []).map(({ row, quantity }) => `K${row.id}x${quantity}`)),
                ...((plan?.selectedFixedMenus || []).map(({ row, quantity }) => `M${row.id}x${quantity}`)),
            ].join("_") || "PARTIAL";
            const total = Number(checkoutTotalValue || 0);
            const sourceRows = Array.isArray(paymentRows) && paymentRows.length > 0
                ? paymentRows
                : [{ method: paymentMethode || "cash", amount: planTotal }];
            const ratio = total > 0 ? planTotal / total : 1;
            let assigned = 0;

            return sourceRows
                .map((row, index) => {
                    const sourceAmount = normalizeAmountInput(row.amount);
                    const amount = index === sourceRows.length - 1
                        ? Number((planTotal - assigned).toFixed(2))
                        : Number((sourceAmount * ratio).toFixed(2));
                    assigned += amount;

                    const payment = {
                        method: RECEIPT_METHOD_BY_UI_METHOD[row.method] || "CARD",
                        ...(isCardUiMethod(row.method) && row.cardCircuit ? { cardCircuit: row.cardCircuit } : {}),
                        amount,
                        isFiscal: isFiscalPaymentRow(row),
                        note: isManualPmsBuonoMethod(row.method) ? MANUAL_PMS_BUONO_NOTE : "",
                    };

                    if (row.method === "helite_room_charge") {
                        return {
                            ...payment,
                            ...buildHeliteChargePayload({
                                orderId: plan.order.id,
                                amount,
                                guest: heliteGuest,
                                sourceType: "ORDER_PARTIAL",
                                suffix: `${partialSuffix}-${index + 1}`,
                            }),
                        };
                    }

                    return payment;
                })
                .filter((row) => row.amount > 0);
        };

        const plans = [];
        for (const [orderId, allocations] of allocationsByOrder.entries()) {
            const latestOrder = await getOrdineById(orderId, token, tenant)
                .catch(() => (order || []).find((item) => Number(item?.id) === orderId));
            if (!latestOrder) throw new Error(`Ordine ${orderId} non trovato`);

            const products = Array.isArray(latestOrder.orderProducts) ? latestOrder.orderProducts : [];
            const packages = Array.isArray(latestOrder.orderPackages) ? latestOrder.orderPackages : [];
            const fixedMenus = Array.isArray(latestOrder.orderFixedPriceMenus) ? latestOrder.orderFixedPriceMenus : [];

            const selectedProducts = products
                .map((row) => ({
                    row,
                    quantity: Math.min(
                        Number(row?.quantity || 0),
                        Number(allocations.get(`product:${Number(row?.id)}`)?.quantity || 0)
                    ),
                }))
                .filter((entry) => entry.quantity > 0);
            const selectedPackages = packages
                .map((row) => ({
                    row,
                    quantity: Math.min(
                        Number(row?.quantity || 0),
                        Number(allocations.get(`package:${Number(row?.id)}`)?.quantity || 0)
                    ),
                }))
                .filter((entry) => entry.quantity > 0);
            const selectedFixedMenus = fixedMenus
                .map((row) => ({
                    row,
                    quantity: Math.min(
                        Number(row?.quantity || 0),
                        Number(allocations.get(`fixed-menu:${Number(row?.id)}`)?.quantity || 0)
                    ),
                }))
                .filter((entry) => entry.quantity > 0);

            const remainingQuantity = [
                ...products.map((row) => (
                    Number(row?.quantity || 0)
                    - Number(allocations.get(`product:${Number(row?.id)}`)?.quantity || 0)
                )),
                ...packages.map((row) => (
                    Number(row?.quantity || 0)
                    - Number(allocations.get(`package:${Number(row?.id)}`)?.quantity || 0)
                )),
                ...fixedMenus.map((row) => (
                    Number(row?.quantity || 0)
                    - Number(allocations.get(`fixed-menu:${Number(row?.id)}`)?.quantity || 0)
                )),
            ].reduce((sum, quantity) => sum + Math.max(0, quantity), 0);
            if (selectedProducts.length === 0 && selectedPackages.length === 0 && selectedFixedMenus.length === 0) {
                throw new Error(`Nessun articolo pagabile trovato nell'ordine ${orderId}`);
            }

            const selectedTotal = Number((
                selectedProducts.reduce((sum, { row, quantity }) => (
                    sum + Number(row?.unitPrice ?? row?.product?.price ?? 0) * quantity
                ), 0)
                + selectedPackages.reduce((sum, { row, quantity }) => (
                    sum + Number(row?.unitPrice ?? row?.package?.price ?? row?.product?.price ?? 0) * quantity
                ), 0)
                + selectedFixedMenus.reduce((sum, { row, quantity }) => (
                    sum + Number(row?.finalPrice ?? row?.unitPrice ?? row?.product?.price ?? 0) * quantity
                ), 0)
            ).toFixed(2));

            plans.push({
                order: latestOrder,
                selectedProducts,
                selectedPackages,
                selectedFixedMenus,
                selectedTotal,
                payWholeOrder: remainingQuantity <= 0,
            });
        }

        let heliteBuoniQueued = false;
        let genericVouchersQueued = false;
        let paidTotal = 0;
        const documents = [];
        for (const plan of plans) {
            if (plan.payWholeOrder) {
                const result = await checkoutOrderReceipt({
                    orderId: Number(plan.order.id),
                    discount: Number(plan.order?.totalDiscount || 0),
                    discountAmount: Math.max(0, Number(plan.order?.discountAmount || 0)),
                    surchargeAmount: Math.max(0, Number(plan.order?.surchargeAmount || 0)),
                    payments: buildPartialCheckoutPayments(plan),
                    ...(heliteGuest ? {
                        billId: getHeliteBillId(heliteGuest),
                        heliteBillId: getHeliteBillId(heliteGuest),
                        ...(getHeliteRoomNumber(heliteGuest) ? {
                            heliteRoomNumber: getHeliteRoomNumber(heliteGuest),
                            roomNumber: getHeliteRoomNumber(heliteGuest),
                        } : {}),
                        ...(heliteGuest?.holderName ? { heliteHolderName: heliteGuest.holderName, holderName: heliteGuest.holderName } : {}),
                        pax: Number(selectedTable?.takenSeats || heliteGuest?.guests || 1),
                        desc: t("heliteGuestPicker.restaurantChargeDescription", {
                            room: formatHeliteGuestLabel(heliteGuest),
                            table: "",
                        }),
                        date: formatDateYyyyMmDd(),
                        ...(getGuestBoardBasis(heliteGuest) ? { boardBasis: getGuestBoardBasis(heliteGuest) } : {}),
                    } : {}),
                }, token, tenant);
                heliteBuoniQueued = enqueueHeliteBuoniFromResult(result) > 0 || heliteBuoniQueued;
                genericVouchersQueued = enqueueGenericVouchersFromResult(result) > 0 || genericVouchersQueued;
                paidTotal += getCheckoutPaidTotal(result, plan.selectedTotal);
                documents.push(...(result?.documents || []));
                continue;
            }

            const items = [
                ...plan.selectedProducts.map(({ row, quantity }) => ({
                    type: "product",
                    orderProductId: Number(row.id),
                    quantity,
                })),
                ...plan.selectedPackages.map(({ row, quantity }) => ({
                    type: "package",
                    orderPackageId: Number(row.id),
                    quantity,
                })),
                ...plan.selectedFixedMenus.map(({ row, quantity }) => ({
                    type: "fixedPriceMenu",
                    orderFixedPriceMenuId: Number(row.id),
                    quantity: Math.max(1, Math.trunc(Number(quantity) || 1)),
                })),
            ];

            const result = await partialCheckoutComanda(plan.order.id, {
                items,
                payments: buildPartialCheckoutPayments(plan),
                ...(heliteGuest ? {
                    billId: getHeliteBillId(heliteGuest),
                    heliteBillId: getHeliteBillId(heliteGuest),
                    ...(getHeliteRoomNumber(heliteGuest) ? {
                        heliteRoomNumber: getHeliteRoomNumber(heliteGuest),
                        roomNumber: getHeliteRoomNumber(heliteGuest),
                    } : {}),
                    ...(heliteGuest?.holderName ? { heliteHolderName: heliteGuest.holderName, holderName: heliteGuest.holderName } : {}),
                    pax: Number(selectedTable?.takenSeats || heliteGuest?.guests || 1),
                    desc: `Checkout parziale ordine ${plan.order.id}`,
                    ...(getGuestBoardBasis(heliteGuest) ? { boardBasis: getGuestBoardBasis(heliteGuest) } : {}),
                } : {}),
            }, token, tenant);
            heliteBuoniQueued = enqueueHeliteBuoniFromResult(result) > 0 || heliteBuoniQueued;
            genericVouchersQueued = enqueueGenericVouchersFromResult(result) > 0 || genericVouchersQueued;
            paidTotal += getCheckoutPaidTotal(result, plan.selectedTotal);
            documents.push(...(result?.documents || []));
        }

        setCheckboxSelectedIds(new Set());
        return {
            partial: true,
            heliteBuoniQueued,
            genericVouchersQueued,
            documents,
            paidTotal: roundMoney(paidTotal),
        };
    };

    const totaleFattura = calcolaTotale(ordiniFiltrati);

    //solo per Dev, verrÃ  rimossa, serve a mettere tutti i tavoli a LIBERO e gli ordini a PAID
    async function liberaTuttiTavoli() {
        try {
            let flag = 0
            const tablesToClear = (table || []).filter(elem => elem.status !== "LIBERO");
            await Promise.all(tablesToClear.map(async (elem) => {
                if (elem.status !== "LIBERO") {
                    await liberaTavolo(elem, "LIBERO")
                    flag++;
                }
            }));
            const ordersToPay = (order || []).filter((ord) => !["PAID", "CANCELLED"].includes(ord.status));
            await Promise.all(ordersToPay.map(async (ord) => {
                if (!["PAID", "CANCELLED"].includes(ord.status)) {
                    await aggiornaStatusComanda(ord.id, { status: "PAID" }, token, tenant);
                    flag++;
                }
            }));
            if (flag > 0) handleAlert(getTenantTranslation(t, tenantType, "allTablesCleared"), "success")

            if (flag < 1) handleAlert(getTenantTranslation(t, tenantType, "allTablesAlreadyFree"), "success")
        } catch (err) {
            handleAlert(err, "error")
        }
    }

    const selectTableAndGoToCheckout = useCallback((tableId) => {
        const targetTable = (table || []).find((tab) => Number(tab.id) === Number(tableId));
        if (!targetTable) {
            handleAlert(getTenantTranslation(t, tenantType, "No_table_selected"), "warning");
            return;
        }

        setOpenPendingList(false);
        setOpenOccupiedList(false);
        setOpenLiberaTavolo(false);
        setOrdiniDivisi(false);
        setSplitTargetRowIds(null);
        setCheckboxSelectedIds(new Set());
        hasAppliedNavigationTable.current = false;
        navigate(`/punto_cassa?tableId=${Number(tableId)}`, {
            replace: true,
            state: { checkoutTableId: Number(tableId) }
        });
    }, [table, t, tenantType, handleAlert, navigate]);

    const openMultipleCheckoutDialog = useCallback(() => {
        if (multipleCheckoutOrders.length < 2) {
            handleAlert("Servono almeno due ordini aperti per il checkout multiplo", "warning");
            return;
        }

        setMultipleCheckoutOrderIds(new Set());
        setOpenMultipleCheckout(true);
    }, [multipleCheckoutOrders.length, handleAlert]);

    const handleFiscalDailyClosure = useCallback(async () => {
        if (fiscalDailyClosureSubmitting) return;

        setFiscalDailyClosureError("");
        setFiscalDailyClosureSubmitting(true);
        try {
            const result = await fiscalDailyClosure(token, tenant, fiscalClosureTarget === "all"
                ? { all: true }
                : fiscalClosureTarget === "default"
                    ? {}
                    : { fiscalDeviceId: Number(fiscalClosureTarget) });
            handleAlert(result?.message || "Chiusura Giornaliera Cassa Fiscale eseguita", "success");
            setOpenFiscalDailyClosureConfirm(false);
        } catch (err) {
            const message = toDisplayMessage(err, "Errore chiusura giornaliera cassa fiscale");
            setFiscalDailyClosureError(message);
            handleAlert(message, "error");
        } finally {
            setFiscalDailyClosureSubmitting(false);
        }
    }, [fiscalClosureTarget, fiscalDailyClosureSubmitting, handleAlert, tenant, token]);

    const toggleMultipleCheckoutOrder = useCallback((orderId) => {
        const normalizedId = Number(orderId);
        setMultipleCheckoutOrderIds((prev) => {
            const next = new Set(prev);
            if (next.has(normalizedId)) next.delete(normalizedId);
            else next.add(normalizedId);
            return next;
        });
    }, []);

    const bringMultipleCheckoutToOrder = useCallback(() => {
        if (selectedMultipleCheckoutOrders.length < 2) {
            handleAlert(t("checkout_page.multipleCheckoutMinOrders"), "warning");
            return;
        }
        if (multipleCheckoutTotal <= 0) {
            handleAlert(t("checkout_page.invalidCheckoutTotal"), "warning");
            return;
        }

        setCombinedCheckoutOrders(selectedMultipleCheckoutOrders);
        setSelectedTable({
            id: MULTIPLE_CHECKOUT_ID,
            name: MULTIPLE_CHECKOUT_LABEL,
            status: "IN_ATTESA",
            isMultipleCheckout: true,
        });
        setPaidReservationLabel(t("checkout_page.multipleCheckoutOrderCount", { count: selectedMultipleCheckoutOrders.length }));
        setSelectedTableReservation(t("checkout_page.multipleCheckoutOrderCount", { count: selectedMultipleCheckoutOrders.length }));
        setOpenMultipleCheckout(false);
        setMultipleCheckoutOrderIds(new Set());
        setOrdiniDivisi(false);
        setSplitTargetRowIds(null);
        setCheckboxSelectedIds(new Set());
        navigate("/punto_cassa", { replace: true, state: {} });
    }, [selectedMultipleCheckoutOrders, multipleCheckoutTotal, handleAlert, navigate, t]);

    const moveOccupiedTableToPending = useCallback(async (tableId) => {
        try {
            await aggiornaTavolo(Number(tableId), { status: "IN_ATTESA" }, token, tenant);

            await fetchAllData();

            const updatedTable = (table || []).find(
                (tab) => Number(tab.id) === Number(tableId)
            );

            if (updatedTable) {
                setSelectedTable({
                    ...updatedTable,
                    status: "IN_ATTESA",
                });
            }

            setOpenOccupiedList(false);
            setOpenPendingList(true);

            handleAlert(
                getTenantTranslation(t, tenantType, "checkout_page.pending"),
                "success"
            );
        } catch (err) {
            handleAlert(err?.message || String(err), "error");
        }
    }, [token, tenant, table, fetchAllData, t, tenantType, handleAlert]);

    const handleCloseCheckoutSuccessModal = useCallback(() => {
        setOpenCloseCheckout(false);
        if (keepTableAfterCheckoutModal && selectedTable?.id && !isCounterCheckout && !isMultipleCheckout) {
            setKeepTableAfterCheckoutModal(false);
            setOrdiniDivisi(false);
            setSplitTargetRowIds(null);
            setCheckboxSelectedIds(new Set());
            navigate(`/punto_cassa?tableId=${Number(selectedTable.id)}`, {
                replace: true,
                state: { checkoutTableId: Number(selectedTable.id) }
            });
            return;
        }

        setKeepTableAfterCheckoutModal(false);
        setSelectedTable("");
        setSelectedTableReservation(null);
        setPaidReservationLabel(null);
        navigate("/punto_cassa", { replace: true, state: {} });
    }, [keepTableAfterCheckoutModal, selectedTable?.id, isCounterCheckout, isMultipleCheckout, navigate]);

    function toPositiveIntOrUndefined(value) {
        if (value === null || value === undefined || value === "") return undefined;
        const parsed = Number(value);
        if (!Number.isInteger(parsed) || parsed <= 0) return undefined;
        return parsed;
    }

    const buildHelitePaymentRows = useCallback(() => ([{
        id: Date.now(),
        method: "helite_room_charge",
        amount: Number(checkoutAmountDue || 0).toFixed(2),
        given: "",
    }]), [checkoutAmountDue]);

    const loadHeliteGuestsForSelection = useCallback(async () => {
        setHeliteGuestsLoading(true);
        setSelectedHeliteGuestId("");
        setHeliteGuestSearch("");

        try {
            const payload = await getHeliteCheckedInGuests(token, tenant, { activeOnly: true });
            const guests = Array.isArray(payload?.guests) ? payload.guests : [];
            setHeliteGuests(guests);
            setSelectedHeliteGuestId(guests[0]?.id ? String(guests[0].id) : "");
        } catch (err) {
            setHeliteGuests([]);
            handleAlert(err?.message || t("heliteGuestPicker.loadError"), "error");
        } finally {
            setHeliteGuestsLoading(false);
        }
    }, [token, tenant, handleAlert, t]);

    async function handleConfirmCheckoutPayment() {
        if (checkoutPaymentSubmitting) return;
        if (!areCardCircuitsValid) {
            handleAlert("Seleziona il tipo di carta", "warning");
            return;
        }
        setCheckoutPaymentSubmitting(true);
        try {
            const manualPmsRows = closeCheckoutPayments.filter((row) => isManualPmsBuonoMethod(row.method));
            if (manualPmsRows.length > 0) {
                const manualPmsTotal = manualPmsRows.reduce(
                    (sum, row) => sum + normalizeAmountInput(row.amount),
                    0
                );
                if (
                    isPartialCheckoutSelection
                    || closeCheckoutPayments.length !== 1
                    || Math.abs(manualPmsTotal - checkoutAmountDue) >= 0.01
                ) {
                    handleAlert("Il buono PMS manuale deve chiudere l'intero conto da solo.", "warning");
                    return;
                }
            }
            const hasHelitePayment = closeCheckoutPayments.some((row) => row.method === "helite_room_charge");
            if (!hasHelitePayment) {
                await confirmPayment(undefined, closeCheckoutPayments);
                return;
            }

            if (checkoutAmountDue <= 0) {
                handleAlert(t("heliteGuestPicker.invalidCheckoutTotal"), "warning");
                return;
            }

            if (currentReservationHeliteGuest) {
                await confirmPayment("helite_room_charge", closeCheckoutPayments, currentReservationHeliteGuest);
                return;
            }

            setPendingHelitePaymentRows(closeCheckoutPayments);
            setOpenHeliteGuestDialog(true);
            await loadHeliteGuestsForSelection();
        } finally {
            setCheckoutPaymentSubmitting(false);
        }
    }

    async function handleConfirmHeliteGuestSelection() {
        if (checkoutPaymentSubmitting) return;
        if (!selectedHeliteGuest) {
            handleAlert(t("heliteGuestPicker.selectRequired"), "warning");
            return;
        }

        setCheckoutPaymentSubmitting(true);
        try {
            const paymentRows = pendingHelitePaymentRows || buildHelitePaymentRows();
            setOpenHeliteGuestDialog(false);
            setPendingHelitePaymentRows(null);
            await confirmPayment("helite_room_charge", paymentRows, selectedHeliteGuest);
        } finally {
            setCheckoutPaymentSubmitting(false);
        }
    }

    const getPrepaymentCheckoutErrorMessage = useCallback((err) => {
        if (err?.code === "ADVANCE_EXCEEDS_COMMERCIAL_VAT_TOTAL") {
            return t("checkout_page.prepaymentCommercialVatBlocked");
        }
        if (err?.code === "APPLICATIONS_EXCEED_ORDER_TOTAL") {
            return t("checkout_page.prepaymentExceedsOrder");
        }
        if (err?.code === "ORDER_NOT_APPLICABLE") {
            return t("checkout_page.prepaymentOrderNotApplicable");
        }
        if (err?.code === "SOURCE_MOVEMENT_NOT_FOUND") {
            return t("checkout_page.prepaymentSourceMissing");
        }
        if (err?.code === "SOURCE_NOT_RECEIPT") {
            return t("checkout_page.prepaymentSourceInvalid");
        }
        if (err?.code === "ADVANCE_ALREADY_APPLIED") {
            return t("checkout_page.prepaymentAdvanceAlreadyApplied");
        }
        if (err?.code === "APPLICATION_EXCEEDS_AVAILABLE_AMOUNT") {
            return t("checkout_page.prepaymentApplicationExceedsAvailable");
        }
        if (err?.code === "ADVANCE_MUST_BE_APPLIED_IN_FULL") {
            return t("checkout_page.prepaymentAdvanceMustApplyInFull");
        }
        if (err?.code === "INVALID_FINAL_INVOICE") {
            return t("checkout_page.prepaymentInvalidFinalInvoice");
        }
        if (err?.code === "FINAL_INVOICE_ALREADY_SENT") {
            return t("checkout_page.prepaymentFinalInvoiceAlreadySent");
        }
        if (err?.code === "INVOICE_NOT_FOUND") {
            return t("checkout_page.prepaymentInvoiceNotFound");
        }
        if (err?.code === "ADVANCE_EXCEEDS_INVOICE_VAT_TOTAL") {
            return t("checkout_page.prepaymentInvoiceVatBlocked");
        }
        return err?.message || t("checkout_page.prepaymentApplyError");
    }, [t]);

    const applyCheckoutPrepayments = useCallback(async ({ invoiceId = null } = {}) => {
        if (!checkoutReservationEventId || checkoutPrepaymentApplications.length === 0) return null;
        const orderId = checkoutOrderIdsForPayment[0];
        if (!Number.isInteger(Number(orderId)) || Number(orderId) <= 0) return null;

        const applicationsKey = checkoutPrepaymentApplications
            .map(({ movementId, amount }) => `${movementId}-${Math.round(Number(amount || 0) * 100)}`)
            .join("_");
        const idempotencyKey = [
            "final",
            checkoutReservationEventId,
            orderId,
            invoiceId || "comm",
            applicationsKey,
        ].join(":").slice(0, 80);
        const payload = {
            orderId: Number(orderId),
            ...(invoiceId ? { invoiceId: Number(invoiceId) } : {}),
            applications: checkoutPrepaymentApplications,
            note: t("checkout_page.prepaymentApplyNote"),
            idempotencyKey,
        };

        try {
            const result = await applyReservationEventPrepayments(checkoutReservationEventId, payload, token, tenant);
            return result;
        } catch (err) {
            if (
                invoiceId
                && err?.code === "ADVANCE_EXCEEDS_INVOICE_VAT_TOTAL"
                && err?.details?.canProceed
                && window.confirm(t("checkout_page.prepaymentInvoiceVatWarning"))
            ) {
                const result = await applyReservationEventPrepayments(
                    checkoutReservationEventId,
                    { ...payload, acceptAdvanceVatWarning: true },
                    token,
                    tenant
                );
                return result;
            }
            throw new Error(getPrepaymentCheckoutErrorMessage(err));
        }
    }, [
        checkoutOrderIdsForPayment,
        checkoutPrepaymentApplications,
        checkoutReservationEventId,
        getPrepaymentCheckoutErrorMessage,
        tenant,
        token,
        t,
    ]);

    async function confirmPayment(method = paymentMethode, paymentRows = null, heliteGuest = null) {
        const baseRows = Array.isArray(paymentRows)
            ? paymentRows
            : (checkoutAmountDue > 0.009
                ? [{ method: method || paymentMethode || "cash", amount: checkoutAmountDue }]
                : []);
        const normalizedRows = (() => {
            const rows = [];
            let heliteRow = null;

            for (const row of baseRows) {
                if (row?.method !== "helite_room_charge") {
                    rows.push(row);
                    continue;
                }

                const amount = normalizeAmountInput(row.amount);
                if (!heliteRow) {
                    heliteRow = { ...row, amount };
                    rows.push(heliteRow);
                    continue;
                }

                heliteRow.amount = Number((normalizeAmountInput(heliteRow.amount) + amount).toFixed(2));
            }

            return rows;
        })();
        const selectedPaymentMethod = normalizedRows[0]?.method || method || paymentMethode || "cash";
        const hasGenericVoucherPayment = normalizedRows.some((row) => isGenericVoucherUiMethod(row.method));
        if (hasGenericVoucherPayment) {
            if (normalizedRows.length !== 1) {
                handleAlert("Buono PMS manuale disponibile solo come pagamento unico.", "warning");
                return;
            }
            if (isMultipleCheckout || isPartialCheckoutSelection) {
                handleAlert("Buono PMS manuale disponibile solo per chiusura conto completa da tavolo.", "warning");
                return;
            }
            if (checkoutAppliedPrepaymentTotal > 0.009) {
                handleAlert("Buono PMS manuale non disponibile con acconti gia applicati.", "warning");
                return;
            }
        }
        const heliteGuestLabel = heliteGuest ? formatHeliteGuestLabel(heliteGuest) : "";
        const cashGiven = normalizedRows.reduce((sum, row) => (
            isCashMethod(row.method) ? sum + normalizeAmountInput(row.given) : sum
        ), 0);
        const cashAmount = normalizedRows.reduce((sum, row) => (
            isCashMethod(row.method) ? sum + normalizeAmountInput(row.amount) : sum
        ), 0);
        const snapshotTotal = checkoutAmountDue;
        const methodsLabel = normalizedRows.length > 1
            ? "Pagamento misto"
            : checkoutAmountDue <= 0.009
                ? t("checkout_page.prepaymentsCovered")
                : isCardUiMethod(selectedPaymentMethod)
                    ? `${getPaymentMethodLabel(selectedPaymentMethod, t)} - ${getCardCircuitLabel(normalizedRows[0]?.cardCircuit)}`
                    : getPaymentMethodLabel(selectedPaymentMethod, t);
        setLastPaymentSummary({
            total: snapshotTotal,
            methodLabel: methodsLabel,
            cashGiven: Number(cashGiven.toFixed(2)),
            cashChange: Number(Math.max(0, cashGiven - cashAmount).toFixed(2)),
            hasCash: cashAmount > 0,
            heliteGuest,
            heliteGuestLabel,
        });
        setPaymentMethode(selectedPaymentMethod);
        const checkoutOrderIds = [...new Set(checkoutPayableRows
            .map((row) => Number(row?.orderId))
            .filter((orderId) => Number.isInteger(orderId) && orderId > 0))];
        const checkoutOrders = (order || []).filter((item) =>
            checkoutOrderIds.includes(Number(item?.id))
        );
        const latestNonReservationOrderIds = getNonReservationOrderIds(tenant);
        const latestNonReservationTableMarkers = getNonReservationTableMarkers(tenant);
        const selectedReservationMarker = selectedTableTodayReservation?.id
            ? `${Number(selectedTable?.id)}:${Number(selectedTableTodayReservation.id)}`
            : null;
        const isNonReservationCheckout =
            (selectedReservationMarker && latestNonReservationTableMarkers.has(selectedReservationMarker))
            || checkoutOrderIds.some((orderId) => latestNonReservationOrderIds.has(orderId));
        const reservationClientId = Number(
            selectedTableTodayReservation?.clientId
            ?? selectedTableTodayReservation?.client?.id
        );
        const hasReservationClientOrder = Number.isInteger(reservationClientId)
            && checkoutOrders.some((item) => Number(item?.clientId) === reservationClientId);
        const paidReservation = isNonReservationCheckout
            ? null
            : activeCheckoutReservation
                || (hasReservationClientOrder ? selectedTableTodayReservation : null);
        const reservationToKeepConfirmed = isNonReservationCheckout
            ? selectedTableTodayReservation
            : null;
        const recipientLabel = getReservationRecipientLabel(paidReservation);
        if (!isMultipleCheckout) setPaidReservationLabel(recipientLabel || null);
        if (recipientLabel) setSelectedTableReservation(recipientLabel);

        let appliedPrepaymentResult = null;
        let heliteBuoniQueued = false;
        let genericVouchersQueued = false;
        let checkoutDocuments = [];
        let effectivePaidTotal = snapshotTotal;
        try {
            let partialCheckoutCompleted = false;
            if (isMultipleCheckout) {
                const orderIds = [...new Set((combinedCheckoutOrders || [])
                    .map((ordine) => Number(ordine.id))
                    .filter((orderId) => Number.isInteger(orderId) && orderId > 0))];
                const total = Number(calcolaTotale(ordiniFiltrati));

                if (orderIds.length < 2) {
                    handleAlert("Seleziona almeno due ordini da accorpare", "warning");
                    return;
                }

                const result = await checkoutMultipleOrders({
                    orderIds,
                    payments: normalizedRows.map((row) => ({
                        method: RECEIPT_METHOD_BY_UI_METHOD[row.method] || "CARD",
                        ...(isCardUiMethod(row.method) && row.cardCircuit ? { cardCircuit: row.cardCircuit } : {}),
                        amount: Number(normalizeAmountInput(row.amount).toFixed(2)),
                        isFiscal: isFiscalPaymentRow(row),
                        note: isManualPmsBuonoMethod(row.method)
                            ? MANUAL_PMS_BUONO_NOTE
                            : (heliteGuestLabel || MULTIPLE_CHECKOUT_LABEL),
                    })),
                    ...(heliteGuest ? {
                        billId: getHeliteBillId(heliteGuest),
                        heliteBillId: getHeliteBillId(heliteGuest),
                        ...(getHeliteRoomNumber(heliteGuest) ? {
                            heliteRoomNumber: getHeliteRoomNumber(heliteGuest),
                            roomNumber: getHeliteRoomNumber(heliteGuest),
                        } : {}),
                        ...(heliteGuest?.holderName ? { heliteHolderName: heliteGuest.holderName, holderName: heliteGuest.holderName } : {}),
                        pax: Number(selectedTable?.takenSeats || 1),
                        desc: `Addebito ristorante ${heliteGuestLabel || MULTIPLE_CHECKOUT_LABEL}`,
                        ...(getGuestBoardBasis(heliteGuest) ? { boardBasis: getGuestBoardBasis(heliteGuest) } : {}),
                    } : {}),
                }, token, tenant);

                heliteBuoniQueued = enqueueHeliteBuoniFromResult(result) > 0 || heliteBuoniQueued;
                genericVouchersQueued = enqueueGenericVouchersFromResult(result) > 0 || genericVouchersQueued;
                checkoutDocuments = result?.documents || [];
                effectivePaidTotal = getCheckoutPaidTotal(result, total);
                setTotalPayment(effectivePaidTotal);
                setCombinedCheckoutOrders([]);
            } else {
                appliedPrepaymentResult = await applyCheckoutPrepayments();
                const paymentResult = await pagaRigheSelezionate(normalizedRows, heliteGuest);
                partialCheckoutCompleted = Boolean(paymentResult?.partial);
                heliteBuoniQueued = Boolean(paymentResult?.heliteBuoniQueued) || heliteBuoniQueued;
                genericVouchersQueued = Boolean(paymentResult?.genericVouchersQueued) || genericVouchersQueued;
                checkoutDocuments = paymentResult?.documents || [];
                effectivePaidTotal = Number.isFinite(Number(paymentResult?.paidTotal))
                    ? roundMoney(paymentResult.paidTotal)
                    : snapshotTotal;
            }
            setKeepTableAfterCheckoutModal(partialCheckoutCompleted);

            if (!partialCheckoutCompleted && reservationToKeepConfirmed?.id) {
                clearNonReservationMarkersForTable(tenant, selectedTable?.id, checkoutOrderIds);
            }

            if (!partialCheckoutCompleted && !isCounterCheckout && !isMultipleCheckout) {
                const nextTableStatus = reservationToKeepConfirmed ? "RISERVATO" : "LIBERO";
                await liberaTavolo(selectedTable, nextTableStatus)
                if (nextTableStatus === "LIBERO") {
                    removeTableFromCheckoutLists(selectedTable?.id);
                }
            }
            if (!partialCheckoutCompleted && paidReservation?.id) {
                try {
                    await aggiornaStatoPrenotazione(paidReservation.id, "COMPLETED", token, tenant);
                } catch (reservationErr) {
                    if (!isReservationCompletionBlockedError(reservationErr)) throw reservationErr;
                    handleAlert(
                        `Pagamento registrato. ${reservationErr?.message || reservationErr}`,
                        "warning"
                    );
                }
            } else if (!partialCheckoutCompleted && reservationToKeepConfirmed?.id) {
                await aggiornaStatoPrenotazione(reservationToKeepConfirmed.id, "CONFIRMED", token, tenant);
            }
            if (
                !partialCheckoutCompleted
                && isSaleCongressiTenant
                && checkoutReservationEventId
                && !isCounterCheckout
                && !isMultipleCheckout
            ) {
                await patchReservationEventStatus(
                    checkoutReservationEventId,
                    preserveReservationEventStatus || "COMPLETED",
                    token,
                    tenant
                );
            }
            window.dispatchEvent(new CustomEvent('comanda-creata'));
            window.dispatchEvent(new CustomEvent('map-data-changed'));
            await fetchAllData();

            let defaultFiscalVatRate = 10;
            try {
                const tenantSettings = await getTenantSettings(token, tenant);
                const parsedVatRate = normalizeVatRate(tenantSettings?.defaultVatRate);
                if (parsedVatRate !== undefined) defaultFiscalVatRate = parsedVatRate;
            } catch (settingsErr) {
                console.warn("Impossibile recuperare IVA predefinita per lo scontrino fiscale:", settingsErr);
            }

            const shouldPrintFiscalReceipt = normalizedRows.some(isFiscalPaymentRow);
            const fiscalRows = buildFiscalRowsForCheckout(checkoutPayableRows, defaultFiscalVatRate);
            if (checkoutOrderAdjustmentTotals.surcharge > 0.009) {
                fiscalRows.push({
                    description: "Extra banco",
                    quantity: 1,
                    unitPrice: Number(checkoutOrderAdjustmentTotals.surcharge.toFixed(2)),
                    vatRate: defaultFiscalVatRate,
                    department: getFiscalDepartmentByVatRate(defaultFiscalVatRate),
                });
            }

            if (shouldPrintFiscalReceipt && fiscalRows.length > 0) {
                const fiscalPayload = {
                    action: 'fiscal_receipt',
                    receiptId: checkoutDocuments[0]?.id,
                    content: {
                        ...(selectedFiscalDeviceId ? { fiscalDeviceId: Number(selectedFiscalDeviceId) } : {}),
                        tableName: selectedTable?.name || '',
                        customerName: checkoutClientLabel || '',
                        paymentMethod: normalizedRows.length > 1
                            ? 'PAGAMENTO MISTO'
                            : getFiscalPaymentMethodLabel(selectedPaymentMethod),
                        ...(heliteGuestLabel ? { roomChargeGuest: heliteGuestLabel } : {}),
                        total: Number(checkoutAmountDue.toFixed(2)),
                        ...(checkoutAppliedPrepaymentTotal > 0.009 ? {
                            prepayments: {
                                originalTotal: Number(checkoutTotalValue.toFixed(2)),
                                appliedTotal: Number(checkoutAppliedPrepaymentTotal.toFixed(2)),
                                amountDue: Number(checkoutAmountDue.toFixed(2)),
                                finalDocument: appliedPrepaymentResult?.finalDocument || null,
                            },
                        } : {}),
                        payments: normalizedRows.map((row) => ({
                            method: getFiscalPaymentMethodLabel(row.method),
                            amount: Number(normalizeAmountInput(row.amount).toFixed(2)),
                            given: isCashMethod(row.method)
                                ? Number(normalizeAmountInput(row.given).toFixed(2))
                                : undefined,
                        })),
                        items: fiscalRows,
                    }
                };

                try {
                    await sendFiscalReceiptRequest(fiscalPayload, token, tenant);
                } catch (fiscalErr) {
                    handleAlert(`Pagamento registrato, ma stampa fiscale fallita: ${fiscalErr?.message || fiscalErr}`, "warning");
                }
            }
        } catch (err) {
            handleAlert(getCheckoutErrorMessage(err), err?.code === "CHECKOUT_IN_PROGRESS" ? "warning" : "error")
            return;
        }
        setOpenConfirmCloseCheckout(false)
        setLastPaymentSummary((previous) => ({ ...previous, total: effectivePaidTotal }));
        if (!isMultipleCheckout) setTotalPayment(effectivePaidTotal)
        if (heliteBuoniQueued) {
            setCheckoutSuccessPendingBuonoAction(true);
            return;
        }
        if (genericVouchersQueued) {
            setCheckoutSuccessPendingVoucherAction(true);
            return;
        }
        setOpenCloseCheckout(true);
    }

    function handleInvoiceCreated(invoice) {
        setCreatedInvoice(invoice);
        setInvoiceSettlementMethod("");
        setInvoicePaymentRecorded(false);
        setInvoiceCourtesyPrintPrompt(invoice);
    }

    const closeInvoiceCourtesyPrintPrompt = useCallback(() => {
        setInvoiceCourtesyPrintPrompt(null);
        setOpenInvoiceCloseConfirm(true);
    }, []);

    const handleConfirmInvoiceCourtesyPrint = useCallback(async () => {
        const invoiceId = invoiceCourtesyPrintPrompt?.id;
        if (!invoiceId) {
            closeInvoiceCourtesyPrintPrompt();
            return;
        }

        setInvoiceCourtesyPrintSubmitting(true);
        try {
            await printInvoiceCourtesyCopy(invoiceId, token, tenant);
            handleAlert("Stampa copia cortesia inviata", "success");
            closeInvoiceCourtesyPrintPrompt();
        } catch (err) {
            handleAlert(err?.message || "Errore stampa copia cortesia", "error");
        } finally {
            setInvoiceCourtesyPrintSubmitting(false);
        }
    }, [closeInvoiceCourtesyPrintPrompt, handleAlert, invoiceCourtesyPrintPrompt?.id, tenant, token]);

    async function handleCloseCheckoutAfterInvoice() {
        if (!invoiceSettlementMethod) {
            handleAlert("Seleziona come chiudere il pagamento.", "warning");
            return;
        }
        const invoiceOrderIds = [...new Set((ordiniFiltrati || [])
            .map((row) => Number(row?.orderId))
            .filter((orderId) => Number.isInteger(orderId) && orderId > 0))];

        if (invoiceOrderIds.length < 1) {
            handleAlert(t("checkout_page.noRegisteredOrders"), "warning");
            return;
        }

        setInvoiceCloseSubmitting(true);
        try {
            const checkoutOrders = (order || []).filter((item) =>
                invoiceOrderIds.includes(Number(item?.id))
            );
            const latestNonReservationOrderIds = getNonReservationOrderIds(tenant);
            const latestNonReservationTableMarkers = getNonReservationTableMarkers(tenant);
            const selectedReservationMarker = selectedTableTodayReservation?.id
                ? `${Number(selectedTable?.id)}:${Number(selectedTableTodayReservation.id)}`
                : null;
            const isNonReservationCheckout =
                (selectedReservationMarker && latestNonReservationTableMarkers.has(selectedReservationMarker))
                || invoiceOrderIds.some((orderId) => latestNonReservationOrderIds.has(orderId));
            const reservationClientId = Number(
                selectedTableTodayReservation?.clientId
                ?? selectedTableTodayReservation?.client?.id
            );
            const hasReservationClientOrder = Number.isInteger(reservationClientId)
                && checkoutOrders.some((item) => Number(item?.clientId) === reservationClientId);
            const reservationToComplete = isNonReservationCheckout
                ? null
                : activeCheckoutReservation
                    || (hasReservationClientOrder ? selectedTableTodayReservation : null);
            const reservationToKeepConfirmed = isNonReservationCheckout
                ? selectedTableTodayReservation
                : null;

            await applyCheckoutPrepayments({ invoiceId: createdInvoice?.id });

            const amountToCollect = Number(Math.max(0, checkoutAmountDue).toFixed(2));
            if (
                invoiceSettlementMethod !== "SUSPENDED"
                && amountToCollect > 0.009
                && !invoicePaymentRecorded
            ) {
                const methodLabel = INVOICE_SETTLEMENT_METHODS.find(
                    (item) => item.value === invoiceSettlementMethod
                )?.label || invoiceSettlementMethod;
                await createInvoicePayment(createdInvoice.id, {
                    amount: amountToCollect,
                    paymentDate: new Date().toISOString().slice(0, 10),
                    paymentMethod: methodLabel,
                    note: "Incasso registrato alla chiusura del conto",
                }, token, tenant);
                setInvoicePaymentRecorded(true);
            }

            await Promise.all(invoiceOrderIds.map(async (orderId) => {
                const currentOrder = (order || []).find((item) => Number(item?.id) === orderId);
                if (["PAID", "CANCELLED"].includes(currentOrder?.status)) return;
                await aggiornaStatusComanda(orderId, { status: "CANCELLED" }, token, tenant);
            }));

            if (reservationToKeepConfirmed?.id) {
                clearNonReservationMarkersForTable(tenant, selectedTable?.id, invoiceOrderIds);
            }

            if (!isCounterCheckout && !isMultipleCheckout && selectedTable?.id) {
                const nextTableStatus = reservationToKeepConfirmed ? "RISERVATO" : "LIBERO";
                await liberaTavolo(selectedTable, nextTableStatus);
                if (nextTableStatus === "LIBERO") {
                    removeTableFromCheckoutLists(selectedTable?.id);
                }
            }

            if (reservationToComplete?.id && !isSaleCongressiTenant) {
                try {
                    await aggiornaStatoPrenotazione(reservationToComplete.id, "COMPLETED", token, tenant);
                } catch (reservationErr) {
                    if (!isReservationCompletionBlockedError(reservationErr)) throw reservationErr;
                    handleAlert(
                        `Fattura registrata. ${reservationErr?.message || reservationErr}`,
                        "warning"
                    );
                }
            } else if (reservationToKeepConfirmed?.id) {
                await aggiornaStatoPrenotazione(reservationToKeepConfirmed.id, "CONFIRMED", token, tenant);
            }
            if (
                isSaleCongressiTenant
                && checkoutReservationEventId
                && !isCounterCheckout
                && !isMultipleCheckout
            ) {
                await patchReservationEventStatus(
                    checkoutReservationEventId,
                    preserveReservationEventStatus || "COMPLETED",
                    token,
                    tenant
                );
            }

            setOpenInvoiceCloseConfirm(false);
            setInvoiceSettlementMethod("");
            setInvoicePaymentRecorded(false);
            setSelectedTable("");
            setSelectedTableReservation(null);
            setPaidReservationLabel(null);
            setCombinedCheckoutOrders([]);
            setOrdiniDivisi(false);
            setSplitTargetRowIds(null);
            setCheckboxSelectedIds(new Set());
            window.dispatchEvent(new CustomEvent('comanda-creata'));
            window.dispatchEvent(new CustomEvent('map-data-changed'));
            await fetchAllData();
            navigate("/punto_cassa", { replace: true, state: {} });
            handleAlert(t("checkout_page.invoiceCheckoutClosed"), "success");
        } catch (err) {
            handleAlert(getCheckoutErrorMessage(err), err?.code === "CHECKOUT_IN_PROGRESS" ? "warning" : "error");
        } finally {
            setInvoiceCloseSubmitting(false);
        }
    }

    async function checkingSettings() {
        let settings = await getTenantSettings(token, tenant)
        if (
            settings?.cap?.length > 0
            && settings?.city?.length > 0
            && settings?.address?.length > 0
            && settings?.ownerName?.length > 0
            && settings?.vatNumber?.length > 0
        ) {
            setOpenDialogError(false)
            setOpenCreateInvoice(true)
        } else {
            setOpenCreateInvoice(false)
            setOpenDialogError(true)
        }
    }

    const renderPrepaymentPosPanel = () => {
        if (!isPrepaymentPosActive) return null;
        const modeLabel = prepaymentPosMode === "DEPOSIT"
            ? t("checkout_page.prepaymentPos.deposits")
            : t("checkout_page.prepaymentPos.advances");
        const submitLabel = prepaymentPosForm.operation === "REFUND"
            ? t("checkout_page.prepaymentPos.refund")
            : prepaymentPosForm.operation === "REVERSE"
                ? t("checkout_page.prepaymentPos.reverse")
                : t("checkout_page.prepaymentPos.receive");
        const requiresDocumentFields = prepaymentPosMode === "ADVANCE"
            && (
                prepaymentPosForm.operation === "RECEIVE"
                || ["COMMERCIAL_CANCELLATION", "COMMERCIAL_RETURN"].includes(prepaymentPosForm.documentType)
            )
            && prepaymentPosForm.documentType !== "INVOICE_TD02";
        const requiresVatNature = prepaymentPosMode === "ADVANCE"
            && prepaymentPosForm.operation === "RECEIVE"
            && prepaymentPosForm.documentType === "INVOICE_TD02"
            && normalizeAmountInput(prepaymentPosForm.vatRate) === 0;

        return (
            <Paper sx={{ ...theme.checkout.paper_checkout, p: 2, height: "100%", overflow: "auto" }}>
                <Stack spacing={1.5}>
                    <Box>
                        <Typography variant="h6" fontWeight={900}>{modeLabel}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t("checkout_page.prepaymentPos.subtitle")}
                        </Typography>
                    </Box>

                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <Paper variant="outlined" sx={{ p: 1 }}>
                                <Typography variant="caption" color="text.secondary">{t("checkout_page.prepaymentPos.totalDeposits")}</Typography>
                                <Typography fontWeight={900}>EUR {Number(prepaymentPosTotals.totalDeposits || 0).toFixed(2)}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={6}>
                            <Paper variant="outlined" sx={{ p: 1 }}>
                                <Typography variant="caption" color="text.secondary">{t("checkout_page.prepaymentPos.totalAdvances")}</Typography>
                                <Typography fontWeight={900}>EUR {Number(prepaymentPosTotals.totalAdvances || 0).toFixed(2)}</Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                            variant={prepaymentPosForm.operation === "RECEIVE" ? "contained" : "outlined"}
                            onClick={() => setPrepaymentPosOperation("RECEIVE")}
                        >
                            {t("checkout_page.prepaymentPos.receive")}
                        </Button>
                        {prepaymentPosMode === "DEPOSIT" ? (
                            <Button
                                variant={prepaymentPosForm.operation === "REFUND" ? "contained" : "outlined"}
                                onClick={() => setPrepaymentPosOperation("REFUND")}
                            >
                                {t("checkout_page.prepaymentPos.refund")}
                            </Button>
                        ) : (
                            <Button
                                variant={prepaymentPosForm.operation === "REVERSE" ? "contained" : "outlined"}
                                onClick={() => setPrepaymentPosOperation("REVERSE")}
                            >
                                {t("checkout_page.prepaymentPos.reverse")}
                            </Button>
                        )}
                    </Stack>

                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label={t("checkout_page.amount")}
                                value={prepaymentPosForm.amount}
                                onChange={(e) => updatePrepaymentPosForm("amount", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={t("checkout_page.prepaymentPos.paymentMethod")}
                                value={prepaymentPosForm.paymentMethod}
                                onChange={(e) => updatePrepaymentPosForm("paymentMethod", e.target.value)}
                            >
                                {PREPAYMENT_POS_PAYMENT_METHODS.map((method) => (
                                    <MenuItem key={method} value={method}>
                                        {t(`checkout_page.prepaymentPos.paymentMethods.${method}`)}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {prepaymentPosMode === "ADVANCE" && prepaymentPosForm.operation === "RECEIVE" ? (
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={t("checkout_page.prepaymentPos.vatRate")}
                                    value={prepaymentPosForm.vatRate}
                                    onChange={(e) => updatePrepaymentPosForm("vatRate", e.target.value)}
                                />
                            </Grid>
                        ) : null}

                        {prepaymentPosMode === "ADVANCE" ? (
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={t("checkout_page.prepaymentPos.documentType")}
                                    value={prepaymentPosForm.documentType}
                                    onChange={(e) => updatePrepaymentPosForm("documentType", e.target.value)}
                                >
                                    {prepaymentPosDocumentTypes.map((docType) => (
                                        <MenuItem key={docType} value={docType}>{prepaymentDocumentLabel(docType)}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        ) : null}

                        {prepaymentPosForm.operation !== "RECEIVE" ? (
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={t("checkout_page.prepaymentPos.sourceMovement")}
                                    value={prepaymentPosForm.sourceMovementId}
                                    onChange={(e) => updatePrepaymentPosForm("sourceMovementId", e.target.value)}
                                >
                                    {prepaymentPosRefundableSources.map((movement) => (
                                        <MenuItem key={movement.id} value={movement.id}>
                                            #{movement.id} - EUR {Number(prepaymentMovementAvailableAmount(movement, prepaymentPosMovements)).toFixed(2)} - {movement.documentNumber || movement.description || "-"}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        ) : null}

                        {requiresVatNature ? (
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={t("checkout_page.prepaymentPos.vatNature")}
                                    value={prepaymentPosForm.vatNature}
                                    onChange={(e) => updatePrepaymentPosForm("vatNature", e.target.value)}
                                >
                                    {PREPAYMENT_POS_VAT_NATURE_OPTIONS.map((value) => (
                                        <MenuItem key={value} value={value}>
                                            {t(`checkout_page.prepaymentPos.vatNatureOptions.${value}`)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        ) : null}

                        {requiresDocumentFields ? (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={t("checkout_page.prepaymentPos.documentNumber")}
                                        value={prepaymentPosForm.documentNumber}
                                        onChange={(e) => updatePrepaymentPosForm("documentNumber", e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="date"
                                        label={t("checkout_page.prepaymentPos.documentDate")}
                                        InputLabelProps={{ shrink: true }}
                                        value={prepaymentPosForm.documentDate}
                                        onChange={(e) => updatePrepaymentPosForm("documentDate", e.target.value)}
                                    />
                                </Grid>
                            </>
                        ) : null}

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label={t("checkout_page.prepaymentPos.description")}
                                value={prepaymentPosForm.description}
                                onChange={(e) => updatePrepaymentPosForm("description", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label={t("checkout_page.prepaymentPos.note")}
                                value={prepaymentPosForm.note}
                                onChange={(e) => updatePrepaymentPosForm("note", e.target.value)}
                            />
                        </Grid>
                    </Grid>

                    <Alert severity="info">
                        {t("checkout_page.prepaymentPos.fiscalPending")}
                    </Alert>

                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button variant="outlined" onClick={closePrepaymentPosMode}>
                            {t("annulla")}
                        </Button>
                        <Button variant="contained" disabled={prepaymentPosSubmitting} onClick={submitPrepaymentPos}>
                            {submitLabel}
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
        );
    };

    return (
        <SidebarLayout>
            <Box
                sx={{
                    minHeight: { xs: "auto", lg: "calc(100dvh - 58px)" },
                    height: { xs: "auto", lg: "calc(100dvh - 58px)" },
                    overflowX: "hidden",
                    overflowY: { xs: "auto", lg: "hidden" },
                    display: "flex",
                    flexDirection: "column",
                    px: 1,
                    pb: 1,
                    boxSizing: "border-box",
                }}
            >
                <Typography variant="h5" gutterBottom sx={{ padding: 1, mb: 0 }}> {getTenantTranslation(t, tenantType, 'checkoutPage')}</Typography>
                <Box width={"100%"} sx={{ backgroundColor: "trasparent", px: 1, pt: 0.5, pb: 1, boxSizing: "border-box" }}>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item sx={{ width: { xs: "100%", sm: 250 } }} >
                            <FormControl fullWidth sx={{ pb: { xs: 1, md: 0 } }}>
                                <Select
                                    id="selectTable"
                                    displayEmpty
                                    variant="outlined"
                                    value={selectedTable.id || ""}
                                    onChange={(e) => {
                                        const value = e.target.value === ""
                                            ? ""
                                            : e.target.value === COUNTER_CHECKOUT_ID
                                                ? counterCheckoutTable
                                                : table.find((tab) => Number(tab.id) === Number(e.target.value));
                                        setSelectedTable(value)
                                    }}
                                    size="small"
                                    renderValue={(selected) => {
                                        if (selected === "") {
                                            return <span style={{ color: '#888' }}>{getTenantTranslation(t, tenantType, 'selectTable')}</span>;
                                        }
                                        if (selected === COUNTER_CHECKOUT_ID) return t("checkout_page.counterSale");
                                        if (selected === MULTIPLE_CHECKOUT_ID) return t("checkout_page.multipleCheckout");
                                        const value = table?.find((tab) => Number(tab.id) === Number(selected));
                                        return checkoutTableLabel(value);
                                    }}
                                    sx={theme.checkout.orange_outline_button}
                                >
                                    <MenuItem key={"null"} value={""}>{getTenantTranslation(t, tenantType, 'selectTable')}</MenuItem>
                                    {!isSaleCongressiTenant && (
                                        <MenuItem key={COUNTER_CHECKOUT_ID} value={COUNTER_CHECKOUT_ID}>{t("checkout_page.counterSale")}</MenuItem>
                                    )}
                                    {!isSaleCongressiTenant && isMultipleCheckout && <MenuItem key={MULTIPLE_CHECKOUT_ID} value={MULTIPLE_CHECKOUT_ID}>{t("checkout_page.multipleCheckout")}</MenuItem>}
                                    {checkoutSelectableTables.map((tab) =>
                                        <MenuItem key={tab.id} value={tab.id}>{checkoutTableLabel(tab)}</MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        {!isSaleCongressiTenant && (
                            <Grid item><Button variant="contained" onClick={startCounterSale} sx={theme.checkout.closeCheckout_button}>{t("checkout_page.newCounterSale")}</Button></Grid>
                        )}
                        {!isSaleCongressiTenant && (
                            <Grid item><Button variant="outlined" onClick={() => { setOpenPendingList(true) }} sx={theme.checkout.orange_outline_button} >{getTenantTranslation(t, tenantType, 'checkout_page.pending')}</Button></Grid>
                        )}
                        {!isSaleCongressiTenant && (
                            <Grid item><Button variant="outlined" onClick={() => { setOpenOccupiedList(true) }} sx={theme.checkout.orange_outline_button} >{getTenantTranslation(t, tenantType, 'checkout_page.occupied')}</Button></Grid>
                        )}
                        {!isSaleCongressiTenant && (
                            <Grid item><Button variant="outlined" onClick={openMultipleCheckoutDialog} disabled={multipleCheckoutOrders.length < 2} sx={theme.checkout.orange_outline_button} >{t("checkout_page.multipleCheckout")}</Button></Grid>
                        )}
                        <Grid item>
                            <Button
                                variant="outlined"
                                startIcon={<ReceiptLongOutlinedIcon />}
                                onClick={() => {
                                    setFiscalDailyClosureError("");
                                    setOpenFiscalDailyClosureConfirm(true);
                                }}
                                disabled={fiscalDailyClosureSubmitting}
                                sx={theme.checkout.orange_outline_button}
                            >
                                {fiscalDailyClosureSubmitting ? "Chiusura..." : "Chiusura Giornaliera"}
                            </Button>
                        </Grid>
                        {isSaleCongressiTenant ? (
                            <>
                                <Grid item>
                                    <Button
                                        variant={prepaymentPosMode === "DEPOSIT" ? "contained" : "outlined"}
                                        onClick={() => openPrepaymentPosMode("DEPOSIT")}
                                        disabled={!checkoutReservationEventId}
                                        sx={theme.checkout.orange_outline_button}
                                    >
                                        {t("checkout_page.prepaymentPos.deposits")}
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button
                                        variant={prepaymentPosMode === "ADVANCE" ? "contained" : "outlined"}
                                        onClick={() => openPrepaymentPosMode("ADVANCE")}
                                        disabled={!checkoutReservationEventId}
                                        sx={theme.checkout.orange_outline_button}
                                    >
                                        {t("checkout_page.prepaymentPos.advances")}
                                    </Button>
                                </Grid>
                            </>
                        ) : null}
                        {/* <Grid item><Button variant="outlined" onClick={() => { setOpenTransfer(true) }} disabled={!selectedTable} sx={theme.checkout.orange_outline_button} >{getTenantTranslation(t, tenantType, 'tableMapPage.transferOrders')}</Button></Grid> */}
                        {process.env.REACT_APP_SHOW_DEV_CLEAR_TABLES === "true" && (
                            <Grid item><Button variant="outlined" onClick={() => { liberaTuttiTavoli() }} sx={theme.checkout.orange_outline_button} >{getTenantTranslation(t, tenantType, "clearAllTablesAndPay")}</Button></Grid>
                        )}
                    </Grid>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        width: "100%",
                        display: "grid",
                        gap: 1,
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "1fr 1fr",
                            lg: "1.08fr 1fr 1fr",
                        },
                        gridTemplateRows: { lg: "minmax(0, 1fr)" },
                        overflow: "hidden",
                    }}
                >
                    {/* tastierino */}
                    <Box
                        sx={{
                            minWidth: 0,
                            overflow: "hidden",
                            gridColumn: { xs: "1 / -1", md: "1 / 2", lg: "auto" },
                        }}
                    >
                        <Box sx={{ borderRadius: "16px", width: "100%", height: "100%", overflow: "hidden" }}>
                            <Tastierino
                                selectedTable={selectedTable}
                                category={category}
                                ordiniFiltrati={ordiniFiltrati}
                                setSelectedCategory={setSelectedCategory}
                                setCheckboxSelectedIds={setCheckboxSelectedIds}
                                handleEditQty={handleEditQty}
                                handleDelete={handleDelete}
                                handlePrint={gestisciStampa}
                                token={token}
                                tenant={tenant}
                                handleDisplay={handleDisplay}
                                mostraDisplay={mostraDisplay}
                                cambiaTipoVariazione={cambiaTipoVariazione}
                                setEditPrezzo={setEditPrezzo}
                                editPrezzo={editPrezzo}
                                editQty={editQty}
                                setEditQty={setEditQty}
                                setOpenDelete={setOpenDelete}
                                incrementoPrezzo={incrementoPrezzo}
                                decrementoPrezzo={decrementoPrezzo}
                                setIncrementoPrezzo={setIncrementoPrezzo}
                                setDecrementoPrezzo={setDecrementoPrezzo}
                                handleSplitToggle={handleSplitToggle}
                                handleClearDiscount={handleClearDiscount}
                                handleApplyDirectPrice={handleApplyDirectPrice}
                                priceEditSubmitting={priceEditSubmitting}
                            />
                        </Box>
                    </Box>
                    {/* prodotti */}
                    <Box sx={{ minWidth: 0, display: "flex", overflow: "hidden", gridColumn: { xs: "1 / -1", md: "2 / 3", lg: "auto" } }}>
                        <Paper
                            sx={{
                                ...{
                                    width: "100%",
                                    padding: 2,
                                    overflow: "hidden",
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    minHeight: 0,
                                },
                                ...theme.checkout.paper_checkout
                            }}
                        >
                            <Box sx={{ margin: 1, flexShrink: 0, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                <Typography sx={{ margin: 1, flex: "1 1 auto" }}> {
                                    isPrepaymentPosActive
                                        ? (
                                            prepaymentPosMode === "DEPOSIT"
                                                ? t("checkout_page.prepaymentPos.depositProducts").toUpperCase()
                                                : t("checkout_page.prepaymentPos.advanceProducts").toUpperCase()
                                        )
                                        : normalizedProductSearch ? t('checkout_page.searchProduct').toUpperCase()
                                        : selectedCategory
                                            ? (category?.find((cat) => cat.id === selectedCategory)?.name || t('all')).toUpperCase()
                                        :
                                        (t('all').toUpperCase())
                                } {/* CATEGORIE */}</Typography>
                                <TextField placeholder={t('checkout_page.searchProduct')}
                                    value={searchProduct}
                                    onChange={handleSearch}
                                    InputProps={{
                                        startAdornment: (<SearchIcon sx={{ mr: 1 }} />),
                                        endAdornment: ((searchProduct.length < 1) ? null : <HighlightOffIcon onClick={() => { setSearchProduct("") }} sx={{ mr: 1, cursor: "pointer" }} />)
                                    }}
                                    sx={{
                                        width: { xs: "100%", sm: 280 },
                                        ml: { sm: "auto" },
                                        backgroundColor: theme.checkout.product_button.backgroundColor,
                                        borderRadius: "8px",
                                        border: "0px"
                                    }}
                                />
                            </Box>
                            {isPrepaymentPosActive ? (
                                <Stack spacing={1} sx={{ overflow: "auto", pr: 0.5 }}>
                                    {prepaymentPosProducts.map((product) => (
                                        <Button
                                            key={product.id}
                                            variant={Number(selectedPrepaymentProductId) === Number(product.id) ? "contained" : "outlined"}
                                            onClick={() => selectPrepaymentProduct(product)}
                                            sx={{ justifyContent: "space-between", minHeight: 48, textAlign: "left" }}
                                        >
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography fontWeight={800} sx={{ overflowWrap: "anywhere" }}>{product.name}</Typography>
                                                <Typography variant="caption" sx={{ display: "block" }}>
                                                    EUR {Number(product.price || 0).toFixed(2)}
                                                </Typography>
                                            </Box>
                                        </Button>
                                    ))}
                                    {prepaymentPosProducts.length === 0 ? (
                                        <Alert severity="warning">
                                            {t("checkout_page.prepaymentPos.noProducts")}
                                        </Alert>
                                    ) : null}
                                </Stack>
                            ) : (prodottiFiltratiPerNome) ? <FiltraProdotto
                                prodottiFiltratiPerNome={prodottiFiltratiPerNome}
                                selectedTable={isMultipleCheckout ? null : selectedTable}
                                handleAddProduct={handleAddProduct}
                                activeBoardBasis={activeBoardBasis}
                            /> : null}
                        </Paper>
                    </Box>
                    {/* resoconto ordine */}
                    <Box sx={{ minWidth: 0, minHeight: 0, overflow: "hidden", gridColumn: { xs: "1 / -1", md: "1 / -1", lg: "auto" } }}>
                        {isPrepaymentPosActive ? renderPrepaymentPosPanel() : <ComandaCorrente
                            selectedTable={selectedTable}
                            ordiniDivisi={ordiniDivisi}
                            ordiniFiltrati={ordiniFiltrati}
                            ordiniUniti={ordiniUniti}
                            ordineSchermo={ordineSchermo}
                            checkboxSelectedIds={checkboxSelectedIds}
                            handleCheck={handleCheck}
                            setOpenCloseInvoiceDialog={setOpenCreateInvoice}
                            // setOpenCloseCheckout={setOpenCloseCheckout}
                            checkingSettings={checkingSettings}
                            calcolaTotale={calcolaTotale}
                            setOpenConfirmCloseCheckout={setOpenConfirmCloseCheckout}
                            closeCheckoutLabel={closeCheckoutActionLabel}
                            checkoutOrderSurchargeItems={checkoutOrderSurchargeItems}
                            checkoutOrderDiscountItems={checkoutOrderDiscountItems}
                            checkoutGrossTotalValue={checkoutGrossTotalValue}
                            checkoutAppliedPrepaymentTotal={checkoutAppliedPrepaymentTotal}
                            checkoutAmountDue={checkoutAmountDue}
                            checkoutPrepaymentsLoading={checkoutPrepaymentsLoading}
                            hideTotalPayment={isSaleCongressiTenant}
                        />}
                    </Box>
                </Box>
                {openMultipleCheckout &&
                    <Dialog
                        open={openMultipleCheckout}
                        onClose={() => {
                            setOpenMultipleCheckout(false);
                        }}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogTitle>{t("checkout_page.multipleCheckout")}</DialogTitle>
                        <DialogContent sx={{ pt: 1 }}>
                            <Typography sx={{ mb: 2, color: theme.palette.text.secondary }}>
                                {t("checkout_page.multipleCheckoutHelp")}
                            </Typography>
                            <Box sx={{ maxHeight: 360, overflowY: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 1 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox" />
                                            <TableCell>{t("checkout_page.itemsColumn")}</TableCell>
                                            <TableCell>{t("sala")}</TableCell>
                                            <TableCell>{t("table")}</TableCell>
                                            <TableCell>{t("status")}</TableCell>
                                            <TableCell align="right">{t("total")}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {multipleCheckoutOrders.map((ord) => {
                                            const checked = multipleCheckoutOrderIds.has(Number(ord.id));
                                            return (
                                                <TableRow
                                                    key={ord.id}
                                                    hover
                                                    selected={checked}
                                                    onClick={() => toggleMultipleCheckoutOrder(ord.id)}
                                                    sx={{ cursor: "pointer" }}
                                                >
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            checked={checked}
                                                            onClick={(event) => event.stopPropagation()}
                                                            onChange={() => toggleMultipleCheckoutOrder(ord.id)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={700}>
                                                            {ord.checkoutItemsSummary}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {t("checkout_page.orderColumn")} #{ord.id}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{ord.checkoutRoomLabel}</TableCell>
                                                    <TableCell>{ord.checkoutTableName}</TableCell>
                                                    <TableCell>{ord.status}</TableCell>
                                                    <TableCell align="right">€ {ord.checkoutTotal.toFixed(2)}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Box>
                            <Grid container spacing={2} sx={{ mt: 2 }} alignItems="center">
                                <Grid item xs={12}>
                                    <Box textAlign={{ xs: "left", sm: "right" }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {selectedMultipleCheckoutOrders.length} ordini selezionati
                                        </Typography>
                                        <Typography variant="h6" color={theme.palette.mode === "light" ? "#125605" : "#78BD6A"}>
                                            € {multipleCheckoutTotal.toFixed(2)}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                variant="contained"
                                onClick={() => setOpenMultipleCheckout(false)}
                            >
                                {t("confirmOperation.cancel")}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={bringMultipleCheckoutToOrder}
                                disabled={selectedMultipleCheckoutOrders.length < 2}
                                sx={theme.checkout.closeCheckout_button}
                            >
                                Porta a ordine
                            </Button>
                        </DialogActions>
                    </Dialog>
                }
                {openFiscalDailyClosureConfirm &&
                    <Dialog
                        open={openFiscalDailyClosureConfirm}
                        onClose={() => {
                            if (!fiscalDailyClosureSubmitting) {
                                setFiscalDailyClosureError("");
                                setOpenFiscalDailyClosureConfirm(false);
                            }
                        }}
                        fullWidth
                        maxWidth="xs"
                    >
                        <DialogTitle>Chiusura Giornaliera Cassa Fiscale</DialogTitle>
                        <DialogContent>
                            <Typography variant="body2" color="text.secondary">
                                Confermi l'esecuzione della chiusura giornaliera della cassa fiscale?
                            </Typography>
                            {fiscalDevices.length > 0 ? (
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Cassa fiscale"
                                    value={fiscalClosureTarget}
                                    onChange={(event) => setFiscalClosureTarget(event.target.value)}
                                    sx={{ mt: 2 }}
                                >
                                    {fiscalDevices.map((device) => (
                                        <MenuItem key={device.id} value={String(device.id)}>
                                            {device.name}{device.isDefault ? " (predefinita)" : ""}
                                        </MenuItem>
                                    ))}
                                    {fiscalDevices.length > 1 ? <MenuItem value="all">Tutte le casse fiscali</MenuItem> : null}
                                </TextField>
                            ) : null}
                            {fiscalDailyClosureError ? (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {fiscalDailyClosureError}
                                </Alert>
                            ) : null}
                        </DialogContent>
                        <DialogActions>
                            <Button
                                variant="contained"
                                disabled={fiscalDailyClosureSubmitting}
                                onClick={() => {
                                    setFiscalDailyClosureError("");
                                    setOpenFiscalDailyClosureConfirm(false);
                                }}
                            >
                                {t("confirmOperation.cancel")}
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<ReceiptLongOutlinedIcon />}
                                disabled={fiscalDailyClosureSubmitting}
                                onClick={handleFiscalDailyClosure}
                                sx={theme.checkout.closeCheckout_button}
                            >
                                {fiscalDailyClosureSubmitting ? "Chiusura..." : "Conferma"}
                            </Button>
                        </DialogActions>
                    </Dialog>
                }
                {openConfirmCloseCheckout &&
                    <Dialog
                        open={openConfirmCloseCheckout}
                        onClose={() => {
                            setRomanSplitOpen(false);
                            setOpenConfirmCloseCheckout(false);
                            setRefreshTrigger((prev) => prev + 1)
                        }}
                    >
                        <DialogTitle>{t("checkout_page.selectPaymentMethode")}</DialogTitle>
                        <DialogContent sx={{ width: { xs: "100%", sm: 640 }, maxWidth: "100%" }}>
                            <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="body2" color="text.secondary">{t("total")}</Typography>
                                <Typography variant="h6">EUR {checkoutTotalValue.toFixed(2)}</Typography>
                            </Box>
                            {fiscalDevices.length > 0 ? (
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Cassa fiscale"
                                    value={selectedFiscalDeviceId}
                                    onChange={(event) => {
                                        const deviceId = event.target.value;
                                        setSelectedFiscalDeviceId(deviceId);
                                        writeFiscalDevicePreference(tenant, deviceId);
                                    }}
                                    helperText="La scelta resta memorizzata su questo browser e la cassa viene salvata sul documento per eventuali storni."
                                    sx={{ mb: 1.5 }}
                                >
                                    {fiscalDevices.map((device) => (
                                        <MenuItem key={device.id} value={String(device.id)}>
                                            {device.name}{device.isDefault ? " (predefinita)" : ""}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            ) : null}
                            {checkoutReservationEventId ? (
                                <Box sx={{ mb: 1.5 }}>
                                    <Stack spacing={0.75}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t("checkout_page.prepaymentsApplied")}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={900}>
                                                EUR {checkoutAppliedPrepaymentTotal.toFixed(2)}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t("checkout_page.amountDueAfterPrepayments")}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={900}>
                                                EUR {checkoutAmountDue.toFixed(2)}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    {checkoutPrepaymentsLoading ? (
                                        <Alert severity="info" sx={{ mt: 1 }}>{t("checkout_page.prepaymentsLoading")}</Alert>
                                    ) : checkoutAppliedPrepaymentTotal > 0.009 ? (
                                        <Alert severity="success" sx={{ mt: 1 }}>
                                            {t("checkout_page.prepaymentsWillApply", { amount: `EUR ${checkoutAppliedPrepaymentTotal.toFixed(2)}` })}
                                        </Alert>
                                    ) : (
                                        <Alert severity="info" sx={{ mt: 1 }}>{t("checkout_page.noAvailablePrepayments")}</Alert>
                                    )}
                                    {checkoutAmountDue <= 0.009 && checkoutAppliedPrepaymentTotal > 0.009 ? (
                                        <Alert severity="success" sx={{ mt: 1 }}>{t("checkout_page.zeroBalanceCovered")}</Alert>
                                    ) : null}
                                </Box>
                            ) : null}
                            {checkoutAmountDue > 0.009 ? (
                                <Box sx={{ my: 1.5 }}>
                                    {!romanSplitOpen ? (
                                        <Button
                                            variant="outlined"
                                            startIcon={<AccountBalanceOutlinedIcon />}
                                            onClick={() => setRomanSplitOpen(true)}
                                            sx={{ minHeight: 40 }}
                                        >
                                            {t("checkout_page.romanSplit")}
                                        </Button>
                                    ) : (
                                        <Box
                                            sx={{
                                                p: 1,
                                                display: "flex",
                                                gap: 1,
                                                flexWrap: "wrap",
                                                alignItems: "center",
                                                border: `1px solid ${COLORS.border}`,
                                                borderRadius: 1,
                                            }}
                                        >
                                            <TextField
                                                size="small"
                                                type="number"
                                                label={t("checkout_page.romanSplitParts")}
                                                value={romanSplitParts}
                                                onChange={(e) => setRomanSplitParts(Math.max(2, Math.trunc(Number(e.target.value) || 2)))}
                                                inputProps={{ min: 2, step: 1 }}
                                                sx={{ width: 130 }}
                                            />
                                            <Button
                                                variant="contained"
                                                startIcon={<AccountBalanceOutlinedIcon />}
                                                disabled={checkoutPaymentSubmitting || checkoutPrepaymentsLoading}
                                                onClick={handleAllaRomanaCheckout}
                                                sx={{ minHeight: 40 }}
                                            >
                                                {checkoutPaymentSubmitting ? "Invio..." : t("checkout_page.romanSplit")}
                                            </Button>
                                            <Button
                                                variant="text"
                                                disabled={checkoutPaymentSubmitting}
                                                onClick={() => setRomanSplitOpen(false)}
                                                sx={{ minHeight: 40 }}
                                            >
                                                {t("tableMapPage.close")}
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            ) : null}
                            <Grid container spacing={1}>
                                {closeCheckoutPayments.map((row) => (
                                    <React.Fragment key={row.id}>
                                        <Grid item xs={12} sm={isCardUiMethod(row.method) ? 3 : 4}>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={row.method}
                                                    onChange={(e) => updateClosePaymentRow(row.id, { method: e.target.value })}
                                                >
                                                    <MenuItem value="cash">{t("checkout_page.cash")}</MenuItem>
                                                    <MenuItem value="cards">{t("checkout_page.cards")}</MenuItem>
                                                    <MenuItem value="satispay">{t("satispay")}</MenuItem>
                                                    <MenuItem value="helite_room_charge">{t("heliteGuestPicker.paymentRoomCharge")}</MenuItem>
                                                    <MenuItem value={MANUAL_PMS_BUONO_METHOD}>Buono PMS manuale</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        {isCardUiMethod(row.method) && (
                                            <Grid item xs={12} sm={3}>
                                                <FormControl fullWidth size="small" error={!row.cardCircuit}>
                                                    <Select
                                                        searchable={false}
                                                        displayEmpty
                                                        value={row.cardCircuit || ""}
                                                        renderValue={(value) => value
                                                            ? getCardCircuitLabel(value)
                                                            : "Tipo carta"}
                                                        onChange={(e) => updateClosePaymentRow(row.id, { cardCircuit: e.target.value })}
                                                    >
                                                        <MenuItem disabled value="">Tipo carta</MenuItem>
                                                        {CARD_CIRCUIT_OPTIONS.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        )}
                                        <Grid item xs={6} sm={isCardUiMethod(row.method) ? 2 : 3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={row.amount}
                                                onChange={(e) => updateClosePaymentRow(row.id, { amount: e.target.value })}
                                                inputProps={{ inputMode: "decimal" }}
                                                label={t("checkout_page.amount")}
                                            />
                                        </Grid>
                                        <Grid item xs={6} sm={isCardUiMethod(row.method) ? 2 : 3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={isCashMethod(row.method) ? row.given ?? row.amount : "-"}
                                                onChange={(e) => updateClosePaymentRow(row.id, { given: e.target.value })}
                                                inputProps={{ inputMode: "decimal" }}
                                                label={t("checkout_page.given")}
                                                disabled={!isCashMethod(row.method)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={2}>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                fullWidth
                                                sx={{ minHeight: 40 }}
                                                disabled={closeCheckoutPayments.length <= 1}
                                                onClick={() => removeClosePaymentRow(row.id)}
                                            >
                                                X
                                            </Button>
                                        </Grid>
                                        {isCashMethod(row.method) && (
                                            <Grid item xs={12}>
                                                <Typography variant="caption" color={normalizeAmountInput(row.given) < normalizeAmountInput(row.amount) ? "error.main" : "text.secondary"}>
                                                    {t("checkout_page.rowChange")}: EUR {Math.max(0, normalizeAmountInput(row.given) - normalizeAmountInput(row.amount)).toFixed(2)}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </React.Fragment>
                                ))}
                            </Grid>
                            {closeCheckoutPayments.some((row) => isManualPmsBuonoMethod(row.method)) && (
                                <Alert severity="warning" sx={{ mt: 1.5 }}>
                                    Chiude il conto senza documento fiscale. Usa questa opzione solo per creare un buono da addebitare manualmente su un PMS esterno.
                                </Alert>
                            )}
                            {!areCardCircuitsValid && (
                                <Typography variant="caption" color="error.main" sx={{ display: "block", mt: 1 }}>
                                    Seleziona il tipo di carta per ogni pagamento con carta.
                                </Typography>
                            )}
                            <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "space-between", alignItems: "center" }}>
                                <Button variant="text" onClick={addClosePaymentRow} sx={{ minHeight: 40 }}>{t("checkout_page.addPaymentMethod")}</Button>
                                <Typography variant="body2" color={closeCheckoutRemaining > 0.009 ? "warning.main" : "success.main"}>
                                    {t("checkout_page.remaining")}: EUR {closeCheckoutRemaining.toFixed(2)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t("checkout_page.totalChange")}: EUR {totalCashChange.toFixed(2)}
                                </Typography>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button variant="contained" onClick={() => { setPaymentMethode(); setOpenConfirmCloseCheckout(false) }}>{t("confirmOperation.cancel")}</Button>
                            <Button
                                variant="contained"
                                sx={theme.checkout.closeCheckout_button}
                                disabled={checkoutPaymentSubmitting || checkoutPrepaymentsLoading || !isCloseCheckoutPaymentBalanced || !isCashGivenValid || !areCardCircuitsValid}
                                onClick={handleConfirmCheckoutPayment}
                            >
                                {checkoutPaymentSubmitting ? "Invio..." : t("checkout_page.confirmPayment")}
                            </Button>
                        </DialogActions>
                    </Dialog>
                }
                {allaRomanaRecoveryPrompt &&
                    <Dialog
                        open={Boolean(allaRomanaRecoveryPrompt)}
                        onClose={() => {
                            if (!checkoutPaymentSubmitting) {
                                setAllaRomanaRecoveryPrompt(null);
                                setAllaRomanaRecoveryDocumentNumber("");
                            }
                        }}
                        fullWidth
                        maxWidth="sm"
                    >
                        <DialogTitle>Verifica scontrino divisione in parti uguali</DialogTitle>
                        <DialogContent>
                            <Stack spacing={2} sx={{ mt: 1 }}>
                                <Alert severity="warning">
                                    {allaRomanaRecoveryPrompt.message || "Documento fiscale da verificare sulla cassa"}
                                </Alert>
                                <Typography variant="body2" color="text.secondary">
                                    Documento {allaRomanaRecoveryPrompt.details?.partNumber || "-"} di {allaRomanaRecoveryPrompt.payload?.parts || "-"}
                                    {allaRomanaRecoveryPrompt.details?.failureReason ? `: ${allaRomanaRecoveryPrompt.details.failureReason}` : ""}
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Numero documento stampato"
                                    value={allaRomanaRecoveryDocumentNumber}
                                    onChange={(event) => setAllaRomanaRecoveryDocumentNumber(event.target.value)}
                                    disabled={checkoutPaymentSubmitting}
                                />
                            </Stack>
                        </DialogContent>
                        <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
                            <Button
                                variant="outlined"
                                disabled={checkoutPaymentSubmitting}
                                onClick={() => {
                                    setAllaRomanaRecoveryPrompt(null);
                                    setAllaRomanaRecoveryDocumentNumber("");
                                }}
                            >
                                Annulla
                            </Button>
                            <Button
                                variant="outlined"
                                disabled={checkoutPaymentSubmitting}
                                onClick={() => handleAllaRomanaRecovery("NOT_PRINTED")}
                            >
                                Non stampato - riprova
                            </Button>
                            <Button
                                variant="contained"
                                disabled={checkoutPaymentSubmitting || !allaRomanaRecoveryDocumentNumber.trim()}
                                onClick={() => handleAllaRomanaRecovery("PRINTED")}
                            >
                                Stampato - conferma
                            </Button>
                        </DialogActions>
                    </Dialog>
                }
                {openHeliteGuestDialog &&
                    <Dialog
                        open={openHeliteGuestDialog}
                        onClose={() => {
                            setOpenHeliteGuestDialog(false);
                            setPendingHelitePaymentRows(null);
                        }}
                        fullWidth
                        maxWidth="md"
                    >
                        <DialogTitle>{t("heliteGuestPicker.title")}</DialogTitle>
                        <DialogContent>
                            <Box sx={{ mb: 2 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={heliteGuestSearch}
                                    onChange={(event) => setHeliteGuestSearch(event.target.value)}
                                    placeholder={t("heliteGuestPicker.searchPlaceholder")}
                                    InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
                                />
                            </Box>

                            {heliteGuestsLoading ? (
                                <Typography color="text.secondary">{t("heliteGuestPicker.loading")}</Typography>
                            ) : filteredHeliteGuests.length === 0 ? (
                                <Typography color="text.secondary">{t("heliteGuestPicker.empty")}</Typography>
                            ) : (
                                <Box sx={{ maxHeight: 420, overflow: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 1 }}>
                                    <Table size="small" stickyHeader sx={{ minWidth: 720 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox" />
                                                {[
                                                    { key: "roomnr", label: t("heliteGuestPicker.room") },
                                                    { key: "holderName", label: t("heliteGuestPicker.guest") },
                                                    { key: "boardBasis", label: t("heliteGuestPicker.board") },
                                                    { key: "number", label: t("heliteGuestPicker.booking") },
                                                    { key: "arrival", label: t("heliteGuestPicker.arrival") },
                                                    { key: "departure", label: t("heliteGuestPicker.departure") },
                                                ].map((column) => (
                                                    <TableCell key={column.key} sortDirection={heliteGuestSort.key === column.key ? heliteGuestSort.direction : false}>
                                                        <TableSortLabel
                                                            active={heliteGuestSort.key === column.key}
                                                            direction={heliteGuestSort.key === column.key ? heliteGuestSort.direction : "asc"}
                                                            onClick={() => handleHeliteGuestSort(column.key)}
                                                        >
                                                            {column.label}
                                                        </TableSortLabel>
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredHeliteGuests.map((guest) => {
                                                const checked = String(selectedHeliteGuestId) === String(guest.id);
                                                return (
                                                    <TableRow
                                                        key={`${guest.id}-${guest.idrispren || guest.roomnr}`}
                                                        hover
                                                        selected={checked}
                                                        onClick={() => setSelectedHeliteGuestId(String(guest.id))}
                                                        sx={{ cursor: "pointer" }}
                                                    >
                                                        <TableCell padding="checkbox">
                                                            <Checkbox checked={checked} />
                                                        </TableCell>
                                                        <TableCell>{guest.roomnr || "-"}</TableCell>
                                                        <TableCell>{guest.holderName || "-"}</TableCell>
                                                        <TableCell>{formatBoardBasis(guest.boardBasis)}</TableCell>
                                                        <TableCell>{guest.number || "-"}</TableCell>
                                                        <TableCell>{formatDateDdMmYyyy(guest.arrival)}</TableCell>
                                                        <TableCell>{formatDateDdMmYyyy(guest.departure)}</TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    setOpenHeliteGuestDialog(false);
                                    setPendingHelitePaymentRows(null);
                                }}
                            >
                                {t("confirmOperation.cancel")}
                            </Button>
                            <Button
                                variant="contained"
                                sx={theme.checkout.closeCheckout_button}
                                disabled={checkoutPaymentSubmitting || heliteGuestsLoading || !selectedHeliteGuest}
                                onClick={handleConfirmHeliteGuestSelection}
                            >
                                {checkoutPaymentSubmitting ? "Invio..." : t("heliteGuestPicker.confirmCharge")}
                            </Button>
                        </DialogActions>
                    </Dialog>
                }
                {openCloseCheckout &&
                    <Dialog
                        open={openCloseCheckout}
                        onClose={handleCloseCheckoutSuccessModal}
                    >
                        <Paper sx={theme.checkout.modal_checkout}>
                            <Box textAlign="right">
                                <Button variant="text"
                                    onClick={handleCloseCheckoutSuccessModal}
                                    sx={theme.checkout.closeModalCheckout_button}
                                >{t("tableMapPage.close")}</Button>
                            </Box>
                            <Box
                                sx={{
                                    width: { xs: "92vw", sm: 420 },
                                    maxWidth: 420,
                                    gap: "16px",
                                    opacity: 1,
                                    borderRadius: "13.05px",
                                    pt: "20px",
                                    pr: "16px",
                                    pb: "20px",
                                    pl: "16px",
                                }}
                            >
                                <Box textAlign="center" gap="8px">
                                    <CheckCircleIcon sx={{ color: theme.palette.mode === "light" ? "#125605" : "#78BD6A", fontSize: 48 }} />
                                    <Typography variant='h5'>{t("checkout_page.confirmedPayment")}</Typography>
                                    <Typography variant='h8'>{t("checkout_page.confirmedPayment_note")}</Typography>
                                    <Box sx={{ py: "16px" }} ><Divider /></Box>
                                </Box>
                                <Box textAlign="center" gap="8px">
                                    <Typography variant='h6'>
                                        {t("checkout_page.totalPaid")}
                                    </Typography>
                                    <Typography sx={{ color: theme.palette.mode === "light" ? "#125605" : "#78BD6A", fontSize: "32px" }}>
                                        EUR {Number(lastPaymentSummary?.total ?? totalPayment ?? 0).toFixed(2)}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        pt: "18px",
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                                        gap: 1.5,
                                    }}
                                >
                                    <Paper sx={checkoutInfoPaperSx}>
                                        <Typography sx={theme.checkout.closeModalCheckout_paperTypography1}>{t("checkout_page.paymentDate")}</Typography>
                                        <Typography sx={checkoutInfoValueSx}>{dataAdesso()}</Typography>
                                    </Paper>
                                    <Paper sx={checkoutInfoPaperSx}>
                                        <Typography sx={theme.checkout.closeModalCheckout_paperTypography1}>
                                            {t("checkout_page.paymentMethod")}
                                        </Typography>
                                        <Typography sx={checkoutInfoValueSx}>
                                            {lastPaymentSummary?.methodLabel || getPaymentMethodLabel(paymentMethode, t)}
                                        </Typography>
                                    </Paper>
                                    {lastPaymentSummary?.heliteGuestLabel && (
                                        <Paper sx={{ ...checkoutInfoPaperSx, gridColumn: "1 / -1" }}>
                                            <Typography sx={theme.checkout.closeModalCheckout_paperTypography1}>
                                                {t("heliteGuestPicker.linkedGuest")}
                                            </Typography>
                                            <Typography sx={checkoutInfoValueSx}>
                                                {lastPaymentSummary.heliteGuestLabel}
                                            </Typography>
                                        </Paper>
                                    )}
                                    <Paper sx={checkoutInfoPaperSx}>
                                        <Typography sx={theme.checkout.closeModalCheckout_paperTypography1}>
                                            {t("client")}
                                        </Typography>
                                        <Typography sx={checkoutInfoValueSx}>
                                            {checkoutClientLabel}
                                        </Typography>
                                    </Paper>
                                    <Paper sx={checkoutInfoPaperSx}>
                                        <Typography sx={theme.checkout.closeModalCheckout_paperTypography1}>
                                            {lastPaymentSummary?.hasCash ? t("checkout_page.cashGiven") : t("checkout_page.change")}
                                        </Typography>
                                        <Typography sx={checkoutInfoValueSx}>
                                            EUR {Number(lastPaymentSummary?.hasCash ? lastPaymentSummary?.cashGiven : 0).toFixed(2)}
                                        </Typography>
                                    </Paper>
                                    <Paper sx={checkoutInfoPaperSx}>
                                        <Typography sx={theme.checkout.closeModalCheckout_paperTypography1}>
                                            {t("checkout_page.change")}
                                        </Typography>
                                        <Typography sx={checkoutInfoValueSx}>
                                            EUR {Number(lastPaymentSummary?.cashChange || 0).toFixed(2)}
                                        </Typography>
                                    </Paper>
                                </Box>
                            </Box >
                        </Paper >
                    </Dialog >

                }
                {openRiepilogo &&
                    <FatturazioneRiepilogo
                        open={openRiepilogo}
                        invoice={createdInvoice}
                        onClose={() => setOpenRiepilogo(false)}
                    />
                }
                {openPendingList &&
                    <Dialog
                        open={openPendingList}
                        onClose={() => setOpenPendingList(false)}
                    >
                        <Paper sx={{ ...theme.checkout.modal_checkout, ...{ overflowY: "auto" } }}>
                            <Box display={"flex"} justifyContent={"space-between"} sx={{ p: 2 }}>
                                <Typography variant="h4"
                                    sx={{
                                        py: "8.7px",
                                        px: "26.1px",

                                    }}>{t("checkout_page.pending")}</Typography>
                                <Button variant="text" textAlign="right"
                                    onClick={() => setOpenPendingList(false)}
                                    sx={theme.checkout.closeModalCheckout_button}
                                >{t("tableMapPage.close")}</Button>
                            </Box>

                            <Divider sx={{}} />

                            <Box sx={{ textAlign: "center", m: 2 }}>
                                {
                                    (!pendingTableList || pendingTableList.length < 1) ?
                                        (
                                            <Box>
                                                <Typography variant="h5">{t("tableMapPage.noTableInWaiting")}</Typography>
                                            </Box>)
                                        :
                                        (
                                            <Grid container rowSpacing={2} columnSpacing={2} sx={{ placeContent: "center", }}>

                                                <Grid key={"NOME"} item size={3} sx={{ width: "130px", p: 1, borderBottom: "solid black 1px" }}><Typography variant="h5" sx={{ textAlign: "center", }}>{t("tableMapPage.columnNames.name")}</Typography></Grid>
                                                <Grid key={"POSTI"} item size={3} sx={{ width: "130px", p: 1, borderBottom: "solid black 1px" }}><Typography variant="h5" sx={{ textAlign: "center", }}>{t("tableMapPage.columnNames.seats")}</Typography></Grid>
                                                <Grid key={"STATO"} item size={3} sx={{ width: "130px", p: 1, borderBottom: "solid black 1px" }}><Typography variant="h5" sx={{ textAlign: "center", }}>{t("tableMapPage.columnNames.status")}</Typography></Grid>
                                                <Grid key={"AZIONE"} item size={3} sx={{ width: "130px", p: 1, borderBottom: "solid black 1px" }}><Typography variant="h5" sx={{ textAlign: "center", }}></Typography></Grid>

                                                {pendingTableList?.map(
                                                    (el) => {
                                                        return (
                                                            <>
                                                                <Grid key={el.name} item size={3} sx={{ width: "130px", p: 1 }}><Typography variant="h5">{el.name}</Typography></Grid>
                                                                <Grid key={el.maxSeats} item size={3} sx={{ width: "130px", p: 1 }}><Typography variant="h5">{el.maxSeats}</Typography></Grid>
                                                                <Grid key={el.status} item size={3} sx={{ width: "130px", p: 1 }}><Typography variant="h5">{t(el.status)}</Typography></Grid>
                                                                <Grid key={`${el.id}-to-checkout`} item size={3} sx={{ width: "130px", p: 1 }}>
                                                                    <Button
                                                                        variant="contained"
                                                                        onClick={() => {
                                                                            selectTableAndGoToCheckout(el.id);
                                                                        }}
                                                                    >
                                                                        <Typography sx={{ fontSize: "12px" }}>{t("client_page.toCheckout")}</Typography>
                                                                    </Button>
                                                                </Grid>
                                                            </>
                                                        )
                                                    }
                                                )}
                                            </Grid>)
                                }
                            </Box>
                        </Paper >
                    </Dialog >

                }
                {openOccupiedList &&
                    <Dialog
                        open={openOccupiedList}
                        onClose={() => setOpenOccupiedList(false)}
                    >
                        <Paper sx={{ ...theme.checkout.modal_checkout, ...{ overflowY: "auto" } }} width={100}>
                            <Box display={"flex"} justifyContent={"space-between"} sx={{ p: 2 }}>
                                <Typography variant="h4"
                                    sx={{
                                        py: "8.7px",
                                        px: "26.1px",
                                    }}>{t("checkout_page.occupied")}</Typography>
                                <Button variant="text" textAlign="right"
                                    onClick={() => setOpenOccupiedList(false)}
                                    sx={theme.checkout.closeModalCheckout_button}
                                >{t("tableMapPage.close")}</Button>
                            </Box>

                            <Divider sx={{}} />

                            <Box sx={{ textAlign: "center", m: 2 }}>
                                {(!occupiedTableList || occupiedTableList.length < 1)
                                    ?
                                    (<Box>
                                        <Typography variant="h5">{t("tableMapPage.noTableOccupied")}</Typography>
                                    </Box>
                                    )
                                    :
                                    (
                                        <Grid container rowSpacing={2} columnSpacing={2} sx={{ placeContent: "center", }}>

                                            <Grid key={"NOME"} item size={3} sx={{ width: "130px", p: 1, borderBottom: "solid black 1px" }}><Typography variant="h5" sx={{ textAlign: "center", }}>{t("tableMapPage.columnNames.name")}</Typography></Grid>
                                            <Grid key={"POSTI"} item size={3} sx={{ width: "130px", p: 1, borderBottom: "solid black 1px" }}><Typography variant="h5" sx={{ textAlign: "center", }}>{t("tableMapPage.columnNames.seats")}</Typography></Grid>
                                            <Grid key={"STATO"} item size={3} sx={{ width: "130px", p: 1, borderBottom: "solid black 1px" }}><Typography variant="h5" sx={{ textAlign: "center", }}>{t("tableMapPage.columnNames.status")}</Typography></Grid>
                                            <Grid key={"AZIONE"} item size={3} sx={{ width: "130px", p: 1, borderBottom: "solid black 1px" }}><Typography variant="h5" sx={{ textAlign: "center", }}></Typography></Grid>

                                            {occupiedTableList?.map(
                                                (el) => {
                                                    return (
                                                        <>
                                                            <Grid key={el.name} item size={3} sx={{ width: "130px", p: 1 }}><Typography variant="h5">{el.name}</Typography></Grid>
                                                            <Grid key={el.maxSeats} item size={3} sx={{ width: "130px", p: 1 }}><Typography variant="h5">{el.maxSeats}</Typography></Grid>
                                                            <Grid key={el.status} item size={3} sx={{ width: "130px", p: 1 }}><Typography variant="h5">{t(el.status)}</Typography></Grid>
                                                            <Grid key={`${el.id}-to-pending`} item size={3} sx={{ width: "130px", p: 1 }}>
                                                                <Button
                                                                    variant="contained"
                                                                    onClick={() => moveOccupiedTableToPending(el.id)}
                                                                >
                                                                    <Typography sx={{ fontSize: "12px" }}>{getTenantTranslation(t, tenantType, "checkout_page.move_to_pending")}</Typography>
                                                                </Button>
                                                            </Grid>
                                                        </>
                                                    )
                                                }
                                            )}
                                        </Grid>)}
                            </Box>
                        </Paper >
                    </Dialog >

                }
                {openTransfer &&
                    <Dialog
                        open={openTransfer}
                        onClose={() => setOpenTransfer(false)}
                    >
                        <Paper sx={{ ...theme.checkout.modal_checkout, ...{ overflowY: "auto" } }} fullwidth>
                            <Box display={"flex"} justifyContent={"space-between"} sx={{ p: 2 }}>
                                <Typography variant="h4"
                                    sx={{
                                        py: "8.7px",
                                        px: "26.1px",
                                    }}>{t("tableMapPage.transferOrders")}</Typography>
                                <Button variant="text" textAlign="right" onClick={() => setOpenTransfer(false)} sx={theme.checkout.closeModalCheckout_button}
                                >{t("tableMapPage.close")}</Button>
                            </Box>
                        </Paper >
                    </Dialog >

                }
                {openDelete && <Dialog open={openDelete} onClose={() => { setOpenDelete(false) }}>
                    <DialogTitle>{t("checkout_page.delete_title")}</DialogTitle>
                    <DialogContent>{t("checkout_page.delete_note_2")}</DialogContent>
                    <DialogActions>
                        <Button onClick={() => { setOpenDelete(false) }} color="secondary" variant="outlined">
                            {t('cancel')}
                        </Button>
                        <Button onClick={() => { handleDelete() }} color="primary" variant="contained">
                            {t('confirm')}
                        </Button>
                    </DialogActions>
                </Dialog>
                }
                {openDialogError &&
                    <Dialog
                        open={openDialogError}
                        onClose={() => setOpenDialogError(false)}
                        fullWidth
                        maxWidth="md"
                    >
                        <DialogTitle>{t("checkout_page.warning")}</DialogTitle>
                        <DialogContent>{t("checkout_page.incompleteBillingWarning")}</DialogContent>
                        <DialogActions>
                            <Button variant="outlined" onClick={()=>{setOpenDialogError(false)}}>{t("confirmOperation.cancel")}</Button>
                            <Button variant="contained" onClick={()=>{navigate("/impostazioni-sistema")}}>{t("systemSettings.page")}</Button>
                        </DialogActions>
                    </Dialog>
                }
                <Dialog
                    open={Boolean(invoiceCourtesyPrintPrompt)}
                    onClose={invoiceCourtesyPrintSubmitting ? undefined : closeInvoiceCourtesyPrintPrompt}
                    fullWidth
                    maxWidth="xs"
                >
                    <DialogTitle>Stampare copia cortesia?</DialogTitle>
                    <DialogContent>
                        <Stack spacing={1.5}>
                            <Typography>
                                La fattura e stata creata correttamente. Vuoi stampare una copia di cortesia sulla termica configurata?
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "background.default" }}>
                                <Typography variant="body2" color="text.secondary">
                                    Fattura
                                </Typography>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    {invoiceCourtesyPrintPrompt?.numeroFattura || `#${invoiceCourtesyPrintPrompt?.id || "-"}`}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {invoiceCourtesyPrintPrompt?.denominazione || "Cliente non specificato"}
                                </Typography>
                            </Paper>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={closeInvoiceCourtesyPrintPrompt}
                            disabled={invoiceCourtesyPrintSubmitting}
                        >
                            Non stampare
                        </Button>
                        <Button
                            variant="contained"
                            sx={theme.checkout.closeCheckout_button}
                            onClick={handleConfirmInvoiceCourtesyPrint}
                            disabled={invoiceCourtesyPrintSubmitting}
                        >
                            {invoiceCourtesyPrintSubmitting ? "Invio..." : "Stampa copia"}
                        </Button>
                    </DialogActions>
                </Dialog>
                {openInvoiceCloseConfirm &&
                    <Dialog
                        open={openInvoiceCloseConfirm}
                        onClose={() => {}}
                        fullWidth
                        maxWidth="sm"
                    >
                        <DialogTitle>Chiudi pagamento fattura</DialogTitle>
                        <DialogContent>
                            <Typography>
                                Seleziona il metodo con cui chiudere il conto. Usa Sospeso se la fattura deve essere incassata successivamente.
                            </Typography>
                            <TextField
                                select
                                fullWidth
                                required
                                size="small"
                                label="Chiusura pagamento"
                                value={invoiceSettlementMethod}
                                onChange={(event) => setInvoiceSettlementMethod(event.target.value)}
                                disabled={invoiceCloseSubmitting || invoicePaymentRecorded}
                                sx={{ mt: 2 }}
                            >
                                {INVOICE_SETTLEMENT_METHODS.map((method) => (
                                    <MenuItem key={method.value} value={method.value}>
                                        {method.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            {checkoutAppliedPrepaymentTotal > 0.009 ? (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    {t("checkout_page.invoicePrepaymentsWillApply", { amount: `EUR ${checkoutAppliedPrepaymentTotal.toFixed(2)}` })}
                                </Alert>
                            ) : null}
                        </DialogContent>
                        <DialogActions>
                            <Button
                                variant="contained"
                                sx={theme.checkout.closeCheckout_button}
                                disabled={invoiceCloseSubmitting || !invoiceSettlementMethod}
                                onClick={handleCloseCheckoutAfterInvoice}
                            >
                                {invoiceCloseSubmitting ? "Chiusura..." : "Conferma chiusura"}
                            </Button>
                        </DialogActions>
                    </Dialog>
                }
                {openCreateInvoice &&
                    <FatturazioneDialog
                        openCreateDialog={openCreateInvoice}
                        onCloseCreateDialog={() => setOpenCreateInvoice(false)}
                        onInvoiceCreated={handleInvoiceCreated}
                        prezzoTotale={totaleFattura}
                        colors={COLORS}
                        isDark={isDark}
                        preselectedClientId={selectedClientFromReservation}
                        preselectedCompanyId={selectedCompanyFromReservation}
                        ordiniFiltrati={ordiniFiltrati}
                    />
                }
                {/* {openCreateInvoice && //BackUp
                    <FatturazioneDialog
                        openCreateDialog={openCreateInvoice}
                        onCloseCreateDialog={() => setOpenCreateInvoice(false)}
                        setOpenRiepilogo={setOpenRiepilogo}
                        onInvoiceCreated={setCreatedInvoice}
                        prezzoTotale={totaleFattura}
                        colors={COLORS}
                        isDark={isDark}
                        preselectedClientId={selectedClientFromReservation}
                        preselectedCompanyId={selectedCompanyFromReservation}
                    />
                } */}
                <ConfermaLiberaTavolo
                    open={openLiberaTavolo}
                    tavolo={table_to_free?.name}
                    loading={freeTableSubmitting}
                    onConferma={async () => {
                        if (freeTableSubmitting) return;
                        const tableToFree = table_to_free;
                        const ordersToClose = orderTable_da_liberare;
                        try {
                            setFreeTableSubmitting(true);
                            const activeOrders = ordersToClose.filter((ordTav) =>
                                ["OPEN", "IN_PROGRESS"].includes(ordTav.status)
                            );
                            await Promise.allSettled(activeOrders.map((ordTav) =>
                                aggiornaStatusComanda(ordTav.id, { status: "CANCELLED" }, token, tenant)
                            ));
                            if (tableToFree?.id) {
                                await liberaTavolo(tableToFree, "LIBERO");
                                removeTableFromCheckoutLists(tableToFree.id);
                            }
                            setSelectedTable("");
                            setOpenLiberaTavolo(false);
                            if (openOccupiedList) setOpenOccupiedList(false)
                            setRefreshTrigger((prev) => prev + 1);
                        } catch (err) {
                            handleAlert(err?.message || String(err), "error")
                        } finally {
                            setFreeTableSubmitting(false);
                        }
                    }}
                    onAnnulla={() => {
                        if (!freeTableSubmitting) setOpenLiberaTavolo(false);
                    }}
                />
                <Dialog
                    open={Boolean(heliteBuonoPrintPrompt)}
                    onClose={heliteBuonoPrintSubmitting ? undefined : closeHeliteBuonoPrintPrompt}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>Stampare il buono HElite?</DialogTitle>
                    <DialogContent>
                        <Stack spacing={1.5}>
                            <Typography>
                                La firma del buono è stata salvata correttamente.
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "background.default" }}>
                                <Typography variant="body2" color="text.secondary">
                                    Buono
                                </Typography>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    {heliteBuonoPrintPrompt?.buono?.number || `#${heliteBuonoPrintPrompt?.buono?.id || "-"}`}
                                </Typography>
                            </Paper>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={closeHeliteBuonoPrintPrompt}
                            disabled={heliteBuonoPrintSubmitting}
                        >
                            Non stampare
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleConfirmHeliteBuonoPrint}
                            disabled={heliteBuonoPrintSubmitting}
                        >
                            {heliteBuonoPrintSubmitting ? "Invio..." : "Stampa buono"}
                        </Button>
                    </DialogActions>
                </Dialog>
                <HeliteBuonoSignatureDialog
                    open={Boolean(activeHeliteBuono)}
                    buono={activeHeliteBuono}
                    saving={heliteBuonoSignatureSaving}
                    onClose={closeActiveHeliteBuono}
                    onSkip={closeActiveHeliteBuono}
                    onSave={handleSaveHeliteBuonoSignature}
                />
                <Dialog
                    open={Boolean(genericVoucherPrintPrompt)}
                    onClose={genericVoucherPrintSubmitting ? undefined : closeGenericVoucherPrintPrompt}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>Stampare il buono PMS manuale?</DialogTitle>
                    <DialogContent>
                        <Stack spacing={1.5}>
                            <Typography>
                                La firma del buono e stata salvata correttamente.
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "background.default" }}>
                                <Typography variant="body2" color="text.secondary">
                                    Buono PMS manuale
                                </Typography>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    {genericVoucherPrintPrompt?.voucher?.number || `#${genericVoucherPrintPrompt?.voucher?.id || "-"}`}
                                </Typography>
                            </Paper>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={closeGenericVoucherPrintPrompt}
                            disabled={genericVoucherPrintSubmitting}
                        >
                            Non stampare
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleConfirmGenericVoucherPrint}
                            disabled={genericVoucherPrintSubmitting}
                        >
                            {genericVoucherPrintSubmitting ? "Invio..." : "Stampa buono"}
                        </Button>
                    </DialogActions>
                </Dialog>
                <HeliteBuonoSignatureDialog
                    open={Boolean(activeGenericVoucher)}
                    buono={{
                        ...activeGenericVoucher,
                        heliteRoomNumber: activeGenericVoucher?.tableName || selectedTable?.name || "",
                        heliteHolderName: "Buono PMS manuale",
                    }}
                    saving={genericVoucherSignatureSaving}
                    onClose={closeActiveGenericVoucher}
                    onSkip={closeActiveGenericVoucher}
                    onSave={handleSaveGenericVoucherSignature}
                />
            </Box >
        </SidebarLayout >
    );
};

export default PuntoCassa;

